// PERF WARNING — this file holds EVERY locale's strings inline (~300KB raw).
// Do NOT import it from 'use client' components: that ships all locales to the
// browser. Client code must read the active locale's slice via useUiContent()
// (components/ui-content-provider.tsx). A hard `import 'server-only'` guard is
// not possible here because dual-graph server components (main.tsx, header.tsx,
// related-server.tsx, …) are also imported by the client page /[lang]/ai/[id];
// the dictionaries are allowed in that one route's bundle only. Verify with
// `pnpm analyze` that no *initial* chunk of [...slug]//[lang] pages contains it.
import {
  CoreLanguageType,
  LanguageType,
  LocaleDetails,
  proxyLanguageRecord,
} from 'shared/src/types/languages.type';
import { Region, RegionCode } from 'shared/src/types/region.type';
import { offerLabelsTranslations } from './translations/offer-labels';


type DefaultMetadata = {
  title: string;
  description: string;
};

type ContactForm = {
  personalInformation: string;
  yourMessage: string;
  firstName: string;
  firstNamePlaceholder: string;
  emailAddress: string;
  emailPlaceholder: string;
  phoneNumber: string;
  phonePlaceholder: string;
  company: string;
  companyPlaceholder: string;
  message: string;
  messagePlaceholder: string;
  sendMessage: string;
  sending: string;
  successTitle: string;
  successMessage: string;
  processingMessage: string;
  errorTitle: string;
  errorMessage: string;
};


type TimeUiStrings = {
  noTimeAvailable: string;
  inTheFuture: string;
  justNow: string;
  oneMinuteAgo: string;
  xMinutesAgo: string; // '{x}' placeholder — was a function, but functions can't cross the RSC boundary
  over30Minutes: string;
};

type AgeVerification = {
  title: string;
  subtitle: string;
  warning: string;
  disclaimer: string;
  confirmAge: string;
  enterButton: string;
  exitButton: string;
  mustBe18: string;
  adultContentWarning: string;
  euCompliance: string;
};

type CookieConsent = {
  title: string;
  description: string;
  essential: string;
  analytics: string;
  advertising: string;
  learnMore: string;
  acceptAll: string;
  rejectAll: string;
  customizeSettings: string;
  savePreferences: string;
  essentialDescription: string;
  analyticsDescription: string;
  advertisingDescription: string;
  californiaNotice?: string; // For USA/California users
};

export type UiContent = {
  aboutTitle: string;
  aboutUs: string;
  vision: string;
  aboutUsTitle: string;
  ageVerification: AgeVerification;
  alternativeLabel: string;
  articles: string;
  at: string;
  averagePriceTag: string;
  backToHome: string;
  /** Breadcrumb root node label — a NOUN ('Home'), unlike the action-phrased backToHome. */
  breadcrumbHome: string;
  /** Product JSON-LD BuyAction description. Hand-translated for major languages;
   *  minority languages fall back to English at the consumer (budget-ui-content precedent). */
  buyActionDescription?: string;
  /** Single-retailer variant of `buyActionDescription`. */
  buyActionDescriptionSingleStore?: string;
  goBack: string;
  best: string;
  bestFeatures: string;
  buyNow: string;
  /**
   * Offers panel — the split "Buy Now" pill and the multi-retailer panel it
   * opens (components/products/links/offers/). Hand-translated for en/fr/es/de;
   * every other locale leaves them undefined and falls back to the English
   * string at the call site (buyActionDescription precedent).
   *
   * Two labels this feature needs are deliberately NOT redeclared here because
   * they already exist above as REQUIRED keys with translations in all 24 core
   * languages — duplicating them as optional keys would downgrade 20 locales to
   * English:
   *   - the pill's main label  → use `buyNow`  ('Buy Now')
   *   - the panel's back button → use `goBack` ('Back')
   *
   * Placeholders ({n}, {merchant}, {price}, {time}) are substituted with
   * `replacePlaceholders` from lib/ui-content/price-ui-content.
   */
  /** Pill's right zone, rendered after the offer count: "4 stores". Keep it
   *  SHORT — the collapsed pill is only ~120px inner at text-[0.7rem]. */
  stores?: string;
  /** Accessible name of the stores zone. Contains "{n}". */
  seeAllOffers?: string;
  /** Offers panel / bottom-sheet header. */
  chooseWhereToBuy?: string;
  /** Badge chip on the editorially recommended row. Keep it chip-short. */
  bestPlaceBadge?: string;
  /** Badge chip on the cheapest verified row. Keep it chip-short. */
  bestPriceBadge?: string;
  /** Merchant attribution, e.g. under the pill. Contains "{merchant}". */
  atMerchant?: string;
  /** Section label above the non-primary rows. */
  otherStores?: string;
  /** Expander revealing rows past MAX_VISIBLE_ROWS. Contains "{n}". */
  showAllOffers?: string;
  /** Freshness line under the rows. Contains "{time}" (a relative time). */
  pricesChecked?: string;
  /** Row availability labels. */
  inStock?: string;
  outOfStock?: string;
  /** Hedge for low-confidence prices. Contains "{price}". */
  approxPrice?: string;
  /**
   * The offers panel's note on its prices, in the disclosure's info icon
   * (tooltip and accessible name; no sr-only copy since 2026-10-03), stating the
   * SAME claim as `buyActionDescription`: we rank where to buy among the trusted
   * retailers we track in this market, we show the best price we OBSERVED, and
   * the retailer settles the final number at checkout. It exists because the
   * panel's visible chrome is four words ("Where to buy") and a "Best price"
   * badge — a machine reading only that would infer a price-guarantee we do not
   * make. Contains "{product}" and "{region}"; {region} is always the LOCALE's
   * market (URL-derived), never IP geolocation.
   */
  offersPanelIntro?: string;
  /** Single-retailer variant of `offersPanelIntro`. Contains "{product}", "{region}", "{seller}". */
  offersPanelIntroSingleStore?: string;
  /**
   * One-line affiliate disclosure for the offers panel's 30px footer strip.
   * `affiliateDisclosure` below is a full paragraph that only fits behind the
   * info icon; this is the visible marker beside it. Keep it to ~2 clamped
   * lines at 0.58rem in a 380px panel (French is the length budget).
   */
  affiliateDisclosureShort?: string;
  /**
   * Offers Engine v2 (docs/OFFERS_ENGINE_V2.md section 6): the "as of" stamps,
   * the honesty badge, information links, price kinds, attribution and deals.
   * Hand-set for en/fr/es/de like `pricesChecked`; every other locale leaves
   * them undefined and falls back to English at the consumer, except
   * `amazonPriceAsOf`, whose label is hand-set in all 24 (the rest of its line
   * comes from Intl). No em dash and no emoji, ever.
   */
  /** Beside every Amazon price (row subline, price line, kit cart). "{label} · {time}": the label only, because {time} brings its own day word and clock ("today, 6:40 PM") in the reader's zone (observed-at.ts). Contains "{time}". */
  amazonPriceAsOf?: string;
  /** Amazon's required disclaimer: in the info icon beside an Amazon price (tooltip and accessible name). */
  amazonPriceDisclaimer?: string;
  /** Any other row's subline once its price is older than 24 hours. "{label} · {time}", as above. Contains "{time}". */
  priceAsOf?: string;
  /** The honesty badge. Contains "{percent}". Keep it chip-short. */
  belowUsualBadge?: string;
  /** Its tooltip and sr-only text. Contains "{price}" and "{days}". */
  usualPriceTooltip?: string;
  /** Price slot of a brand information link. */
  officialSite?: string;
  /** Its subline when the brand does not sell online. Contains "{region}". */
  noDirectSales?: string;
  /** Its subline otherwise. */
  seeProductPage?: string;
  /** Chip on a priced brand-store row that carries no other badge. Keep it chip-short. */
  brandStoreBadge?: string;
  /** Price slot of a list-price row. Contains "{price}". */
  msrpLabel?: string;
  /** Subline of a member-price row. */
  memberPriceLabel?: string;
  /** Price slot of a hidden-price row. Contains "{merchant}". */
  seePriceLabel?: string;
  /** Subline of a marketplace (eBay) row; the seller is never named. */
  marketplaceListing?: string;
  /** Tooltip of a row whose program requires attribution. Contains "{merchant}". */
  dataProvidedBy?: string;
  /** Chip on an Amazon row with a live deal. Keep it chip-short. */
  dealBadge?: string;
  /** Its subline. Contains "{time}": the end in the reader's zone ("tomorrow, 6:00 PM", "Oct 9, 6:00 PM"), so the words around it must read before a day word ("until {time}"). */
  dealEnds?: string;
  /** Subline of a pre-order row. */
  preorderLabel?: string;
  by: string;
  cancel: string;
  /** Footer nav label linking to /partners, in every language (the same words
   *  as the explore index, translations/<lang>/explore.ts). */
  partners?: string;
  /**
   * "How we pick": the footer nav label for /methodology, and the quiet link at
   * the end of a guide's AI credit line. In every language: the same words as
   * the explore index (translations/<lang>/explore.ts).
   */
  methodology?: string;
  careers: string;
  changeRegion: string;
  /**
   * Accessible name / tooltip for the header region badge, e.g.
   * "Shopping region: Québec, Canada". MUST contain the literal "{region}",
   * replaced at render time with the localized "<subregion>, <country>".
   * Optional: hand-translated for the major languages, every other locale
   * falls back to English at the consumer (buyActionDescription precedent).
   */
  shoppingRegionLabel?: string;
  choosePrice: string;
  comingSoon: string;
  conclusion: string;
  cookieConsent: CookieConsent;
  /**
   * Honest page credit in the category header, replacing the old "Created by"
   * + AI persona byline (docs/SEO_RECOVERY.md). Optional: translated for
   * en/fr/es only; every other locale falls back to English at the consumer
   * (buyActionDescription precedent). No em dashes (brand copy rule). `{region}` is the
   * market's `aboutInceptionAiRegion` phrase ("in Canada"), dropped when none.
   */
  aiResearchCredit?: string;
  /** "Updated {date}" beside that credit; `{date}` is the locale-formatted last update. */
  pageUpdatedOn?: string;
  contactFormDescription: string;
  contactUs: string;
  defaultMetadata: DefaultMetadata;
  describeDemoCategory: string;
  describeDemoComparison: string;
  descriptionArticle: string;
  directlySearch: string;
  disconnect: string;
  emailPlaceholder: string;
  emailTitle: string;
  example: string;
  exploreMore: string;
  existingPages: string;
  articlesOtherRegions: string;
  firstPostComingSoon: string;
  joinUs: string;
  contactForm: ContactForm;
  loading: string;
  localeDisclaimer: string;
  login: string;
  lookingFor: string;
  // Language selector strings
  otherLanguages: string;
  profile: string;
  showLess: string;
  showMoreLanguages: string;
  messageTitle: string;
  mission: string;
  namePlaceholder: string;
  nameTitle: string;
  newProduct: string;
  newSearch: string;
  nextStep: string;
  noArticles: string;
  noResultsFound: string;
  noSearchResult: string;
  officialWebsite: string;
  orderNow: string;
  // Book-page CTAs (searchType === 'book'). Optional: languages without a
  // translation fall back to the English label at the call site — same pattern
  // as officialWebsite. See components/products/links/book-link-button.tsx.
  getTheBook?: string;
  listenOnAudible?: string;
  // Regulated-product disclosure shown INSTEAD of the buy CTA for
  // prescription-only / professional-use products (we review them, we don't
  // sell them). Optional: locales without a translation fall back to the
  // English defaults in components/products/links/regulated-notice.tsx.
  regulatedNotice?: {
    prescriptionOnly: string;
    professionalUse: string;
    fromVeterinarian: string;
    fromPharmacist: string;
    noOnlinePurchase: string;
    professionalSupply: string;
    whyNoLink: string;
  };
  browseOnAmazon: string;
  // Badge shown on the product card when the enrichment pipeline affirmatively
  // found the product is not purchasable in the page's country. Supports a
  // {country} placeholder (localized via Intl.DisplayNames at the call site).
  // Optional: locales without a translation fall back to the English template.
  unavailableInCountry?: string;
  // CTA for unavailable-in-country products: routes to the Amazon search
  // fallback (affiliate-tagged) so the user can buy a similar product instead
  // of bouncing. Optional: falls back to `searchNow` at the call site.
  searchSimilarOnAmazon?: string;
  ourMission: string;
  overviewTitle: string;
  /** Action chip beside the product tabs; focuses the top-bar search. */
  askAQuestion: string;
  /** The chip right after a press: where to type (the field it focused is up in the top bar). */
  askSentHint: string;
  /** Inline note when the browser refuses the microphone (voice search). Falls back to English. */
  micBlocked?: string;
  pageReference: string;
  phoneTitle: string;
  publishedOn: string;
  publishingOn: string;
  quickMenu: string;
  recentBlogs: string;
  /**
   * Country copy is whole sentences keyed by RegionCode, never a phrase glued to a bare region
   * name: the old "{switchTo} {region}" read "passer à Canada" and put English names ("Germany",
   * "UK") into every language. Each language names the country itself, with the article or
   * preposition it takes ("au Canada", "aux États-Unis", "to the UK", "zur Schweiz"). A language
   * lists only the countries it can be shown for (the banner only offers a market in the page's
   * own language; the redirect toast only exists between the CA, UK, FR and US domains), and the
   * UI shows nothing rather than a sentence it lacks. Completeness is pinned by
   * __tests__/explorer-french-region-banner-grammar.test.ts.
   */
  regionPrompt: {
    /** The top-of-page bar's question for the visitor's country. */
    switchQuestion: Partial<Record<RegionCode, string>>;
    yesSwitch: string;
    noThanks: string;
    /** Toast after an automatic market redirect, for the market it landed on. */
    switchedNotice: Partial<Record<RegionCode, string>>;
    /** The toast's back-link label, for the market it came from. */
    goBackTo: Partial<Record<RegionCode, string>>;
  };
  relatedTitle: string;
  requirements: string;
  responsibilities: string;
  reviewSummary: string;
  /** Header of the review block: everything it shows comes from buyers. */
  buyerReviewSummary?: string;
  /**
   * A star rating always names Amazon, its only source (ratingSource
   * 'amazon-asin', read from the Creators API). `{rating}` is the number out
   * of 5. Review-block header: "4.6/5 on Amazon". Optional: other locales
   * fall back to English, like buyerReviewSummary.
   */
  amazonRatingLine?: string;
  /** Visible tag after the stars on a product card: "on Amazon". */
  amazonStarsSource?: string;
  /** Screen-reader text of those stars. `{rating}` as above. */
  amazonStarsSr?: string;
  /**
   * Line under the header when the block shows our summary of fetched
   * reviews: says it is an AI summary, of `{n}` reviews from `{m}` sources.
   */
  reviewBasisLine?: string;
  /** reviewBasisLine when every review comes from one source (`{m}` = 1). */
  reviewBasisLineOneSource?: string;
  /**
   * Header link label that deep-links to the real Amazon reviews page.
   * Must contain the literal substring "{count}" which is replaced at render
   * time with a localized formatted review count (e.g. "12,345"). Hidden when
   * the count is missing or zero.
   */
  xReviews: string;
  review: string;
  /**
   * Tab label for the verbatim quotes panel. Optional so existing
   * locales don't break; callers should fall back to 'Quotes'.
   */
  webReviewsTab?: string;
  searching: string;
  searchingProduct: string;
  searchNow: string;
  sidemenuListTitle: string;
  slogan: string;
  submit: string;
  technicalSpecs: string;
  nutritionFacts: string;
  ingredients: string;
  materialFabric: string;
  dimensionsSize: string;
  performanceMetrics: string;
  safetyFeatures: string;
  compatibility: string;
  careInstructions: string;
  warrantySupport: string;
  timeUiStrings: TimeUiStrings;
  title: string;
  titleArticle: string;
  trademarkPolicy: string;
  trademarkPolicyContent: string[];
  tryAgain: string;
  uploadCV: string;
  viewDetails: string;
  viewMore: string;
  welcome: string;
  linkInput: {
    smartExtract: string;
    apply: string;
    pasteHelper: string;
    placeholderDefault: string;
  };
  privacyPolicy: string;
  affiliateDisclosure: string;
  /**
   * The Associate statement alone (Associates Operating Agreement section 5),
   * the first sentence of `affiliateDisclosure`: a visible line under the
   * search shelf's buy buttons. Optional: other locales fall back to English.
   */
  amazonAssociateStatement?: string;
  articlesIntro: string;
  fallbackNotice: string;
  aboutInceptionAi: string;
  aboutInceptionAiRegion: Partial<Record<Region, string>>;
};

const uiContentCore: Record<CoreLanguageType, UiContent> = {
  // English
  en: {
    aboutTitle: 'The Essentials:',
    aboutUs: 'About Us',
    vision: 'Our Vision',
    aboutUsTitle: 'What is Inception?',
    ageVerification: {
      title: 'Age Verification Required',
      subtitle: 'Adult Content Warning',
      warning: 'This website contains adult content and is only for individuals 18 years of age or older.',
      disclaimer:
        'By entering this site, you confirm that you are of legal age in your jurisdiction and that you wish to view adult content.',
      confirmAge: 'I confirm that I am 18 years of age or older',
      enterButton: 'Enter Site',
      exitButton: 'Exit',
      mustBe18: 'You must be 18+ to access this content',
      adultContentWarning: 'This site contains explicit adult content intended for mature audiences only.',
      euCompliance: 'This verification complies with EU Digital Services Act and national age verification requirements.',
    },
    alternativeLabel: `*best alternative we found`,
    at: 'at',
    averagePriceTag: 'Average: ',
    best: 'Best PLACEHOLDER',
    bestFeatures: 'What makes a product the best in its category?',
    articles: 'Articles',
    backToHome: 'Go home',
    breadcrumbHome: 'Home',
    buyActionDescription: 'Compare where to buy {product} on Inception: the trusted retailers we track in this market, the best price we have observed at each, and the option we recommend. Prices are checked regularly and can change, and the retailer confirms the final price at checkout.',
    buyActionDescriptionSingleStore: 'For this product we track one retailer, {seller}, and show the price we last observed there; the price you see at checkout may differ.',
    goBack: 'Back',
    by: 'by',
    cancel: 'Cancel',
    partners: 'Partners',
    methodology: 'How we pick',
    careers: 'Careers',

    changeRegion: 'Change Region',
    shoppingRegionLabel: 'Shopping region: {region}',
    choosePrice: 'Meanwhile, you can choose a price range that suits you.',
    comingSoon: 'Coming Soon',
    conclusion: 'Conclusion',
    cookieConsent: {
      title: 'We Value Your Privacy',
      description: 'We use cookies to enhance your experience and provide better product recommendations. Your data stays private and anonymous.',
      essential: 'Essential Cookies',
      analytics: 'Analytics Cookies',
      advertising: 'Advertising Cookies',
      learnMore: 'Learn more',
      acceptAll: 'Accept & Continue',
      rejectAll: 'Decline',
      customizeSettings: 'Customize',
      savePreferences: 'Save Preferences',
      essentialDescription: 'Required for the website to function properly. Cannot be disabled.',
      analyticsDescription: 'Help us understand how visitors interact with our website by collecting anonymous data.',
      advertisingDescription: 'Used to display relevant ads via Google AdSense. Only loads when you consent.',
      californiaNotice: 'California residents: We do not sell your personal information. Declining analytics cookies opts you out of all non-essential data collection.',
    },
    aiResearchCredit: "Inception's AI took the time to understand this market {region}",
    pageUpdatedOn: 'Updated {date}',

    contactUs: 'Contact Us',
    contactFormDescription: 'Please fill out the form below.',
    defaultMetadata: {
      title: `Inception | The best product for you, found by honest AI`,
      description:
        `The best product for you is already out there. Our AI finds it, because we answer to you, not advertisers. Inception gives you AI-researched product recommendations for your region, with no sponsored placements.`,
    },
    descriptionArticle:
      'Our mission is to transform the way businesses enhance their online presence by enabling them to provide quality content FREE for their customers.',
    describeDemoCategory:
      'Here we will define the features and qualities that makes a product a top contender or leader in this category. This is essentiel to output a list of top products in a specific category, with your desired product ranked first. The rest of the list features comparable products, considering both quality and price range, with a focus on Canadian companies.',
    describeDemoComparison:
      'Here we will define the features and qualities that makes a product a top contender or leader in this category to create a straightforward list of the best product names based on your search criteria. This list excludes any additional details and prioritizes local companies in your region for our selection process.',
    directlySearch: 'Directly search',
    disconnect: 'Disconnect',
    emailPlaceholder: 'Enter your email',
    emailTitle: 'Email',
    example: 'Example',
    phoneTitle: 'Phone Number',
    exploreMore: 'Explore More',
    existingPages: 'Visit existing pages that might help',
    articlesOtherRegions: 'From our other regions',
    firstPostComingSoon: `Your first post is coming soon, we'll keep you posted!  
Meanwhile, you can edit the content below if you'd like.`,
    joinUs: 'Join us.',
    contactForm: {
      personalInformation: 'Personal Information',
      yourMessage: 'Your Message',
      firstName: 'First Name',
      firstNamePlaceholder: 'Enter your first name',
      emailAddress: 'Email Address',
      emailPlaceholder: 'Enter your email address',
      phoneNumber: 'Phone Number',
      phonePlaceholder: 'Enter your phone number',
      company: 'Company/Organization',
      companyPlaceholder: 'Enter your company name',
      message: 'Tell us about your project or inquiry',
      messagePlaceholder: 'Share your project details, requirements, or any questions you have...',
      sendMessage: 'Send Message',
      sending: 'Sending...',
      successTitle: 'Message Sent Successfully!',
      successMessage: "Thank you for your message! We'll get back to you soon.",
      processingMessage: 'Processing your request...',
      errorTitle: 'Error Sending Message',
      errorMessage: 'There was an error sending your message. Please try again.',
    },
    loading: 'Loading...',
    localeDisclaimer: 'Only X',
    login: 'Login',
    lookingFor: 'Are you looking for?',
    // Language selector strings
    profile: 'Profile',
    otherLanguages: 'Other Languages',
    showLess: 'Show Less',
    showMoreLanguages: 'Show More Languages',
    messageTitle: 'Message',
    mission: `The best product for you is already out there. Our AI finds it, because we answer to you, not advertisers.`,

    namePlaceholder: 'Enter your name',
    nameTitle: 'Name',
    newProduct: 'NEWLY ADDED',
    newSearch: 'New Search: ',
    nextStep: 'Next Step',
    noArticles: 'There are currently no articles',
    noResultsFound: 'p.s. I can only provide suggestions on marketable products or categories.',
    noSearchResult: 'Keep describing the product you are looking for, then press Enter.',
    officialWebsite: 'official website',
    orderNow: 'Order Now',
    regulatedNotice: {
      prescriptionOnly: 'Prescription only',
      professionalUse: 'Professional use',
      fromVeterinarian: 'Available from your veterinarian',
      fromPharmacist: 'Available from a pharmacist',
      noOnlinePurchase: 'Not sold online',
      professionalSupply: 'Sold through professional suppliers',
      whyNoLink: 'We review this product but do not link to it.',
    },
    getTheBook: 'Get the book',
    listenOnAudible: 'Listen on Audible',
    browseOnAmazon: 'Browse on Amazon',
    unavailableInCountry: 'Currently unavailable in {country}',
    searchSimilarOnAmazon: 'Search similar on Amazon',
    searchNow: 'Search Now',
    ourMission: 'Our Mission',
    overviewTitle: 'Details',
    askAQuestion: 'Ask a question?',
    askSentHint: 'Type it up top',
    micBlocked: 'Microphone is blocked. Allow it in your browser settings.',
    pageReference: 'On this page',
    buyNow: 'Buy Now',
    stores: 'stores',
    seeAllOffers: 'See all {n} buying options',
    chooseWhereToBuy: 'Where to buy',
    bestPlaceBadge: 'Our pick',
    bestPriceBadge: 'Best price',
    atMerchant: 'at {merchant}',
    otherStores: 'More stores',
    showAllOffers: 'Show all {n}',
    pricesChecked: 'Prices checked {time}',
    inStock: 'In stock',
    outOfStock: 'Out of stock',
    approxPrice: 'around {price}',
    offersPanelIntro: 'Where to buy {product} in {region}: the trusted retailers we track in this market, with the best price we have observed at each. Prices are checked regularly and can change, and the retailer confirms the final price at checkout.',
    offersPanelIntroSingleStore: 'Where to buy {product} in {region}: we track one retailer, {seller}, and show the price we last observed there; the price you see at checkout may differ.',
    affiliateDisclosureShort: 'We may earn a commission on purchases. It never affects our rankings.',
    amazonPriceAsOf: 'Amazon price · {time}',
    amazonPriceDisclaimer: 'Product prices and availability are accurate as of the date and time indicated and are subject to change. Any price and availability information displayed on Amazon at the time of purchase will apply to the purchase of this product.',
    priceAsOf: 'Price seen · {time}',
    belowUsualBadge: '{percent}% below its usual price',
    usualPriceTooltip: 'Usual price {price}: the median of the daily prices we observed at this store over the last 60 days, {days} days of data',
    officialSite: 'Official site',
    noDirectSales: 'Does not sell online in {region}',
    seeProductPage: "Product details on the brand's site",
    brandStoreBadge: 'Brand store',
    msrpLabel: 'MSRP {price}',
    memberPriceLabel: 'Member price',
    seePriceLabel: 'See price at {merchant}',
    marketplaceListing: 'Marketplace listing',
    dataProvidedBy: 'Data provided by {merchant}',
    dealBadge: 'Deal',
    dealEnds: 'Deal ends {time}',
    preorderLabel: 'Pre-order',
    publishedOn: 'Published on',
    publishingOn: 'Publishing on',
    quickMenu: 'Top Picks Summary',
    recentBlogs: 'PLACEHOLDER News',
    regionPrompt: {
      switchQuestion: {
        [RegionCode.Canada]: 'Would you like to switch to Canada?',
        [RegionCode.USA]: 'Would you like to switch to the United States?',
        [RegionCode.France]: 'Would you like to switch to France?',
        [RegionCode.UK]: 'Would you like to switch to the UK?',
        [RegionCode.Australia]: 'Would you like to switch to Australia?',
        [RegionCode.Brazil]: 'Would you like to switch to Brazil?',
        [RegionCode.Portugal]: 'Would you like to switch to Portugal?',
        [RegionCode.Germany]: 'Would you like to switch to Germany?',
        [RegionCode.Spain]: 'Would you like to switch to Spain?',
        [RegionCode.Italy]: 'Would you like to switch to Italy?',
        [RegionCode.Mexico]: 'Would you like to switch to Mexico?',
        [RegionCode.Switzerland]: 'Would you like to switch to Switzerland?',
        [RegionCode.Austria]: 'Would you like to switch to Austria?',
        [RegionCode.Belgium]: 'Would you like to switch to Belgium?',
        [RegionCode.Netherlands]: 'Would you like to switch to the Netherlands?',
        [RegionCode.Poland]: 'Would you like to switch to Poland?',
        [RegionCode.Sweden]: 'Would you like to switch to Sweden?',
        [RegionCode.Ireland]: 'Would you like to switch to Ireland?',
        [RegionCode.NewZealand]: 'Would you like to switch to New Zealand?',
        [RegionCode.India]: 'Would you like to switch to India?',
        [RegionCode.Argentina]: 'Would you like to switch to Argentina?',
        [RegionCode.Colombia]: 'Would you like to switch to Colombia?',
        [RegionCode.Chile]: 'Would you like to switch to Chile?',
        [RegionCode.Japan]: 'Would you like to switch to Japan?',
      },
      yesSwitch: 'Yes, switch region',
      noThanks: 'No, thanks',
      switchedNotice: {
        [RegionCode.Canada]: "You've been switched to our store for Canada",
        [RegionCode.USA]: "You've been switched to our store for the United States",
        [RegionCode.UK]: "You've been switched to our store for the UK",
        [RegionCode.France]: "You've been switched to our store for France",
      },
      goBackTo: {
        [RegionCode.Canada]: 'Go back to Canada',
        [RegionCode.USA]: 'Go back to the United States',
        [RegionCode.UK]: 'Go back to the UK',
        [RegionCode.France]: 'Go back to France',
      },
    },
    relatedTitle: 'Discover More',
    requirements: 'Requirements',
    responsibilities: 'Responsibilities',
    reviewSummary: 'Review Summary',
    buyerReviewSummary: 'What buyers say',
    amazonRatingLine: '{rating}/5 on Amazon',
    amazonStarsSource: 'on Amazon',
    amazonStarsSr: 'Rated {rating} out of 5 stars on Amazon',
    reviewBasisLine: 'AI summary of {n} reviews from {m} sources',
    reviewBasisLineOneSource: 'AI summary of {n} reviews from one source',
    xReviews: '{count} reviews',
    review: 'Summary',
    webReviewsTab: 'Quotes',
    searching: 'Searching for ',
    searchingProduct: 'Finding best alternatives for ',
    sidemenuListTitle: 'Our Best Choices',
    slogan: `We answer to you, not advertisers.`,
    submit: 'Submit',
    timeUiStrings: {
      noTimeAvailable: 'No time available',
      inTheFuture: 'In the future',
      justNow: 'just now',
      oneMinuteAgo: '1 minute ago',
      xMinutesAgo: '{x} minutes ago',
      over30Minutes: 'over 30 minutes ago',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Inception News',
    trademarkPolicy: 'Trademark Policy',
    trademarkPolicyContent: [
      "'Inception' is a registered trademark of InceptionAI Inc., duly registered and enforced in Canada as well as in various other jurisdictions. The use of the 'Inception' trademark or any trademark that is confusingly similar without the express written consent of InceptionAI Inc. is strictly prohibited. This policy extends to all trademark symbols, logos, and any other distinctive brand features associated with InceptionAI Inc.",
      'Other trademarks, service marks, logos, and brand names appearing on this website or within our products or services are the property of their respective owners. These include trademarks that are in use or registered in Canada and/or other countries. The mention or appearance of these trademarks on our website or in our products or services does not imply any affiliation with or endorsement by their respective owners unless expressly stated.',
      'We respect intellectual property rights and expect our users and partners to do the same. The unauthorized use of any trademark displayed on our platform or in connection with our products or services is not permitted. If you have any questions or concerns regarding the use of trademarks or this trademark policy, please contact:',
    ],
    tryAgain: 'Try again',
    uploadCV: 'Upload CV',
    viewDetails: 'View Details',
    viewMore: 'Show More',
    linkInput: {
      smartExtract: 'Smart extract:',
      apply: 'Apply',
      pasteHelper: 'Paste full URL for automatic username extraction',
      placeholderDefault: '---',
    },
    technicalSpecs: 'Tech. Specs.',
    nutritionFacts: 'Nutrition Facts',
    ingredients: 'Ingredients',
    materialFabric: 'Material & Fabric',
    dimensionsSize: 'Dimensions & Size',
    performanceMetrics: 'Performance Metrics',
    safetyFeatures: 'Safety Features',
    compatibility: 'Compatibility',
    careInstructions: 'Care Instructions',
    warrantySupport: 'Warranty & Support',
    welcome: 'Welcome to Inception',
    privacyPolicy: 'Privacy Policy',
    affiliateDisclosure: 'As an Amazon Associate and affiliate partner, Inception earns from qualifying purchases. This does not influence our rankings. Our product search and market analysis are separate from the selling part.',
    amazonAssociateStatement: 'As an Amazon Associate and affiliate partner, Inception earns from qualifying purchases.',
    articlesIntro: 'Inception Articles offers buying guides and product analyses, researched and written with AI, to help you make informed purchasing decisions.',
    fallbackNotice: "The specific page you're looking for wasn't found, but we're showing you related content instead.",
    aboutInceptionAi:
      `Inception is an AI shopping engine by InceptionAI Inc. that finds the best product for you {region}. An AI that answers to you, not to advertisers.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'in Canada',
      [Region.USA]: 'in the USA',
      [Region.France]: 'in France',
      [Region.UK]: 'in the UK',
      [Region.Australia]: 'in Australia',
      [Region.Brazil]: 'in Brazil',
      [Region.Portugal]: 'in Portugal',
      [Region.Germany]: 'in Germany',
      [Region.Spain]: 'in Spain',
      [Region.Italy]: 'in Italy',
      [Region.Mexico]: 'in Mexico',
      [Region.Switzerland]: 'in Switzerland',
      [Region.Austria]: 'in Austria',
      [Region.Belgium]: 'in Belgium',
      [Region.Netherlands]: 'in the Netherlands',
      [Region.Poland]: 'in Poland',
      [Region.Sweden]: 'in Sweden',
      [Region.Ireland]: 'in Ireland',
      [Region.NewZealand]: 'in New Zealand',
      [Region.India]: 'in India',
      [Region.Argentina]: 'in Argentina',
      [Region.Colombia]: 'in Colombia',
      [Region.Chile]: 'in Chile',
      [Region.Japan]: 'in Japan',
      [Region.All]: 'in your region',
    },
  },
  fr: {
    aboutTitle: `L'essentiel :`,
    aboutUs: 'À propos',
    vision: 'Notre vision',
    aboutUsTitle: 'Qu’est-ce que Inception?',
    ageVerification: {
      title: "Vérification d'âge requise",
      subtitle: 'Avertissement de contenu pour adultes',
      warning: 'Ce site contient du contenu pour adultes et est réservé aux personnes âgées de 18 ans ou plus.',
      disclaimer:
        'En entrant sur ce site, vous confirmez que vous êtes majeur dans votre juridiction et que vous souhaitez voir du contenu pour adultes.',
      confirmAge: "Je confirme que j'ai 18 ans ou plus",
      enterButton: 'Entrer sur le site',
      exitButton: 'Sortir',
      mustBe18: 'Vous devez avoir 18 ans ou plus pour accéder à ce contenu',
      adultContentWarning: 'Ce site contient du contenu explicite pour adultes destiné uniquement à un public mature.',
      euCompliance:
        "Cette vérification est conforme à la loi européenne sur les services numériques et aux exigences nationales de vérification d'âge.",
    },
    alternativeLabel: `*meilleure alternative trouvée`,
    articles: 'Articles',
    at: 'à',
    averagePriceTag: 'Moyenne: ',
    backToHome: 'Accueil',
    breadcrumbHome: 'Accueil',
    buyActionDescription: 'Comparez où acheter {product} sur Inception : les détaillants de confiance que nous suivons sur ce marché, le meilleur prix que nous y avons observé et l\'option que nous recommandons. Les prix sont vérifiés régulièrement et peuvent changer, et le détaillant confirme le prix final au moment du paiement.',
    buyActionDescriptionSingleStore: 'Pour ce produit nous suivons un détaillant, {seller}, et affichons le dernier prix que nous y avons observé; le prix au moment du paiement peut différer.',
    goBack: 'Retour',
    best: 'Meilleur PLACEHOLDER',
    bestFeatures: `Qu'est-ce qui fait de ce produit le meilleur?`,
    by: 'par',
    cancel: 'Annuler',
    partners: 'Partenaires',
    methodology: 'Notre méthode',
    careers: 'Carrières',
    choosePrice: 'En attendant, vous pouvez choisir une fourchette de prix qui vous convient.',
    comingSoon: 'Bientôt disponible',
    conclusion: 'Conclusion',
    cookieConsent: {
      title: 'Nous respectons votre vie privée',
      description: 'Nous utilisons des cookies pour améliorer votre expérience et fournir de meilleures recommandations de produits. Vos données restent privées et anonymes.',
      essential: 'Cookies essentiels',
      analytics: 'Cookies analytiques',
      advertising: 'Cookies publicitaires',
      learnMore: 'En savoir plus',
      acceptAll: 'Accepter et continuer',
      rejectAll: 'Refuser',
      customizeSettings: 'Personnaliser',
      savePreferences: 'Enregistrer mes préférences',
      essentialDescription: 'Nécessaires au bon fonctionnement du site. Ne peuvent pas être désactivés.',
      analyticsDescription: 'Nous aident à comprendre comment les visiteurs interagissent avec notre site en collectant des données anonymes.',
      advertisingDescription: 'Utilisés pour afficher des publicités pertinentes via Google AdSense. Chargés uniquement avec votre consentement.',
      californiaNotice: 'Résidents de Californie : Nous ne vendons pas vos informations personnelles. Refuser les cookies analytiques vous exclut de toute collecte de données non essentielles.',
    },
    changeRegion: 'Changer de région',
    shoppingRegionLabel: "Région d'achat : {region}",
    aiResearchCredit: "L'IA d'Inception a pris le temps de bien comprendre ce marché {region}",
    pageUpdatedOn: 'Mis à jour le {date}',
    contactUs: 'Contactez-nous',
    contactFormDescription: 'Veuillez remplir le formulaire ci-dessous.',
    defaultMetadata: {
      title: `Inception | Le meilleur produit pour vous, trouvé par une IA honnête`,
      description:
        `Le meilleur produit pour vous existe déjà. Notre IA le trouve, car c'est à vous que nous rendons des comptes, pas aux annonceurs. Inception vous offre des recommandations de produits issues d'une recherche par IA pour votre région, sans placement commandité.`,
    },
    descriptionArticle:
      'Notre mission est de transformer la façon dont les entreprises améliorent leur présence en ligne en leur permettant de fournir du contenu de qualité GRATUIT à leurs clients.',
    describeDemoCategory:
      'Ici, nous définirons les caractéristiques et qualités qui font d’un produit un leader dans sa catégorie. Ceci est essentiel pour produire une liste des meilleurs produits dans une catégorie spécifique, avec votre produit préféré en tête. Le reste de la liste présente des produits comparables, en tenant compte de la qualité et de la gamme de prix, avec un accent sur les entreprises canadiennes.',
    describeDemoComparison:
      'Ici, nous définirons les caractéristiques et qualités qui font d’un produit un leader dans sa catégorie pour créer une liste simple des meilleurs noms de produits basée sur vos critères de recherche. Cette liste exclut les détails supplémentaires et privilégie les entreprises locales de votre région pour notre processus de sélection.',
    directlySearch: 'Recherche directe',
    disconnect: 'Déconnexion',
    emailPlaceholder: 'Entrez votre email',
    emailTitle: 'Email',
    example: 'Exemple',
    phoneTitle: 'Numéro de téléphone',
    exploreMore: 'Explorez Davantage',
    existingPages: 'Visitez les pages existantes qui pourraient vous aider',
    articlesOtherRegions: 'De nos autres régions',
    firstPostComingSoon: `Votre premier article arrive bientôt, nous vous tiendrons informé!  
Entre-temps, vous pouvez modifier le contenu ci-dessous si vous le souhaitez.`,
    joinUs: 'Rejoignez-nous',
    contactForm: {
      personalInformation: 'Informations Personnelles',
      yourMessage: 'Votre Message',
      firstName: 'Prénom',
      firstNamePlaceholder: 'Entrez votre prénom',
      emailAddress: 'Adresse email',
      emailPlaceholder: 'Entrez votre adresse email',
      phoneNumber: 'Numéro de téléphone',
      phonePlaceholder: 'Entrez votre numéro de téléphone',
      company: 'Entreprise/Organisation',
      companyPlaceholder: 'Entrez le nom de votre entreprise',
      message: 'Parlez-nous de votre projet ou demande',
      messagePlaceholder: 'Partagez les détails de votre projet, vos exigences ou toute question que vous avez...',
      sendMessage: 'Envoyer le message',
      sending: 'Envoi en cours...',
      successTitle: 'Message envoyé avec succès !',
      successMessage: 'Merci pour votre message ! Nous vous recontacterons bientôt.',
      processingMessage: 'Traitement de votre demande...',
      errorTitle: "Erreur lors de l'envoi du message",
      errorMessage: "Une erreur s'est produite lors de l'envoi de votre message. Veuillez réessayer.",
    },
    loading: 'Chargement...',
    localeDisclaimer: 'Contenu 100% X',
    login: 'Connexion',
    lookingFor: 'Vous recherchez ?',
    // Language selector strings
    profile: 'Profil',
    otherLanguages: 'Autres Langues',
    showLess: 'Afficher Moins',
    showMoreLanguages: 'Afficher Plus de Langues',
    messageTitle: 'Message',
    mission: `Le meilleur produit pour vous existe déjà. Notre IA le trouve, car c'est à vous que nous rendons des comptes, pas aux annonceurs.`,
    namePlaceholder: 'Entrez votre nom',
    nameTitle: 'Nom',
    newProduct: 'NOUVEAU',
    newSearch: 'Nouvelle Recherche: ',
    nextStep: 'Étape suivante',
    noArticles: "Il n'y a actuellement aucun article",
    noResultsFound: 'ps. Je ne peux fournir que des suggestions sur des produits / catégories commercialisables.',
    noSearchResult: `Continuez à décrire le produit que vous recherchez, puis appuyez sur Entrée.`,
    officialWebsite: 'site officiel',
    orderNow: 'Commander maintenant',
    regulatedNotice: {
      prescriptionOnly: 'Sur ordonnance uniquement',
      professionalUse: 'Usage professionnel',
      fromVeterinarian: 'Disponible auprès de votre vétérinaire',
      fromPharmacist: 'Disponible en pharmacie',
      noOnlinePurchase: 'Non vendu en ligne',
      professionalSupply: 'Vendu par des fournisseurs professionnels',
      whyNoLink: "Nous évaluons ce produit mais ne proposons pas de lien d'achat.",
    },
    getTheBook: 'Obtenir le livre',
    listenOnAudible: 'Écouter sur Audible',
    browseOnAmazon: 'Parcourir sur Amazon',
    unavailableInCountry: 'Actuellement indisponible ({country})',
    searchSimilarOnAmazon: 'Chercher un produit similaire sur Amazon',
    searchNow: 'Rechercher',
    ourMission: 'Notre Mission',
    overviewTitle: 'Détails',
    askAQuestion: 'Une question ?',
    askSentHint: 'Écrivez-la en haut',
    micBlocked: 'Le microphone est bloqué. Autorisez-le dans les réglages de votre navigateur.',
    pageReference: 'Sur cette page',
    buyNow: 'Acheter maintenant',
    stores: 'magasins',
    seeAllOffers: 'Voir les {n} options d\'achat',
    chooseWhereToBuy: 'Où acheter',
    bestPlaceBadge: 'Notre choix',
    bestPriceBadge: 'Meilleur prix',
    atMerchant: 'chez {merchant}',
    otherStores: 'Plus de magasins',
    showAllOffers: 'Afficher les {n}',
    pricesChecked: 'Prix vérifiés {time}',
    inStock: 'En stock',
    outOfStock: 'Épuisé',
    approxPrice: 'environ {price}',
    offersPanelIntro: 'Où acheter {product} en {region} : les détaillants de confiance que nous suivons sur ce marché, avec le meilleur prix que nous avons observé chez chacun. Les prix sont vérifiés régulièrement et peuvent changer, et le détaillant confirme le prix final au moment du paiement.',
    offersPanelIntroSingleStore: 'Où acheter {product} en {region} : nous suivons un détaillant, {seller}, et affichons le dernier prix que nous y avons observé; le prix au moment du paiement peut différer.',
    affiliateDisclosureShort: 'Nous pouvons percevoir une commission sur les achats. Cela n\'influence jamais nos classements.',
    amazonPriceAsOf: 'Prix Amazon · {time}',
    amazonPriceDisclaimer: 'Les prix et la disponibilité des produits sont exacts à la date et à l\'heure indiquées et peuvent changer. Le prix et la disponibilité affichés sur Amazon au moment de l\'achat s\'appliquent à l\'achat de ce produit.',
    priceAsOf: 'Prix relevé · {time}',
    belowUsualBadge: '{percent} % sous son prix habituel',
    usualPriceTooltip: 'Prix habituel {price} : la médiane des prix quotidiens observés chez ce marchand au cours des 60 derniers jours, {days} jours de données',
    officialSite: 'Site officiel',
    noDirectSales: 'Ne vend pas en ligne ({region})',
    seeProductPage: 'Détails du produit sur le site de la marque',
    brandStoreBadge: 'Boutique de la marque',
    msrpLabel: 'PDSF {price}',
    memberPriceLabel: 'Prix membre',
    seePriceLabel: 'Voir le prix chez {merchant}',
    marketplaceListing: 'Annonce de place de marché',
    dataProvidedBy: 'Données fournies par {merchant}',
    dealBadge: 'Offre',
    dealEnds: 'Offre jusqu\'à {time}',
    preorderLabel: 'Précommande',
    publishedOn: 'Publié le',
    publishingOn: 'Publication le',
    quickMenu: 'Les meilleurs choix',
    recentBlogs: 'Articles récents - PLACEHOLDER',
    regionPrompt: {
      switchQuestion: {
        [RegionCode.Canada]: 'Souhaitez-vous passer au Canada\u00a0?',
        [RegionCode.USA]: 'Souhaitez-vous passer aux États-Unis\u00a0?',
        [RegionCode.France]: 'Souhaitez-vous passer en France\u00a0?',
        [RegionCode.UK]: 'Souhaitez-vous passer au Royaume-Uni\u00a0?',
        [RegionCode.Switzerland]: 'Souhaitez-vous passer en Suisse\u00a0?',
        [RegionCode.Belgium]: 'Souhaitez-vous passer en Belgique\u00a0?',
      },
      yesSwitch: 'Oui, changer de région',
      noThanks: 'Non, merci',
      switchedNotice: {
        [RegionCode.Canada]: 'Vous êtes maintenant sur notre boutique canadienne',
        [RegionCode.USA]: 'Vous êtes maintenant sur notre boutique américaine',
        [RegionCode.UK]: 'Vous êtes maintenant sur notre boutique britannique',
        [RegionCode.France]: 'Vous êtes maintenant sur notre boutique française',
      },
      goBackTo: {
        [RegionCode.Canada]: 'Revenir à la boutique canadienne',
        [RegionCode.USA]: 'Revenir à la boutique américaine',
        [RegionCode.UK]: 'Revenir à la boutique britannique',
        [RegionCode.France]: 'Revenir à la boutique française',
      },
    },
    relatedTitle: 'Découvrez plus',
    requirements: 'Exigences',
    responsibilities: 'Responsabilités',
    reviewSummary: 'Résumé des avis',
    buyerReviewSummary: 'Ce que disent les acheteurs',
    amazonRatingLine: '{rating}/5 sur Amazon',
    amazonStarsSource: 'sur Amazon',
    amazonStarsSr: 'Note sur Amazon : {rating} étoiles sur 5',
    reviewBasisLine: 'Résumé IA de {n} avis provenant de {m} sources',
    reviewBasisLineOneSource: "Résumé IA de {n} avis provenant d'une seule source",
    xReviews: '{count} avis',
    review: 'Avis',
    webReviewsTab: 'Avis du web',
    searching: 'Recherche de ',
    searchingProduct: 'Recherche des meilleures alternatives pour ',
    sidemenuListTitle: 'Nos meilleurs choix',
    slogan: `Nous rendons des comptes à vous, pas aux annonceurs.`,
    submit: 'Soumettre',
    technicalSpecs: 'Caractéristiques techniques',
    nutritionFacts: 'Informations nutritionnelles',
    ingredients: 'Ingrédients',
    materialFabric: 'Matériau et tissu',
    dimensionsSize: 'Dimensions et taille',
    performanceMetrics: 'Métriques de performance',
    safetyFeatures: 'Caractéristiques de sécurité',
    compatibility: 'Compatibilité',
    careInstructions: "Instructions d'entretien",
    warrantySupport: 'Garantie et support',
    timeUiStrings: {
      noTimeAvailable: "Pas d'heure disponible",
      inTheFuture: 'Dans le futur',
      justNow: "À l'instant",
      oneMinuteAgo: 'il y a 1 minute',
      xMinutesAgo: 'il y a {x} minutes',
      over30Minutes: 'il y a plus de 30 minutes',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Inception Articles',
    trademarkPolicy: 'Politique de marque',
    trademarkPolicyContent: [
      "'Inception' est une marque déposée d'InceptionAI Inc., dûment enregistrée et appliquée au Canada ainsi que dans diverses autres juridictions. L'utilisation de la marque 'Inception' ou de toute marque de manière similaire sans le consentement écrit exprès d'InceptionAI Inc. est strictement interdite. Cette politique s'étend à tous les symboles, logos et autres caractéristiques distinctives de la marque associés à InceptionAI Inc.",
      "Les autres marques, marques de service, logos et noms commerciaux apparaissant sur ce site web ou dans nos produits ou services sont la propriété de leurs propriétaires respectifs. Cela inclut les marques qui sont utilisées ou enregistrées au Canada et/ou dans d'autres pays. La mention ou l'apparition de ces marques sur notre site web ou dans nos produits ou services n'implique aucune affiliation avec ou approbation par leurs propriétaires respectifs, sauf déclaration expresse.",
      "Nous respectons les droits de propriété intellectuelle et attendons de nos utilisateurs et partenaires qu'ils en fassent autant. L'utilisation non autorisée de toute marque affichée sur notre plateforme ou en lien avec nos produits ou services n'est pas permise. Si vous avez des questions ou des préoccupations concernant l'utilisation des marques ou cette politique de marque, veuillez contacter:",
    ],
    tryAgain: 'Réessayer plus tard',
    uploadCV: 'Télécharger CV',
    viewDetails: 'Voir les détails',
    viewMore: 'Afficher plus',
    welcome: 'Bienvenue chez Inception',
    linkInput: {
      smartExtract: 'Extraction intelligente:',
      apply: 'Appliquer',
      pasteHelper: "Collez l'URL complète pour l'extraction automatique du nom d'utilisateur",
      placeholderDefault: '---',
    },
    privacyPolicy: 'Politique de confidentialité',
    affiliateDisclosure: "En tant que partenaire Amazon et affilié, Inception perçoit des commissions sur les achats éligibles. Cela n'influence pas nos classements. Notre recherche de produits et notre analyse du marché sont indépendantes de la partie vente.",
    amazonAssociateStatement: 'En tant que partenaire Amazon et affilié, Inception perçoit des commissions sur les achats éligibles.',
    articlesIntro: "Les articles Inception proposent des guides d'achat et des analyses de produits préparés avec l'IA, pour vous aider à prendre des décisions d'achat éclairées.",
    fallbackNotice:
      "La page spécifique que vous recherchez n'a pas été trouvée, mais nous vous montrons du contenu connexe à la place.",
    aboutInceptionAi:
      `Inception est un moteur d'achat IA d'InceptionAI Inc. qui trouve le meilleur produit pour vous {region}. Une IA qui vous rend des comptes, pas aux annonceurs.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'au Canada',
      [Region.USA]: "aux États-Unis",
      [Region.France]: 'en France',
      [Region.UK]: 'au Royaume-Uni',
      [Region.Australia]: "en Australie",
      [Region.Brazil]: 'au Brésil',
      [Region.Portugal]: 'au Portugal',
      [Region.Germany]: 'en Allemagne',
      [Region.Spain]: 'en Espagne',
      [Region.Italy]: 'en Italie',
      [Region.Mexico]: 'au Mexique',
      [Region.Switzerland]: 'en Suisse',
      [Region.Austria]: 'en Autriche',
      [Region.Belgium]: 'en Belgique',
      [Region.Netherlands]: 'aux Pays-Bas',
      [Region.Poland]: 'en Pologne',
      [Region.Sweden]: 'en Suède',
      [Region.Ireland]: 'en Irlande',
      [Region.NewZealand]: 'en Nouvelle-Zélande',
      [Region.India]: 'en Inde',
      [Region.Argentina]: 'en Argentine',
      [Region.Colombia]: 'en Colombie',
      [Region.Chile]: 'au Chili',
      [Region.Japan]: 'au Japon',
      [Region.All]: 'dans votre région',
    },
  },
  // Spanish
  es: {
    aboutTitle: 'Lo esencial:',
    aboutUs: 'Sobre nosotros',
    vision: 'Nuestra visión',
    aboutUsTitle: '¿Qué es Inception?',
    ageVerification: {
      title: 'Verificación de edad requerida',
      subtitle: 'Advertencia de contenido para adultos',
      warning: 'Este sitio contiene contenido para adultos y es solo para personas de 18 años o más.',
      disclaimer:
        'Al ingresar a este sitio, confirmas que eres mayor de edad en tu jurisdicción y que deseas ver contenido para adultos.',
      confirmAge: 'Confirmo que tengo 18 años o más',
      enterButton: 'Entrar al sitio',
      exitButton: 'Salir',
      mustBe18: 'Debes tener 18 años o más para acceder a este contenido',
      adultContentWarning: 'Este sitio contiene contenido explícito para adultos destinado únicamente a audiencias maduras.',
      euCompliance:
        'Esta verificación cumple con la Ley de Servicios Digitales de la UE y los requisitos nacionales de verificación de edad.',
    },
    alternativeLabel: `*mejor alternativa que encontramos`,
    at: 'en',
    averagePriceTag: 'Promedio: ',
    best: 'Mejor PLACEHOLDER',
    bestFeatures: '¿Qué hace que un producto sea el mejor en su categoría?',
    articles: 'Artículos',
    backToHome: 'Inicio',
    breadcrumbHome: 'Inicio',
    buyActionDescription: 'Compara dónde comprar {product} en Inception: las tiendas de confianza que seguimos en este mercado, el mejor precio que hemos observado en cada una y la opción que recomendamos. Los precios se comprueban con regularidad y pueden cambiar, y la tienda confirma el precio final al finalizar la compra.',
    buyActionDescriptionSingleStore: 'Para este producto seguimos una tienda, {seller}, y mostramos el último precio que observamos allí; el precio al finalizar la compra puede diferir.',
    goBack: 'Atrás',
    by: 'por',
    cancel: 'Cancelar',
    partners: 'Socios',
    methodology: 'Cómo elegimos',
    careers: 'Carreras',
    changeRegion: 'Cambiar región',
    shoppingRegionLabel: 'Región de compra: {region}',
    choosePrice: 'Mientras tanto, puedes elegir un rango de precios que te convenga.',
    comingSoon: 'Próximamente',
    conclusion: 'Conclusión',
    cookieConsent: {
      title: 'Valoramos tu privacidad',
      description: 'Usamos cookies para mejorar tu experiencia y ofrecer mejores recomendaciones de productos. Tus datos permanecen privados y anónimos.',
      essential: 'Cookies esenciales',
      analytics: 'Cookies analíticas',
      advertising: 'Cookies publicitarias',
      learnMore: 'Más información',
      acceptAll: 'Aceptar y continuar',
      rejectAll: 'Rechazar',
      customizeSettings: 'Personalizar',
      savePreferences: 'Guardar preferencias',
      essentialDescription: 'Necesarias para que el sitio funcione correctamente. No se pueden desactivar.',
      analyticsDescription: 'Nos ayudan a entender cómo los visitantes interactúan con nuestro sitio recopilando datos anónimos.',
      advertisingDescription: 'Utilizadas para mostrar anuncios relevantes mediante Google AdSense. Solo se cargan con tu consentimiento.',
      californiaNotice: 'Residentes de California: No vendemos su información personal. Rechazar las cookies analíticas te excluye de toda recopilación de datos no esenciales.',
    },
    aiResearchCredit: 'La IA de Inception dedicó tiempo a entender bien este mercado {region}',
    pageUpdatedOn: 'Actualizado el {date}',
    contactUs: 'Contáctanos',
    contactFormDescription: 'Por favor, completa el siguiente formulario.',
    defaultMetadata: {
      title: `Inception | El mejor producto para ti, encontrado por una IA honesta`,
      description:
        `El mejor producto para ti ya existe. Nuestra IA lo encuentra, porque respondemos ante ti, no ante los anunciantes. Inception te ofrece recomendaciones de productos investigadas con IA para tu región, sin productos patrocinados.`,
    },
    descriptionArticle:
      'Nuestra misión es transformar la forma en que las empresas mejoran su presencia en línea al permitirles ofrecer contenido de calidad GRATIS a sus clientes.',
    describeDemoCategory:
      'Aquí definiremos las características y cualidades que hacen de un producto un candidato destacado o líder en esta categoría. Esto es esencial para generar una lista de los mejores productos en una categoría específica, con tu producto deseado en primer lugar. El resto de la lista presenta productos comparables, considerando tanto la calidad como el rango de precios, con un enfoque en empresas canadienses.',
    describeDemoComparison:
      'Aquí definiremos las características y cualidades que hacen de un producto un candidato destacado o líder en esta categoría para crear una lista sencilla con los mejores nombres de productos según tus criterios de búsqueda. Esta lista excluye detalles adicionales y prioriza empresas locales de tu región en nuestro proceso de selección.',
    directlySearch: 'Buscar directamente',
    disconnect: 'Desconectar',
    emailPlaceholder: 'Introduce tu correo electrónico',
    emailTitle: 'Correo electrónico',
    example: 'Ejemplo',
    phoneTitle: 'Número de teléfono',
    exploreMore: 'Explorar más',
    existingPages: 'Visita páginas existentes que podrían ayudarte',
    articlesOtherRegions: 'De nuestras otras regiones',
    firstPostComingSoon: `¡Tu primera publicación llegará pronto, te mantendremos informado!
Mientras tanto, puedes editar el contenido a continuación si lo deseas.`,
    joinUs: 'Únete a nosotros.',
    contactForm: {
      personalInformation: 'Información Personal',
      yourMessage: 'Tu Mensaje',
      firstName: 'Nombre',
      firstNamePlaceholder: 'Ingresa tu nombre',
      emailAddress: 'Dirección de Email',
      emailPlaceholder: 'Ingresa tu dirección de email',
      phoneNumber: 'Número de Teléfono',
      phonePlaceholder: 'Ingresa tu número de teléfono',
      company: 'Empresa/Organización',
      companyPlaceholder: 'Ingresa el nombre de tu empresa',
      message: 'Cuéntanos sobre tu proyecto o consulta',
      messagePlaceholder: 'Comparte los detalles de tu proyecto, requisitos o cualquier pregunta que tengas...',
      sendMessage: 'Enviar Mensaje',
      sending: 'Enviando...',
      successTitle: '¡Mensaje Enviado Exitosamente!',
      successMessage: '¡Gracias por tu mensaje! Te contactaremos pronto.',
      processingMessage: 'Procesando tu solicitud...',
      errorTitle: 'Error al Enviar el Mensaje',
      errorMessage: 'Hubo un error al enviar tu mensaje. Por favor intenta de nuevo.',
    },
    loading: 'Cargando...',
    localeDisclaimer: 'Solo X',
    login: 'Iniciar sesión',
    lookingFor: '¿Qué buscas?',
    // Language selector strings
    profile: 'Perfil',
    otherLanguages: 'Otros Idiomas',
    showLess: 'Mostrar Menos',
    showMoreLanguages: 'Mostrar Más Idiomas',
    messageTitle: 'Mensaje',
    mission: `El mejor producto para ti ya existe. Nuestra IA lo encuentra, porque respondemos ante ti, no ante los anunciantes.`,
    namePlaceholder: 'Introduce tu nombre',
    nameTitle: 'Nombre',
    newProduct: 'RECIÉN AÑADIDO',
    newSearch: 'Nueva búsqueda: ',
    nextStep: 'Siguiente paso',
    noArticles: 'Actualmente no hay artículos',
    noResultsFound: 'PD: Solo puedo ofrecer sugerencias sobre productos o categorías comercializables.',
    noSearchResult: 'Sigue describiendo el producto que buscas y luego presiona Enter.',
    officialWebsite: 'sitio web oficial',
    orderNow: 'Ordenar ahora',
    regulatedNotice: {
      prescriptionOnly: 'Solo con receta',
      professionalUse: 'Uso profesional',
      fromVeterinarian: 'Disponible a través de su veterinario',
      fromPharmacist: 'Disponible en farmacia',
      noOnlinePurchase: 'No se vende en línea',
      professionalSupply: 'Se vende a través de proveedores profesionales',
      whyNoLink: 'Analizamos este producto pero no ofrecemos enlace de compra.',
    },
    getTheBook: 'Obtener el libro',
    listenOnAudible: 'Escuchar en Audible',
    browseOnAmazon: 'Explorar en Amazon',
    unavailableInCountry: 'No disponible actualmente en {country}',
    searchSimilarOnAmazon: 'Buscar similares en Amazon',
    searchNow: 'Buscar',
    ourMission: 'Nuestra misión',
    overviewTitle: 'Detalles',
    askAQuestion: '¿Tienes una pregunta?',
    askSentHint: 'Escríbela arriba',
    micBlocked: 'El micrófono está bloqueado. Permítelo en la configuración de tu navegador.',
    pageReference: 'En esta página',
    buyNow: 'Compra ahora',
    stores: 'tiendas',
    seeAllOffers: 'Ver las {n} opciones de compra',
    chooseWhereToBuy: 'Dónde comprar',
    bestPlaceBadge: 'Recomendado',
    bestPriceBadge: 'Mejor precio',
    atMerchant: 'en {merchant}',
    otherStores: 'Más tiendas',
    showAllOffers: 'Ver las {n}',
    pricesChecked: 'Precios verificados {time}',
    inStock: 'Disponible',
    outOfStock: 'Agotado',
    approxPrice: 'aprox. {price}',
    offersPanelIntro: 'Dónde comprar {product} en {region}: las tiendas de confianza que seguimos en este mercado, con el mejor precio que hemos observado en cada una. Los precios se comprueban con regularidad y pueden cambiar, y la tienda confirma el precio final al finalizar la compra.',
    offersPanelIntroSingleStore: 'Dónde comprar {product} en {region}: seguimos una tienda, {seller}, y mostramos el último precio que observamos allí; el precio al finalizar la compra puede diferir.',
    affiliateDisclosureShort: 'Podemos ganar una comisión por las compras. Nunca influye en nuestras clasificaciones.',
    amazonPriceAsOf: 'Precio de Amazon · {time}',
    amazonPriceDisclaimer: 'Los precios y la disponibilidad de los productos son exactos en la fecha y hora indicadas y están sujetos a cambios. La información de precio y disponibilidad que aparezca en Amazon en el momento de la compra se aplicará a la compra de este producto.',
    priceAsOf: 'Precio visto · {time}',
    belowUsualBadge: '{percent}% por debajo de su precio habitual',
    usualPriceTooltip: 'Precio habitual {price}: la mediana de los precios diarios que observamos en esta tienda durante los últimos 60 días, {days} días de datos',
    officialSite: 'Sitio oficial',
    noDirectSales: 'No vende en línea ({region})',
    seeProductPage: 'Detalles del producto en el sitio de la marca',
    brandStoreBadge: 'Tienda de la marca',
    msrpLabel: 'PVP {price}',
    memberPriceLabel: 'Precio para miembros',
    seePriceLabel: 'Ver el precio en {merchant}',
    marketplaceListing: 'Anuncio de marketplace',
    dataProvidedBy: 'Datos proporcionados por {merchant}',
    dealBadge: 'Oferta',
    dealEnds: 'Oferta hasta {time}',
    preorderLabel: 'Reserva',
    publishedOn: 'Publicado el',
    publishingOn: 'Publicación el',
    quickMenu: 'Resumen de las mejores selecciones',
    recentBlogs: 'Noticias recientes - PLACEHOLDER',
    regionPrompt: {
      switchQuestion: {
        [RegionCode.USA]: '¿Te gustaría cambiar a Estados Unidos?',
        [RegionCode.Spain]: '¿Te gustaría cambiar a España?',
        [RegionCode.Mexico]: '¿Te gustaría cambiar a México?',
        [RegionCode.Argentina]: '¿Te gustaría cambiar a Argentina?',
        [RegionCode.Colombia]: '¿Te gustaría cambiar a Colombia?',
        [RegionCode.Chile]: '¿Te gustaría cambiar a Chile?',
      },
      yesSwitch: 'Sí, cambiar región',
      noThanks: 'No, gracias',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'Descubre más',
    requirements: 'Requisitos',
    responsibilities: 'Responsabilidades',
    reviewSummary: 'Resumen de reseñas',
    buyerReviewSummary: 'Lo que dicen los compradores',
    amazonRatingLine: '{rating}/5 en Amazon',
    amazonStarsSource: 'en Amazon',
    amazonStarsSr: 'Calificación de {rating} sobre 5 estrellas en Amazon',
    reviewBasisLine: 'Resumen IA de {n} reseñas de {m} fuentes',
    reviewBasisLineOneSource: 'Resumen IA de {n} reseñas de una sola fuente',
    xReviews: '{count} reseñas',
    review: 'Reseña',
    searching: 'Buscando ',
    searchingProduct: 'Encontrando las mejores alternativas para ',
    sidemenuListTitle: 'Nuestras mejores elecciones',
    slogan: `Respondemos ante ti, no ante los anunciantes.`,
    submit: 'Enviar',
    technicalSpecs: 'Especificaciones técnicas',
    nutritionFacts: 'Información Nutricional',
    ingredients: 'Ingredientes',
    materialFabric: 'Material y Tela',
    dimensionsSize: 'Dimensiones y Tamaño',
    performanceMetrics: 'Métricas de Rendimiento',
    safetyFeatures: 'Características de Seguridad',
    compatibility: 'Compatibilidad',
    careInstructions: 'Instrucciones de Cuidado',
    warrantySupport: 'Garantía y Soporte',
    timeUiStrings: {
      noTimeAvailable: 'No hay tiempo disponible',
      inTheFuture: 'En el futuro',
      justNow: 'justo ahora',
      oneMinuteAgo: 'hace 1 minuto',
      xMinutesAgo: 'hace {x} minutos',
      over30Minutes: 'hace más de 30 minutos',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Noticias de Inception',
    trademarkPolicy: 'Política de marcas',
    trademarkPolicyContent: [
      `'Inception' es una marca registrada de InceptionAI Inc., debidamente registrada y aplicada en Canadá, así como en diversas otras jurisdicciones. El uso de la marca 'Inception' o de cualquier marca que resulte confusamente similar sin el consentimiento expreso por escrito de InceptionAI Inc. está estrictamente prohibido. Esta política se extiende a todos los símbolos de marca, logotipos y cualquier otro elemento distintivo de la marca asociado con InceptionAI Inc.`,
      'Otras marcas registradas, marcas de servicio, logotipos y nombres comerciales que aparecen en este sitio web o dentro de nuestros productos o servicios son propiedad de sus respectivos propietarios. Esto incluye marcas que están en uso o registradas en Canadá y/o en otros países. La mención o aparición de estas marcas en nuestro sitio web o en nuestros productos o servicios no implica ninguna afiliación con o respaldo por parte de sus respectivos propietarios a menos que se indique expresamente.',
      'Respetamos los derechos de propiedad intelectual y esperamos que nuestros usuarios y socios hagan lo mismo. El uso no autorizado de cualquier marca registrada mostrada en nuestra plataforma o en relación con nuestros productos o servicios no está permitido. Si tienes alguna pregunta o inquietud con respecto al uso de marcas registradas o esta política de marcas, por favor contacta con:',
    ],
    tryAgain: 'Inténtalo de nuevo',
    uploadCV: 'Subir CV',
    viewDetails: 'Ver detalles',
    viewMore: 'Mostrar más',
    welcome: 'Bienvenido a Inception',
    linkInput: {
      smartExtract: 'Extracción inteligente:',
      apply: 'Aplicar',
      pasteHelper: 'Pega la URL completa para la extracción automática del nombre de usuario',
      placeholderDefault: '---',
    },
    privacyPolicy: 'Política de Privacidad',
    affiliateDisclosure: 'Como asociado de Amazon y socio afiliado, Inception obtiene ingresos por compras que califican. Esto no influye en nuestras clasificaciones. Nuestra búsqueda de productos y análisis de mercado son independientes de la parte de ventas.',
    amazonAssociateStatement: 'Como asociado de Amazon y socio afiliado, Inception obtiene ingresos por compras que califican.',
    articlesIntro: 'Los artículos de Inception ofrecen guías de compra y análisis de productos elaborados con IA para ayudarte a tomar decisiones de compra informadas.',
    fallbackNotice:
      'La página específica que estás buscando no se encontró, pero te estamos mostrando contenido relacionado en su lugar.',
    aboutInceptionAi:
      `Inception encuentra el mejor producto para ti {region}, con una IA que responde ante ti, no ante los anunciantes.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'en Canadá',
      [Region.USA]: 'en Estados Unidos',
      [Region.France]: 'en Francia',
      [Region.UK]: 'en Reino Unido',
      [Region.Australia]: 'en Australia',
      [Region.Brazil]: 'en Brasil',
      [Region.Portugal]: 'en Portugal',
      [Region.Germany]: 'en Alemania',
      [Region.Spain]: 'en España',
      [Region.Italy]: 'en Italia',
      [Region.Mexico]: 'en México',
      [Region.Switzerland]: 'en Suiza',
      [Region.Austria]: 'en Austria',
      [Region.Belgium]: 'en Bélgica',
      [Region.Netherlands]: 'en los Países Bajos',
      [Region.Poland]: 'en Polonia',
      [Region.Sweden]: 'en Suecia',
      [Region.Ireland]: 'en Irlanda',
      [Region.NewZealand]: 'en Nueva Zelanda',
      [Region.India]: 'en la India',
      [Region.Argentina]: 'en Argentina',
      [Region.Colombia]: 'en Colombia',
      [Region.Chile]: 'en Chile',
      [Region.Japan]: 'en Japón',
      [Region.All]: 'en tu región',
    },
  },

  // German
  de: {
    aboutTitle: 'Das Wesentliche:',
    aboutUs: 'Über uns',
    vision: 'Unsere Vision',
    aboutUsTitle: 'Was ist Inception?',
    ageVerification: {
      title: 'Altersverifikation erforderlich',
      subtitle: 'Warnung vor Inhalten für Erwachsene',
      warning: 'Diese Website enthält Inhalte für Erwachsene und ist nur für Personen ab 18 Jahren bestimmt.',
      disclaimer:
        'Durch das Betreten dieser Website bestätigen Sie, dass Sie in Ihrer Rechtsordnung volljährig sind und Inhalte für Erwachsene anzeigen möchten.',
      confirmAge: 'Ich bestätige, dass ich 18 Jahre oder älter bin',
      enterButton: 'Website betreten',
      exitButton: 'Verlassen',
      mustBe18: 'Sie müssen 18 Jahre oder älter sein, um auf diese Inhalte zugreifen zu können',
      adultContentWarning:
        'Diese Website enthält explizite Inhalte für Erwachsene, die nur für erwachsene Zielgruppen bestimmt sind.',
      euCompliance:
        'Diese Verifikation entspricht dem EU-Gesetz über digitale Dienste und nationalen Altersverifikationsanforderungen.',
    },
    alternativeLabel: `*beste Alternative, die wir gefunden haben`,
    at: 'bei',
    averagePriceTag: 'Durchschnitt: ',
    best: 'Beste/r/s PLACEHOLDER',
    bestFeatures: 'Was macht ein Produkt zum besten in seiner Kategorie?',
    articles: 'Artikel',
    backToHome: 'Startseite',
    breadcrumbHome: 'Startseite',
    buyActionDescription: 'Vergleichen Sie auf Inception, wo Sie {product} kaufen können: die vertrauenswürdigen Händler, die wir in diesem Markt beobachten, den besten dort beobachteten Preis und die von uns empfohlene Option. Preise werden regelmäßig geprüft und können sich ändern, und der Händler bestätigt den endgültigen Preis beim Bezahlvorgang.',
    goBack: 'Zurück',
    by: 'von',
    cancel: 'Abbrechen',
    partners: 'Partner',
    methodology: 'So wählen wir aus',
    careers: 'Karriere',
    changeRegion: 'Region ändern',
    shoppingRegionLabel: 'Einkaufsregion: {region}',
    choosePrice: 'In der Zwischenzeit kannst du eine Preisspanne wählen, die dir passt.',
    comingSoon: 'Demnächst verfügbar',
    conclusion: 'Fazit',
    cookieConsent: {
      title: 'Wir respektieren Ihre Privatsphäre',
      description: 'Wir verwenden Cookies, um Ihre Erfahrung zu verbessern und bessere Produktempfehlungen zu bieten. Ihre Daten bleiben privat und anonym.',
      essential: 'Notwendige Cookies',
      analytics: 'Analyse-Cookies',
      advertising: 'Werbe-Cookies',
      learnMore: 'Mehr erfahren',
      acceptAll: 'Akzeptieren & Fortfahren',
      rejectAll: 'Ablehnen',
      customizeSettings: 'Anpassen',
      savePreferences: 'Einstellungen speichern',
      essentialDescription: 'Erforderlich für das ordnungsgemäße Funktionieren der Website. Können nicht deaktiviert werden.',
      analyticsDescription: 'Helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, indem anonyme Daten gesammelt werden.',
      advertisingDescription: 'Werden für die Anzeige relevanter Werbung über Google AdSense verwendet. Werden nur mit Ihrer Zustimmung geladen.',
      californiaNotice: 'Einwohner Kaliforniens: Wir verkaufen Ihre persönlichen Daten nicht. Das Ablehnen von Analyse-Cookies schließt Sie von jeder nicht wesentlichen Datenerfassung aus.',
    },
    contactUs: 'Kontakt',
    contactFormDescription: 'Bitte fülle das folgende Formular aus.',
    defaultMetadata: {
      title: `Inception | Das beste Produkt für dich, gefunden von ehrlicher KI`,
      description:
        `Das beste Produkt für dich gibt es bereits. Unsere KI findet es, denn wir sind dir verpflichtet, nicht den Werbetreibenden. Inception bietet dir KI-recherchierte Produktempfehlungen für deine Region, ohne gesponserte Platzierungen.`,
    },
    descriptionArticle:
      'Unsere Mission ist es, die Art und Weise zu verändern, wie Unternehmen ihre Online-Präsenz verbessern, indem wir ihnen ermöglichen, ihren Kunden qualitativ hochwertige Inhalte KOSTENLOS zur Verfügung zu stellen.',
    describeDemoCategory:
      'Hier definieren wir die Merkmale und Qualitäten, die ein Produkt zu einem Spitzenkandidaten oder Marktführer in dieser Kategorie machen. Dies ist entscheidend, um eine Liste der besten Produkte in einer bestimmten Kategorie zu erstellen, wobei dein gewünschtes Produkt an erster Stelle steht. Der Rest der Liste enthält vergleichbare Produkte, wobei Qualität und Preisspanne berücksichtigt werden, mit einem Fokus auf kanadische Unternehmen.',
    describeDemoComparison:
      'Hier definieren wir die Merkmale und Qualitäten, die ein Produkt zu einem Spitzenkandidaten oder Marktführer in dieser Kategorie machen, um eine klare Liste der besten Produktnamen basierend auf deinen Suchkriterien zu erstellen. Diese Liste schließt zusätzliche Details aus und priorisiert lokale Unternehmen in deiner Region für unseren Auswahlprozess.',
    directlySearch: 'Direkt suchen',
    disconnect: 'Verbindung trennen',
    emailPlaceholder: 'Gib deine E-Mail-Adresse ein',
    emailTitle: 'E-Mail',
    example: 'Beispiel',
    phoneTitle: 'Telefonnummer',
    exploreMore: 'Mehr entdecken',
    existingPages: 'Besuche vorhandene Seiten, die dir helfen könnten',
    articlesOtherRegions: 'Aus unseren anderen Regionen',
    firstPostComingSoon: `Dein erster Beitrag kommt bald, wir halten dich auf dem Laufenden!
In der Zwischenzeit kannst du den folgenden Inhalt bearbeiten, falls du möchtest.`,
    joinUs: 'Mach mit.',
    contactForm: {
      personalInformation: 'Persönliche Informationen',
      yourMessage: 'Deine Nachricht',
      firstName: 'Vorname',
      firstNamePlaceholder: 'Gib deinen Vornamen ein',
      emailAddress: 'E-Mail-Adresse',
      emailPlaceholder: 'Gib deine E-Mail-Adresse ein',
      phoneNumber: 'Telefonnummer',
      phonePlaceholder: 'Gib deine Telefonnummer ein',
      company: 'Unternehmen/Organisation',
      companyPlaceholder: 'Gib deinen Firmennamen ein',
      message: 'Erzähl uns von deinem Projekt oder deiner Anfrage',
      messagePlaceholder: 'Teile die Details deines Projekts, Anforderungen oder Fragen mit...',
      sendMessage: 'Nachricht Senden',
      sending: 'Wird gesendet...',
      successTitle: 'Nachricht Erfolgreich Gesendet!',
      successMessage: 'Danke für deine Nachricht! Wir werden uns bald bei dir melden.',
      processingMessage: 'Deine Anfrage wird bearbeitet...',
      errorTitle: 'Fehler beim Senden der Nachricht',
      errorMessage: 'Beim Senden deiner Nachricht ist ein Fehler aufgetreten. Bitte versuche es erneut.',
    },
    loading: 'Wird geladen...',
    localeDisclaimer: 'Nur X',
    login: 'Anmelden',
    lookingFor: 'Wonach suchst du?',
    // Language selector strings
    profile: 'Profil',
    otherLanguages: 'Andere Sprachen',
    showLess: 'Weniger anzeigen',
    showMoreLanguages: 'Mehr Sprachen anzeigen',
    messageTitle: 'Nachricht',
    mission: `Das beste Produkt für dich gibt es bereits. Unsere KI findet es, denn wir sind dir verpflichtet, nicht den Werbetreibenden.`,
    namePlaceholder: 'Gib deinen Namen ein',
    nameTitle: 'Name',
    newProduct: 'NEU HINZUGEFÜGT',
    newSearch: 'Neue Suche: ',
    nextStep: 'Nächster Schritt',
    noArticles: 'Derzeit gibt es keine Artikel',
    noResultsFound: 'P.S. Ich kann nur Vorschläge zu vermarktbaren Produkten oder Kategorien machen.',
    noSearchResult: 'Beschreibe weiterhin das Produkt, das du suchst, und drücke dann Enter.',
    officialWebsite: 'offizielle Website',
    orderNow: 'Jetzt bestellen',
    regulatedNotice: {
      prescriptionOnly: 'Verschreibungspflichtig',
      professionalUse: 'Für den professionellen Gebrauch',
      fromVeterinarian: 'Erhältlich bei Ihrem Tierarzt',
      fromPharmacist: 'In der Apotheke erhältlich',
      noOnlinePurchase: 'Nicht online erhältlich',
      professionalSupply: 'Vertrieb über Fachhändler',
      whyNoLink: 'Wir bewerten dieses Produkt, verlinken es aber nicht.',
    },
    getTheBook: 'Buch holen',
    listenOnAudible: 'Auf Audible hören',
    browseOnAmazon: 'Auf Amazon stöbern',
    unavailableInCountry: 'Derzeit nicht verfügbar in {country}',
    searchSimilarOnAmazon: 'Ähnliche Produkte auf Amazon suchen',
    searchNow: 'Suchen',
    ourMission: 'Unsere Mission',
    overviewTitle: 'Details',
    askAQuestion: 'Frage stellen?',
    askSentHint: 'Oben eintippen',
    micBlocked: 'Das Mikrofon ist blockiert. Erlaube es in den Einstellungen deines Browsers.',
    pageReference: 'Auf dieser Seite',
    buyNow: 'Jetzt kaufen',
    stores: 'Shops',
    seeAllOffers: 'Alle {n} Kaufoptionen ansehen',
    chooseWhereToBuy: 'Wo kaufen',
    bestPlaceBadge: 'Unsere Wahl',
    bestPriceBadge: 'Bester Preis',
    atMerchant: 'bei {merchant}',
    otherStores: 'Weitere Shops',
    showAllOffers: 'Alle {n} anzeigen',
    pricesChecked: 'Preise geprüft {time}',
    inStock: 'Auf Lager',
    outOfStock: 'Ausverkauft',
    approxPrice: 'ca. {price}',
    offersPanelIntro: 'Wo Sie {product} in {region} kaufen können: die vertrauenswürdigen Händler, die wir in diesem Markt beobachten, mit dem besten Preis, den wir bei jedem beobachtet haben. Preise werden regelmäßig geprüft und können sich ändern, und der Händler bestätigt den endgültigen Preis beim Bezahlvorgang.',
    affiliateDisclosureShort: 'Wir erhalten unter Umständen eine Provision für Käufe. Unsere Rankings beeinflusst das nie.',
    amazonPriceAsOf: 'Amazon-Preis · {time}',
    amazonPriceDisclaimer: 'Preise und Verfügbarkeit der Produkte gelten zum angegebenen Datum und Zeitpunkt und können sich ändern. Für den Kauf dieses Produkts gelten die zum Zeitpunkt des Kaufs auf Amazon angezeigten Preis- und Verfügbarkeitsangaben.',
    priceAsOf: 'Preis gesehen · {time}',
    belowUsualBadge: '{percent} % unter dem üblichen Preis',
    usualPriceTooltip: 'Üblicher Preis {price}: der Median der Tagespreise, die wir in den letzten 60 Tagen bei diesem Händler beobachtet haben, {days} Tage Daten',
    officialSite: 'Offizielle Website',
    noDirectSales: 'Kein Online-Verkauf ({region})',
    seeProductPage: 'Produktdetails auf der Website der Marke',
    brandStoreBadge: 'Markenshop',
    msrpLabel: 'UVP {price}',
    memberPriceLabel: 'Mitgliederpreis',
    seePriceLabel: 'Preis bei {merchant} ansehen',
    marketplaceListing: 'Marktplatz-Angebot',
    dataProvidedBy: 'Daten bereitgestellt von {merchant}',
    dealBadge: 'Angebot',
    dealEnds: 'Angebot bis {time}',
    preorderLabel: 'Vorbestellung',
    publishedOn: 'Veröffentlicht am',
    publishingOn: 'Veröffentlichung am',
    quickMenu: 'Übersicht der Top-Auswahlen',
    recentBlogs: 'Aktuelle Nachrichten - PLACEHOLDER',
    regionPrompt: {
      switchQuestion: {
        [RegionCode.Germany]: 'Möchten Sie zu Deutschland wechseln?',
        [RegionCode.Italy]: 'Möchten Sie zu Italien wechseln?',
        [RegionCode.Switzerland]: 'Möchten Sie zur Schweiz wechseln?',
        [RegionCode.Austria]: 'Möchten Sie zu Österreich wechseln?',
        [RegionCode.Belgium]: 'Möchten Sie zu Belgien wechseln?',
      },
      yesSwitch: 'Ja, Region wechseln',
      noThanks: 'Nein, danke',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'Mehr entdecken',
    requirements: 'Anforderungen',
    responsibilities: 'Verantwortlichkeiten',
    reviewSummary: 'Zusammenfassung der Bewertungen',
    xReviews: '{count} Bewertungen',
    review: 'Bewertung',
    searching: 'Suche nach ',
    searchingProduct: 'Finde die besten Alternativen für ',
    sidemenuListTitle: 'Unsere besten Auswahlen',
    slogan: `Wir sind dir verpflichtet, nicht den Werbetreibenden.`,
    submit: 'Einreichen',
    technicalSpecs: 'Technische Spezifikationen',
    nutritionFacts: 'Nährwerte',
    ingredients: 'Inhaltsstoffe',
    materialFabric: 'Material & Stoff',
    dimensionsSize: 'Abmessungen & Größe',
    performanceMetrics: 'Leistungsmetriken',
    safetyFeatures: 'Sicherheitsmerkmale',
    compatibility: 'Kompatibilität',
    careInstructions: 'Pflegehinweise',
    warrantySupport: 'Garantie & Support',
    timeUiStrings: {
      noTimeAvailable: 'Keine Zeit verfügbar',
      inTheFuture: 'In der Zukunft',
      justNow: 'Gerade jetzt',
      oneMinuteAgo: 'vor 1 Minute',
      xMinutesAgo: 'vor {x} Minuten',
      over30Minutes: 'vor mehr als 30 Minuten',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Inception Nachrichten',
    trademarkPolicy: 'Markenrichtlinie',
    trademarkPolicyContent: [
      `'Inception' ist eine eingetragene Marke von InceptionAI Inc., die ordnungsgemäß in Kanada sowie in verschiedenen anderen Gerichtsbarkeiten registriert und angewendet ist. Die Verwendung der Marke 'Inception' oder einer ähnlichen Marke ohne die ausdrückliche schriftliche Zustimmung von InceptionAI Inc. ist strengstens untersagt. Diese Richtlinie erstreckt sich auf alle Markensymbole, Logos und andere kennzeichnenden Merkmale, die mit InceptionAI Inc. verbunden sind.`,
      'Andere Marken, Dienstleistungsmarken, Logos und Handelsnamen, die auf dieser Website oder in unseren Produkten oder Dienstleistungen erscheinen, sind Eigentum ihrer jeweiligen Eigentümer. Dazu gehören Marken, die in Kanada und/oder anderen Ländern verwendet oder registriert werden. Die Erwähnung oder das Erscheinen dieser Marken auf unserer Website oder in unseren Produkten oder Dienstleistungen impliziert keine Zugehörigkeit zu oder Genehmigung durch ihre jeweiligen Eigentümer, es sei denn, dies wird ausdrücklich erklärt.',
      `Wir respektieren geistige Eigentumsrechte und erwarten von unseren Nutzern und Partnern, dass sie dies ebenfalls tun.
    Die unbefugte Verwendung einer auf unserer Plattform oder in Verbindung mit unseren Produkten oder Dienstleistungen angezeigten Marke ist nicht gestattet. Wenn du Fragen oder Bedenken zur Verwendung von Marken oder zu dieser Markenrichtlinie hast, kontaktiere bitte:`,
    ],
    tryAgain: 'Erneut versuchen',
    uploadCV: 'Lebenslauf hochladen',
    viewDetails: 'Details ansehen',
    viewMore: 'Mehr anzeigen',
    welcome: 'Willkommen bei Inception',
    linkInput: {
      smartExtract: 'Intelligente Extraktion:',
      apply: 'Anwenden',
      pasteHelper: 'Vollständige URL für automatische Benutzernamen-Extraktion einfügen',
      placeholderDefault: '---',
    },
    privacyPolicy: 'Datenschutzrichtlinie',
    affiliateDisclosure: 'Als Amazon-Partner und Affiliate-Partner verdient Inception an qualifizierten Käufen. Dies beeinflusst unsere Rankings nicht. Unsere Produktsuche und Marktanalyse sind getrennt vom Verkaufsteil.',
    articlesIntro: 'Inception-Artikel bieten mit KI erstellte Kaufratgeber und Produktanalysen, die Ihnen helfen, fundierte Kaufentscheidungen zu treffen.',
    fallbackNotice:
      'Die spezifische Seite, die Sie suchen, wurde nicht gefunden, aber wir zeigen Ihnen stattdessen verwandte Inhalte.',
    aboutInceptionAi:
      `Inception findet das beste Produkt für dich {region}, mit KI, die dir verpflichtet ist, nicht den Werbetreibenden.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'in Kanada',
      [Region.USA]: 'in den USA',
      [Region.France]: 'in Frankreich',
      [Region.UK]: 'im Vereinigten Königreich',
      [Region.Australia]: 'in Australien',
      [Region.All]: 'in Ihrer Region',
    },
  },
  it: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Prezzo Amazon · {time}',
    aboutTitle: "L'essenziale:",
    aboutUs: 'Chi siamo',
    vision: 'La nostra visione',
    aboutUsTitle: "Cos'è Inception?",
    ageVerification: {
      title: 'Verifica età richiesta',
      subtitle: 'Avviso contenuto per adulti',
      warning: 'Questo sito contiene contenuti per adulti ed è destinato solo a persone di 18 anni o più.',
      disclaimer: 'Accedendo a questo sito, confermate di avere l\'età legale nella vostra giurisdizione e di voler visualizzare contenuti per adulti.',
      confirmAge: 'Confermo di avere 18 anni o più',
      enterButton: 'Entra nel sito',
      exitButton: 'Esci',
      mustBe18: 'Devi avere 18 anni o più per accedere a questo contenuto',
      adultContentWarning: 'Questo sito contiene contenuti espliciti per adulti destinati solo a un pubblico maturo.',
      euCompliance: 'Questa verifica è conforme al Digital Services Act dell\'UE e ai requisiti nazionali di verifica dell\'età.',
    },
    alternativeLabel: `*migliore alternativa che abbiamo trovato`,
    at: 'a',
    averagePriceTag: 'Media: ',
    best: 'Migliore PLACEHOLDER',
    bestFeatures: 'Cosa rende un prodotto il migliore nella sua categoria?',
    articles: 'Articoli',
    backToHome: 'Home',
    breadcrumbHome: 'Home',
    buyActionDescription: 'Confronta dove acquistare {product} su Inception: i rivenditori affidabili che monitoriamo in questo mercato, il miglior prezzo che vi abbiamo osservato e l\'opzione che consigliamo. I prezzi vengono verificati regolarmente e possono cambiare, e il rivenditore conferma il prezzo finale al momento del pagamento.',
    goBack: 'Indietro',
    by: 'di',
    cancel: 'Annulla',
    methodology: 'Come scegliamo',
    partners: 'Partner',
    careers: 'Carriere',
    changeRegion: 'Cambia regione',
    shoppingRegionLabel: 'Regione di acquisto: {region}',
    choosePrice: 'Nel frattempo, puoi scegliere una fascia di prezzo adatta a te.',
    comingSoon: 'Prossimamente',
    conclusion: 'Conclusione',
    cookieConsent: {
      title: 'Rispettiamo la tua privacy',
      description: 'Utilizziamo cookie per migliorare la tua esperienza e fornire migliori raccomandazioni di prodotti. I tuoi dati rimangono privati e anonimi.',
      essential: 'Cookie essenziali',
      analytics: 'Cookie analitici',
      advertising: 'Cookie pubblicitari',
      learnMore: 'Scopri di più',
      acceptAll: 'Accetta e continua',
      rejectAll: 'Rifiuta',
      customizeSettings: 'Personalizza',
      savePreferences: 'Salva preferenze',
      essentialDescription: 'Necessari per il corretto funzionamento del sito. Non possono essere disabilitati.',
      analyticsDescription: 'Ci aiutano a capire come i visitatori interagiscono con il nostro sito raccogliendo dati anonimi.',
      advertisingDescription: 'Utilizzati per visualizzare annunci pertinenti tramite Google AdSense. Si caricano solo con il tuo consenso.',
      californiaNotice: 'Residenti in California: non vendiamo le vostre informazioni personali. Rifiutare i cookie analitici vi esclude da tutta la raccolta di dati non essenziali.',
    },
    contactUs: 'Contattaci',
    contactFormDescription: 'Compila il modulo sottostante.',
    defaultMetadata: {
      title: `Inception | Il prodotto migliore per te, trovato da un'IA onesta`,
      description: `Il prodotto migliore per te esiste già. La nostra IA lo trova, perché rispondiamo a te, non agli inserzionisti. Inception ti offre raccomandazioni di prodotti ricercate dall'IA per la tua regione, senza posizionamenti sponsorizzati.`,
    },
    descriptionArticle: 'La nostra missione è trasformare il modo in cui le aziende migliorano la loro presenza online.',
    describeDemoCategory: 'Qui definiremo le caratteristiche e qualità che rendono un prodotto un leader in questa categoria.',
    describeDemoComparison: 'Qui definiremo le caratteristiche che rendono un prodotto un leader per creare un elenco semplice.',
    directlySearch: 'Cerca direttamente',
    disconnect: 'Disconnetti',
    emailPlaceholder: 'Inserisci la tua email',
    emailTitle: 'Email',
    example: 'Esempio',
    phoneTitle: 'Numero di telefono',
    exploreMore: 'Esplora di più',
    existingPages: 'Visita le pagine esistenti che potrebbero aiutare',
    articlesOtherRegions: 'Dalle nostre altre regioni',
    firstPostComingSoon: `Il tuo primo post arriverà presto, ti terremo aggiornato!\nNel frattempo, puoi modificare il contenuto qui sotto.`,
    joinUs: 'Unisciti a noi.',
    contactForm: {
      personalInformation: 'Informazioni personali',
      yourMessage: 'Il tuo messaggio',
      firstName: 'Nome',
      firstNamePlaceholder: 'Inserisci il tuo nome',
      emailAddress: 'Indirizzo email',
      emailPlaceholder: 'Inserisci il tuo indirizzo email',
      phoneNumber: 'Numero di telefono',
      phonePlaceholder: 'Inserisci il tuo numero di telefono',
      company: 'Azienda/Organizzazione',
      companyPlaceholder: 'Inserisci il nome della tua azienda',
      message: 'Raccontaci del tuo progetto o richiesta',
      messagePlaceholder: 'Condividi i dettagli del tuo progetto, i requisiti o le domande...',
      sendMessage: 'Invia messaggio',
      sending: 'Invio in corso...',
      successTitle: 'Messaggio inviato con successo!',
      successMessage: 'Grazie per il tuo messaggio! Ti risponderemo presto.',
      processingMessage: 'Elaborazione della tua richiesta...',
      errorTitle: 'Errore nell\'invio del messaggio',
      errorMessage: 'Si è verificato un errore nell\'invio del messaggio. Riprova.',
    },
    loading: 'Caricamento...',
    localeDisclaimer: 'Solo X',
    login: 'Accedi',
    lookingFor: 'Cosa stai cercando?',
    profile: 'Profilo',
    otherLanguages: 'Altre lingue',
    showLess: 'Mostra meno',
    showMoreLanguages: 'Mostra più lingue',
    messageTitle: 'Messaggio',
    mission: `Il prodotto migliore per te esiste già. La nostra IA lo trova, perché rispondiamo a te, non agli inserzionisti.`,
    namePlaceholder: 'Inserisci il tuo nome',
    nameTitle: 'Nome',
    newProduct: 'APPENA AGGIUNTO',
    newSearch: 'Nuova ricerca: ',
    nextStep: 'Passo successivo',
    noArticles: 'Al momento non ci sono articoli',
    noResultsFound: 'PS: posso fornire suggerimenti solo su prodotti o categorie commercializzabili.',
    noSearchResult: 'Continua a descrivere il prodotto che stai cercando, poi premi Invio.',
    officialWebsite: 'sito ufficiale',
    orderNow: 'Ordina ora',
    regulatedNotice: {
      prescriptionOnly: 'Solo su prescrizione',
      professionalUse: 'Uso professionale',
      fromVeterinarian: 'Disponibile presso il tuo veterinario',
      fromPharmacist: 'Disponibile in farmacia',
      noOnlinePurchase: 'Non venduto online',
      professionalSupply: 'Venduto tramite fornitori professionali',
      whyNoLink: "Recensiamo questo prodotto ma non forniamo un link d'acquisto.",
    },
    getTheBook: 'Ottieni il libro',
    listenOnAudible: 'Ascolta su Audible',
    browseOnAmazon: 'Sfoglia su Amazon',
    unavailableInCountry: 'Attualmente non disponibile in {country}',
    searchSimilarOnAmazon: 'Cerca prodotti simili su Amazon',
    searchNow: 'Cerca ora',
    ourMission: 'La nostra missione',
    overviewTitle: 'Dettagli',
    askAQuestion: 'Hai una domanda?',
    askSentHint: 'Scrivila in alto',
    micBlocked: 'Il microfono è bloccato. Consentilo nelle impostazioni del browser.',
    pageReference: 'In questa pagina',
    buyNow: 'Acquista ora',
    publishedOn: 'Pubblicato il',
    publishingOn: 'Pubblicazione il',
    quickMenu: 'Riepilogo scelte principali',
    recentBlogs: 'Notizie recenti - PLACEHOLDER',
    regionPrompt: {
      switchQuestion: {
        [RegionCode.Italy]: "Vuoi passare all'Italia?",
        [RegionCode.Switzerland]: 'Vuoi passare alla Svizzera?',
      },
      yesSwitch: 'Sì, cambia regione',
      noThanks: 'No, grazie',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'Scopri di più',
    requirements: 'Requisiti',
    responsibilities: 'Responsabilità',
    reviewSummary: 'Riepilogo recensioni',
    xReviews: '{count} recensioni',
    review: 'Recensione',
    searching: 'Ricerca di ',
    searchingProduct: 'Ricerca delle migliori alternative per ',
    sidemenuListTitle: 'Le nostre migliori scelte',
    slogan: `Rispondiamo a te, non agli inserzionisti.`,
    submit: 'Invia',
    technicalSpecs: 'Specifiche tecniche',
    nutritionFacts: 'Valori nutrizionali',
    ingredients: 'Ingredienti',
    materialFabric: 'Materiale e tessuto',
    dimensionsSize: 'Dimensioni e taglia',
    performanceMetrics: 'Metriche di prestazione',
    safetyFeatures: 'Caratteristiche di sicurezza',
    compatibility: 'Compatibilità',
    careInstructions: 'Istruzioni per la cura',
    warrantySupport: 'Garanzia e supporto',
    timeUiStrings: {
      noTimeAvailable: 'Nessun orario disponibile',
      inTheFuture: 'Nel futuro',
      justNow: 'proprio ora',
      oneMinuteAgo: '1 minuto fa',
      xMinutesAgo: '{x} minuti fa',
      over30Minutes: 'più di 30 minuti fa',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Notizie di Inception',
    trademarkPolicy: 'Politica dei marchi',
    trademarkPolicyContent: [
      `'Inception' è un marchio registrato di InceptionAI Inc.`,
      'Altri marchi che appaiono su questo sito sono di proprietà dei rispettivi titolari.',
      'Per domande sui marchi, contattare:',
    ],
    tryAgain: 'Riprova',
    uploadCV: 'Carica CV',
    viewDetails: 'Visualizza dettagli',
    viewMore: 'Mostra di più',
    welcome: 'Benvenuto in Inception',
    linkInput: {
      smartExtract: 'Estrazione intelligente:',
      apply: 'Applica',
      pasteHelper: 'Incolla URL completo per l\'estrazione automatica del nome utente',
      placeholderDefault: '---',
    },
    privacyPolicy: 'Informativa sulla privacy',
    affiliateDisclosure: 'Come associato Amazon e partner affiliato, Inception guadagna dagli acquisti idonei. Questo non influenza le nostre classifiche.',
    articlesIntro: 'Gli articoli di Inception offrono guide all\'acquisto e analisi di prodotti realizzate con l\'IA.',
    fallbackNotice: 'La pagina specifica che stai cercando non è stata trovata, ma ti mostriamo contenuti correlati.',
    aboutInceptionAi: `Inception trova il prodotto migliore per te {region}, con un'IA che risponde a te, non agli inserzionisti.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'in Canada',
      [Region.USA]: 'negli USA',
      [Region.France]: 'in Francia',
      [Region.UK]: 'nel Regno Unito',
      [Region.Australia]: 'in Australia',
      [Region.Brazil]: 'in Brasile',
      [Region.Portugal]: 'in Portogallo',
      [Region.All]: 'nella tua regione',
    },
  },
  'pt-BR': {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Preço na Amazon · {time}',
    aboutTitle: 'O essencial:',
    aboutUs: 'Sobre nós',
    vision: 'Nossa visão',
    aboutUsTitle: 'O que é o Inception?',
    ageVerification: {
      title: 'Verificação de idade necessária',
      subtitle: 'Aviso de conteúdo adulto',
      warning: 'Este site contém conteúdo adulto e é destinado apenas a pessoas com 18 anos ou mais.',
      disclaimer: 'Ao entrar neste site, você confirma que tem a idade legal em sua jurisdição e que deseja visualizar conteúdo adulto.',
      confirmAge: 'Confirmo que tenho 18 anos ou mais',
      enterButton: 'Entrar no site',
      exitButton: 'Sair',
      mustBe18: 'Você deve ter 18 anos ou mais para acessar este conteúdo',
      adultContentWarning: 'Este site contém conteúdo adulto explícito destinado apenas a públicos maduros.',
      euCompliance: 'Esta verificação está em conformidade com o Digital Services Act da UE e os requisitos nacionais de verificação de idade.',
    },
    alternativeLabel: `*melhor alternativa que encontramos`,
    at: 'em',
    averagePriceTag: 'Média: ',
    best: 'Melhor PLACEHOLDER',
    bestFeatures: 'O que torna um produto o melhor na sua categoria?',
    articles: 'Artigos',
    backToHome: 'Início',
    breadcrumbHome: 'Início',
    buyActionDescription: 'Compare onde comprar {product} na Inception: as lojas confiáveis que acompanhamos neste mercado, o melhor preço que observamos em cada uma e a opção que recomendamos. Os preços são verificados com regularidade e podem mudar, e a loja confirma o preço final na finalização da compra.',
    goBack: 'Voltar',
    by: 'por',
    cancel: 'Cancelar',
    methodology: 'Como escolhemos',
    partners: 'Parceiros',
    careers: 'Carreiras',
    changeRegion: 'Mudar região',
    shoppingRegionLabel: 'Região de compra: {region}',
    choosePrice: 'Enquanto isso, você pode escolher uma faixa de preço que lhe convém.',
    comingSoon: 'Em breve',
    conclusion: 'Conclusão',
    cookieConsent: {
      title: 'Valorizamos sua privacidade',
      description: 'Usamos cookies para melhorar sua experiência e fornecer melhores recomendações de produtos. Seus dados permanecem privados e anônimos.',
      essential: 'Cookies essenciais',
      analytics: 'Cookies analíticos',
      advertising: 'Cookies publicitários',
      learnMore: 'Saiba mais',
      acceptAll: 'Aceitar e continuar',
      rejectAll: 'Recusar',
      customizeSettings: 'Personalizar',
      savePreferences: 'Salvar preferências',
      essentialDescription: 'Necessários para o funcionamento adequado do site. Não podem ser desativados.',
      analyticsDescription: 'Nos ajudam a entender como os visitantes interagem com nosso site coletando dados anônimos.',
      advertisingDescription: 'Usados para exibir anúncios relevantes via Google AdSense. Carregam apenas com seu consentimento.',
      californiaNotice: 'Residentes da Califórnia: não vendemos suas informações pessoais. Recusar cookies analíticos exclui você de toda coleta de dados não essenciais.',
    },
    contactUs: 'Entre em contato',
    contactFormDescription: 'Por favor, preencha o formulário abaixo.',
    defaultMetadata: {
      title: `Inception | O melhor produto para você, encontrado por uma IA honesta`,
      description: `O melhor produto para você já existe. Nossa IA o encontra, porque respondemos a você, não aos anunciantes. A Inception oferece recomendações de produtos pesquisadas por IA para a sua região, sem posicionamentos patrocinados.`,
    },
    descriptionArticle: 'Nossa missão é transformar a forma como as empresas melhoram sua presença online.',
    describeDemoCategory: 'Aqui definiremos as características e qualidades que tornam um produto um líder nesta categoria.',
    describeDemoComparison: 'Aqui definiremos as características que tornam um produto um líder para criar uma lista simples.',
    directlySearch: 'Pesquisar diretamente',
    disconnect: 'Desconectar',
    emailPlaceholder: 'Insira seu email',
    emailTitle: 'Email',
    example: 'Exemplo',
    phoneTitle: 'Número de telefone',
    exploreMore: 'Explorar mais',
    existingPages: 'Visite as páginas existentes que podem ajudar',
    articlesOtherRegions: 'De nossas outras regiões',
    firstPostComingSoon: `Sua primeira publicação chegará em breve, manteremos você informado!\nEnquanto isso, você pode editar o conteúdo abaixo.`,
    joinUs: 'Junte-se a nós.',
    contactForm: {
      personalInformation: 'Informações pessoais',
      yourMessage: 'Sua mensagem',
      firstName: 'Nome',
      firstNamePlaceholder: 'Insira seu primeiro nome',
      emailAddress: 'Endereço de email',
      emailPlaceholder: 'Insira seu endereço de email',
      phoneNumber: 'Número de telefone',
      phonePlaceholder: 'Insira seu número de telefone',
      company: 'Empresa/Organização',
      companyPlaceholder: 'Insira o nome da sua empresa',
      message: 'Conte-nos sobre seu projeto ou consulta',
      messagePlaceholder: 'Compartilhe os detalhes do seu projeto, requisitos ou perguntas...',
      sendMessage: 'Enviar mensagem',
      sending: 'Enviando...',
      successTitle: 'Mensagem enviada com sucesso!',
      successMessage: 'Obrigado pela sua mensagem! Entraremos em contato em breve.',
      processingMessage: 'Processando sua solicitação...',
      errorTitle: 'Erro ao enviar mensagem',
      errorMessage: 'Ocorreu um erro ao enviar sua mensagem. Por favor, tente novamente.',
    },
    loading: 'Carregando...',
    localeDisclaimer: 'Apenas X',
    login: 'Entrar',
    lookingFor: 'O que você está procurando?',
    profile: 'Perfil',
    otherLanguages: 'Outros idiomas',
    showLess: 'Mostrar menos',
    showMoreLanguages: 'Mostrar mais idiomas',
    messageTitle: 'Mensagem',
    mission: `O melhor produto para você já existe. Nossa IA o encontra, porque respondemos a você, não aos anunciantes.`,
    namePlaceholder: 'Insira seu nome',
    nameTitle: 'Nome',
    newProduct: 'RECÉM ADICIONADO',
    newSearch: 'Nova pesquisa: ',
    nextStep: 'Próximo passo',
    noArticles: 'Atualmente não há artigos',
    noResultsFound: 'PS: só posso fornecer sugestões sobre produtos ou categorias comercializáveis.',
    noSearchResult: 'Continue descrevendo o produto que está procurando e pressione Enter.',
    officialWebsite: 'site oficial',
    orderNow: 'Peça agora',
    regulatedNotice: {
      prescriptionOnly: 'Somente com receita',
      professionalUse: 'Uso profissional',
      fromVeterinarian: 'Disponível com seu veterinário',
      fromPharmacist: 'Disponível em farmácia',
      noOnlinePurchase: 'Não vendido online',
      professionalSupply: 'Vendido por fornecedores profissionais',
      whyNoLink: 'Avaliamos este produto, mas não fornecemos link de compra.',
    },
    browseOnAmazon: 'Navegar na Amazon',
    unavailableInCountry: 'Indisponível no momento em {country}',
    searchSimilarOnAmazon: 'Buscar similares na Amazon',
    searchNow: 'Pesquisar agora',
    ourMission: 'Nossa missão',
    overviewTitle: 'Detalhes',
    askAQuestion: 'Tem uma pergunta?',
    askSentHint: 'Digite lá em cima',
    micBlocked: 'O microfone está bloqueado. Permita o acesso nas configurações do navegador.',
    pageReference: 'Nesta página',
    buyNow: 'Compre agora',
    publishedOn: 'Publicado em',
    publishingOn: 'Publicação em',
    quickMenu: 'Resumo das principais escolhas',
    recentBlogs: 'Notícias recentes - PLACEHOLDER',
    regionPrompt: {
      switchQuestion: {},
      yesSwitch: 'Sim, mudar região',
      noThanks: 'Não, obrigado',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'Descubra mais',
    requirements: 'Requisitos',
    responsibilities: 'Responsabilidades',
    reviewSummary: 'Resumo das avaliações',
    xReviews: '{count} avaliações',
    review: 'Avaliação',
    searching: 'Pesquisando ',
    searchingProduct: 'Encontrando as melhores alternativas para ',
    sidemenuListTitle: 'Nossas melhores escolhas',
    slogan: `Respondemos a você, não aos anunciantes.`,
    submit: 'Enviar',
    technicalSpecs: 'Especificações técnicas',
    nutritionFacts: 'Informações nutricionais',
    ingredients: 'Ingredientes',
    materialFabric: 'Material e tecido',
    dimensionsSize: 'Dimensões e tamanho',
    performanceMetrics: 'Métricas de desempenho',
    safetyFeatures: 'Recursos de segurança',
    compatibility: 'Compatibilidade',
    careInstructions: 'Instruções de cuidado',
    warrantySupport: 'Garantia e suporte',
    timeUiStrings: {
      noTimeAvailable: 'Nenhum horário disponível',
      inTheFuture: 'No futuro',
      justNow: 'agora mesmo',
      oneMinuteAgo: '1 minuto atrás',
      xMinutesAgo: '{x} minutos atrás',
      over30Minutes: 'mais de 30 minutos atrás',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Notícias da Inception',
    trademarkPolicy: 'Política de marcas',
    trademarkPolicyContent: [
      `'Inception' é uma marca registrada da InceptionAI Inc.`,
      'Outras marcas que aparecem neste site são propriedade de seus respectivos donos.',
      'Para dúvidas sobre marcas, entre em contato com:',
    ],
    tryAgain: 'Tentar novamente',
    uploadCV: 'Carregar CV',
    viewDetails: 'Ver detalhes',
    viewMore: 'Mostrar mais',
    welcome: 'Bem-vindo à Inception',
    linkInput: {
      smartExtract: 'Extração inteligente:',
      apply: 'Aplicar',
      pasteHelper: 'Cole o URL completo para extração automática do nome de usuário',
      placeholderDefault: '---',
    },
    privacyPolicy: 'Política de privacidade',
    affiliateDisclosure: 'Como associado da Amazon e parceiro afiliado, a Inception ganha com compras qualificadas. Isso não influencia nossos rankings.',
    articlesIntro: 'Os artigos da Inception oferecem guias de compra e análises de produtos elaborados com IA.',
    fallbackNotice: 'A página específica que você está procurando não foi encontrada, mas estamos mostrando conteúdo relacionado.',
    aboutInceptionAi: `A Inception encontra o melhor produto para você {region}, com IA que responde a você, não aos anunciantes.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'no Canadá',
      [Region.USA]: 'nos EUA',
      [Region.France]: 'na França',
      [Region.UK]: 'no Reino Unido',
      [Region.Australia]: 'na Austrália',
      [Region.Brazil]: 'no Brasil',
      [Region.Portugal]: 'em Portugal',
      [Region.All]: 'na sua região',
    },
  },
  'pt-PT': {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Preço na Amazon · {time}',
    aboutTitle: 'O essencial:',
    aboutUs: 'Sobre nós',
    vision: 'A nossa visão',
    aboutUsTitle: 'O que é o Inception?',
    ageVerification: {
      title: 'Verificação de idade necessária',
      subtitle: 'Aviso de conteúdo para adultos',
      warning: 'Este sítio contém conteúdo para adultos e destina-se apenas a pessoas com 18 anos ou mais.',
      disclaimer: 'Ao aceder a este sítio, confirma que tem a idade legal na sua jurisdição e que deseja ver conteúdo para adultos.',
      confirmAge: 'Confirmo que tenho 18 anos ou mais',
      enterButton: 'Entrar no sítio',
      exitButton: 'Sair',
      mustBe18: 'Deve ter 18 anos ou mais para aceder a este conteúdo',
      adultContentWarning: 'Este sítio contém conteúdo explícito para adultos destinado apenas a públicos maduros.',
      euCompliance: 'Esta verificação está em conformidade com o Digital Services Act da UE e os requisitos nacionais de verificação de idade.',
    },
    alternativeLabel: `*melhor alternativa que encontrámos`,
    at: 'em',
    averagePriceTag: 'Média: ',
    best: 'Melhor PLACEHOLDER',
    bestFeatures: 'O que torna um produto o melhor na sua categoria?',
    articles: 'Artigos',
    backToHome: 'Início',
    breadcrumbHome: 'Início',
    buyActionDescription: 'Compare onde comprar {product} na Inception: as lojas de confiança que acompanhamos neste mercado, o melhor preço que observámos em cada uma e a opção que recomendamos. Os preços são verificados regularmente e podem mudar, e a loja confirma o preço final no momento do pagamento.',
    goBack: 'Voltar',
    by: 'por',
    cancel: 'Cancelar',
    methodology: 'Como escolhemos',
    partners: 'Parceiros',
    careers: 'Carreiras',
    changeRegion: 'Mudar região',
    shoppingRegionLabel: 'Região de compra: {region}',
    choosePrice: 'Enquanto isso, pode escolher uma gama de preços que lhe convenha.',
    comingSoon: 'Em breve',
    conclusion: 'Conclusão',
    cookieConsent: {
      title: 'Valorizamos a sua privacidade',
      description: 'Utilizamos cookies para melhorar a sua experiência e fornecer melhores recomendações de produtos. Os seus dados permanecem privados e anónimos.',
      essential: 'Cookies essenciais',
      analytics: 'Cookies analíticos',
      advertising: 'Cookies publicitários',
      learnMore: 'Saiba mais',
      acceptAll: 'Aceitar e continuar',
      rejectAll: 'Recusar',
      customizeSettings: 'Personalizar',
      savePreferences: 'Guardar preferências',
      essentialDescription: 'Necessários para o funcionamento adequado do sítio. Não podem ser desativados.',
      analyticsDescription: 'Ajudam-nos a compreender como os visitantes interagem com o nosso sítio recolhendo dados anónimos.',
      advertisingDescription: 'Utilizados para apresentar anúncios relevantes via Google AdSense. Carregam apenas com o seu consentimento.',
      californiaNotice: 'Residentes na Califórnia: não vendemos as suas informações pessoais. Recusar cookies analíticos exclui-o de toda a recolha de dados não essenciais.',
    },
    contactUs: 'Contacte-nos',
    contactFormDescription: 'Por favor, preencha o formulário abaixo.',
    defaultMetadata: {
      title: `Inception | O melhor produto para si, encontrado por uma IA honesta`,
      description: `O melhor produto para si já existe. A nossa IA encontra-o, porque respondemos a si, não aos anunciantes. A Inception oferece-lhe recomendações de produtos pesquisadas por IA para a sua região, sem posicionamentos patrocinados.`,
    },
    descriptionArticle: 'A nossa missão é transformar a forma como as empresas melhoram a sua presença online.',
    describeDemoCategory: 'Aqui definiremos as características e qualidades que tornam um produto um líder nesta categoria.',
    describeDemoComparison: 'Aqui definiremos as características que tornam um produto um líder para criar uma lista simples.',
    directlySearch: 'Pesquisar diretamente',
    disconnect: 'Desligar',
    emailPlaceholder: 'Introduza o seu email',
    emailTitle: 'Email',
    example: 'Exemplo',
    phoneTitle: 'Número de telefone',
    exploreMore: 'Explorar mais',
    existingPages: 'Visite as páginas existentes que podem ajudar',
    articlesOtherRegions: 'Das nossas outras regiões',
    firstPostComingSoon: `A sua primeira publicação chegará em breve, manteremos informado!\nEnquanto isso, pode editar o conteúdo abaixo.`,
    joinUs: 'Junte-se a nós.',
    contactForm: {
      personalInformation: 'Informações pessoais',
      yourMessage: 'A sua mensagem',
      firstName: 'Nome próprio',
      firstNamePlaceholder: 'Introduza o seu nome próprio',
      emailAddress: 'Endereço de email',
      emailPlaceholder: 'Introduza o seu endereço de email',
      phoneNumber: 'Número de telefone',
      phonePlaceholder: 'Introduza o seu número de telefone',
      company: 'Empresa/Organização',
      companyPlaceholder: 'Introduza o nome da sua empresa',
      message: 'Conte-nos sobre o seu projeto ou consulta',
      messagePlaceholder: 'Partilhe os detalhes do seu projeto, requisitos ou perguntas...',
      sendMessage: 'Enviar mensagem',
      sending: 'A enviar...',
      successTitle: 'Mensagem enviada com sucesso!',
      successMessage: 'Obrigado pela sua mensagem! Entraremos em contacto em breve.',
      processingMessage: 'A processar o seu pedido...',
      errorTitle: 'Erro ao enviar mensagem',
      errorMessage: 'Ocorreu um erro ao enviar a sua mensagem. Por favor, tente novamente.',
    },
    loading: 'A carregar...',
    localeDisclaimer: 'Apenas X',
    login: 'Entrar',
    lookingFor: 'O que está à procura?',
    profile: 'Perfil',
    otherLanguages: 'Outros idiomas',
    showLess: 'Mostrar menos',
    showMoreLanguages: 'Mostrar mais idiomas',
    messageTitle: 'Mensagem',
    mission: `O melhor produto para si já existe. A nossa IA encontra-o, porque respondemos a si, não aos anunciantes.`,
    namePlaceholder: 'Introduza o seu nome',
    nameTitle: 'Nome',
    newProduct: 'RECÉM ADICIONADO',
    newSearch: 'Nova pesquisa: ',
    nextStep: 'Passo seguinte',
    noArticles: 'Atualmente não há artigos',
    noResultsFound: 'PS: só posso fornecer sugestões sobre produtos ou categorias comercializáveis.',
    noSearchResult: 'Continue a descrever o produto que está à procura e prima Enter.',
    officialWebsite: 'sítio oficial',
    orderNow: 'Encomendar agora',
    regulatedNotice: {
      prescriptionOnly: 'Apenas com receita',
      professionalUse: 'Uso profissional',
      fromVeterinarian: 'Disponível junto do seu veterinário',
      fromPharmacist: 'Disponível em farmácia',
      noOnlinePurchase: 'Não vendido online',
      professionalSupply: 'Vendido por fornecedores profissionais',
      whyNoLink: 'Avaliamos este produto, mas não disponibilizamos ligação de compra.',
    },
    browseOnAmazon: 'Navegar na Amazon',
    unavailableInCountry: 'Indisponível de momento em {country}',
    searchSimilarOnAmazon: 'Pesquisar semelhantes na Amazon',
    searchNow: 'Pesquisar agora',
    ourMission: 'A nossa missão',
    overviewTitle: 'Detalhes',
    askAQuestion: 'Tem uma pergunta?',
    askSentHint: 'Escreva lá em cima',
    micBlocked: 'O microfone está bloqueado. Permita o acesso nas definições do navegador.',
    pageReference: 'Nesta página',
    buyNow: 'Comprar agora',
    publishedOn: 'Publicado em',
    publishingOn: 'Publicação em',
    quickMenu: 'Resumo das principais escolhas',
    recentBlogs: 'Notícias recentes - PLACEHOLDER',
    regionPrompt: {
      switchQuestion: {},
      yesSwitch: 'Sim, mudar região',
      noThanks: 'Não, obrigado',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'Descobrir mais',
    requirements: 'Requisitos',
    responsibilities: 'Responsabilidades',
    reviewSummary: 'Resumo das avaliações',
    xReviews: '{count} avaliações',
    review: 'Avaliação',
    searching: 'A pesquisar ',
    searchingProduct: 'A encontrar as melhores alternativas para ',
    sidemenuListTitle: 'As nossas melhores escolhas',
    slogan: `Respondemos a si, não aos anunciantes.`,
    submit: 'Enviar',
    technicalSpecs: 'Especificações técnicas',
    nutritionFacts: 'Informação nutricional',
    ingredients: 'Ingredientes',
    materialFabric: 'Material e tecido',
    dimensionsSize: 'Dimensões e tamanho',
    performanceMetrics: 'Métricas de desempenho',
    safetyFeatures: 'Características de segurança',
    compatibility: 'Compatibilidade',
    careInstructions: 'Instruções de cuidado',
    warrantySupport: 'Garantia e suporte',
    timeUiStrings: {
      noTimeAvailable: 'Sem hora disponível',
      inTheFuture: 'No futuro',
      justNow: 'agora mesmo',
      oneMinuteAgo: '1 minuto atrás',
      xMinutesAgo: '{x} minutos atrás',
      over30Minutes: 'mais de 30 minutos atrás',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Notícias da Inception',
    trademarkPolicy: 'Política de marcas',
    trademarkPolicyContent: [
      `'Inception' é uma marca registada da InceptionAI Inc.`,
      'Outras marcas que aparecem neste sítio são propriedade dos respetivos titulares.',
      'Para questões sobre marcas, contacte:',
    ],
    tryAgain: 'Tentar novamente',
    uploadCV: 'Carregar CV',
    viewDetails: 'Ver detalhes',
    viewMore: 'Mostrar mais',
    welcome: 'Bem-vindo à Inception',
    linkInput: {
      smartExtract: 'Extração inteligente:',
      apply: 'Aplicar',
      pasteHelper: 'Cole o URL completo para extração automática do nome de utilizador',
      placeholderDefault: '---',
    },
    privacyPolicy: 'Política de privacidade',
    affiliateDisclosure: 'Como associado da Amazon e parceiro afiliado, a Inception ganha com compras elegíveis. Isto não influencia as nossas classificações.',
    articlesIntro: 'Os artigos da Inception oferecem guias de compra e análises de produtos elaborados com IA.',
    fallbackNotice: 'A página específica que está à procura não foi encontrada, mas estamos a mostrar conteúdo relacionado.',
    aboutInceptionAi: `A Inception encontra o melhor produto para si {region}, com IA que responde a si, não aos anunciantes.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'no Canadá',
      [Region.USA]: 'nos EUA',
      [Region.France]: 'na França',
      [Region.UK]: 'no Reino Unido',
      [Region.Australia]: 'na Austrália',
      [Region.Brazil]: 'no Brasil',
      [Region.Portugal]: 'em Portugal',
      [Region.All]: 'na sua região',
    },
  },
  pl: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Cena na Amazonie · {time}',
    aboutTitle: 'Najważniejsze:',
    aboutUs: 'O nas',
    vision: 'Nasza wizja',
    aboutUsTitle: 'Czym jest Inception?',
    ageVerification: {
      title: 'Wymagana weryfikacja wieku',
      subtitle: 'Ostrzeżenie o treściach dla dorosłych',
      warning: 'Ta strona zawiera treści dla dorosłych i jest przeznaczona wyłącznie dla osób w wieku 18 lat lub starszych.',
      disclaimer: 'Wchodząc na tę stronę, potwierdzasz, że jesteś pełnoletni w swojej jurysdykcji i chcesz oglądać treści dla dorosłych.',
      confirmAge: 'Potwierdzam, że mam 18 lat lub więcej',
      enterButton: 'Wejdź na stronę',
      exitButton: 'Wyjdź',
      mustBe18: 'Musisz mieć co najmniej 18 lat, aby uzyskać dostęp do tych treści',
      adultContentWarning: 'Ta strona zawiera wyraźne treści dla dorosłych przeznaczone wyłącznie dla dojrzałych odbiorców.',
      euCompliance: 'Ta weryfikacja jest zgodna z Aktem o Usługach Cyfrowych UE i krajowymi wymogami weryfikacji wieku.',
    },
    alternativeLabel: `*najlepsza znaleziona alternatywa`,
    at: 'w',
    averagePriceTag: 'Średnia: ',
    best: 'Najlepszy PLACEHOLDER',
    bestFeatures: 'Co sprawia, że produkt jest najlepszy w swojej kategorii?',
    articles: 'Artykuły',
    backToHome: 'Strona główna',
    breadcrumbHome: 'Strona główna',
    buyActionDescription: 'Porównaj w Inception, gdzie kupić {product}: zaufani sprzedawcy, których śledzimy na tym rynku, najlepsza zaobserwowana u nich cena i opcja, którą polecamy. Ceny są regularnie sprawdzane i mogą się zmieniać, a sprzedawca potwierdza ostateczną cenę przy składaniu zamówienia.',
    goBack: 'Wstecz',
    by: 'przez',
    cancel: 'Anuluj',
    methodology: 'Jak wybieramy',
    partners: 'Partnerzy',
    careers: 'Kariera',
    changeRegion: 'Zmień region',
    shoppingRegionLabel: 'Region zakupów: {region}',
    choosePrice: 'W międzyczasie możesz wybrać odpowiedni dla siebie zakres cenowy.',
    comingSoon: 'Wkrótce',
    conclusion: 'Podsumowanie',
    cookieConsent: {
      title: 'Szanujemy Twoją prywatność',
      description: 'Używamy plików cookie, aby poprawić wrażenia i zapewnić lepsze rekomendacje produktów. Twoje dane pozostają prywatne i anonimowe.',
      essential: 'Niezbędne pliki cookie',
      analytics: 'Analityczne pliki cookie',
      advertising: 'Reklamowe pliki cookie',
      learnMore: 'Dowiedz się więcej',
      acceptAll: 'Zaakceptuj i kontynuuj',
      rejectAll: 'Odrzuć',
      customizeSettings: 'Dostosuj',
      savePreferences: 'Zapisz preferencje',
      essentialDescription: 'Wymagane do prawidłowego działania strony. Nie można ich wyłączyć.',
      analyticsDescription: 'Pomagają nam zrozumieć, jak odwiedzający korzystają z naszej strony, zbierając anonimowe dane.',
      advertisingDescription: 'Używane do wyświetlania trafnych reklam przez Google AdSense. Ładowane tylko za Twoją zgodą.',
      californiaNotice: 'Mieszkańcy Kalifornii: nie sprzedajemy danych osobowych. Odrzucenie analitycznych plików cookie wyłącza zbieranie nieistotnych danych.',
    },
    contactUs: 'Kontakt',
    contactFormDescription: 'Prosimy wypełnić poniższy formularz.',
    defaultMetadata: {
      title: `Inception | Najlepszy produkt dla Ciebie, znaleziony przez uczciwą AI`,
      description: `Najlepszy produkt dla Ciebie już istnieje. Nasza AI go znajduje, bo odpowiadamy przed Tobą, a nie przed reklamodawcami. Inception zapewnia Ci rekomendacje produktów opracowane przez AI dla Twojego regionu, bez sponsorowanych pozycji.`,
    },
    descriptionArticle: 'Naszą misją jest transformacja sposobu, w jaki firmy wzmacniają swoją obecność online.',
    describeDemoCategory: 'Tutaj zdefiniujemy cechy i właściwości, które czynią produkt liderem w tej kategorii.',
    describeDemoComparison: 'Tutaj zdefiniujemy cechy, które czynią produkt liderem, aby utworzyć prostą listę najlepszych produktów.',
    directlySearch: 'Szukaj bezpośrednio',
    disconnect: 'Rozłącz',
    emailPlaceholder: 'Wpisz swój e-mail',
    emailTitle: 'E-mail',
    example: 'Przykład',
    phoneTitle: 'Numer telefonu',
    exploreMore: 'Odkrywaj więcej',
    existingPages: 'Odwiedź istniejące strony, które mogą pomóc',
    articlesOtherRegions: 'Z naszych innych regionów',
    firstPostComingSoon: `Twój pierwszy wpis pojawi się wkrótce, będziemy informować!\nW międzyczasie możesz edytować poniższe treści.`,
    joinUs: 'Dołącz do nas.',
    contactForm: {
      personalInformation: 'Dane osobowe',
      yourMessage: 'Twoja wiadomość',
      firstName: 'Imię',
      firstNamePlaceholder: 'Wpisz swoje imię',
      emailAddress: 'Adres e-mail',
      emailPlaceholder: 'Wpisz swój adres e-mail',
      phoneNumber: 'Numer telefonu',
      phonePlaceholder: 'Wpisz swój numer telefonu',
      company: 'Firma/Organizacja',
      companyPlaceholder: 'Wpisz nazwę firmy',
      message: 'Opowiedz nam o swoim projekcie lub zapytaniu',
      messagePlaceholder: 'Podziel się szczegółami projektu, wymaganiami lub pytaniami...',
      sendMessage: 'Wyślij wiadomość',
      sending: 'Wysyłanie...',
      successTitle: 'Wiadomość wysłana pomyślnie!',
      successMessage: 'Dziękujemy za wiadomość! Wkrótce się odezwiemy.',
      processingMessage: 'Przetwarzanie zapytania...',
      errorTitle: 'Błąd podczas wysyłania wiadomości',
      errorMessage: 'Wystąpił błąd podczas wysyłania wiadomości. Prosimy spróbować ponownie.',
    },
    loading: 'Ładowanie...',
    localeDisclaimer: 'Tylko X',
    login: 'Logowanie',
    lookingFor: 'Czego szukasz?',
    profile: 'Profil',
    otherLanguages: 'Inne języki',
    showLess: 'Pokaż mniej',
    showMoreLanguages: 'Pokaż więcej języków',
    messageTitle: 'Wiadomość',
    mission: `Najlepszy produkt dla Ciebie już istnieje. Nasza AI go znajduje, bo odpowiadamy przed Tobą, a nie przed reklamodawcami.`,
    namePlaceholder: 'Wpisz swoje imię',
    nameTitle: 'Imię i nazwisko',
    newProduct: 'NOWO DODANY',
    newSearch: 'Nowe wyszukiwanie: ',
    nextStep: 'Następny krok',
    noArticles: 'Obecnie nie ma artykułów',
    noResultsFound: 'PS: Mogę podawać sugestie tylko dotyczące produktów lub kategorii rynkowych.',
    noSearchResult: 'Kontynuuj opisywanie szukanego produktu, a następnie naciśnij Enter.',
    officialWebsite: 'oficjalna strona',
    orderNow: 'Zamów teraz',
    regulatedNotice: {
      prescriptionOnly: 'Wyłącznie na receptę',
      professionalUse: 'Do użytku profesjonalnego',
      fromVeterinarian: 'Dostępne u lekarza weterynarii',
      fromPharmacist: 'Dostępne w aptece',
      noOnlinePurchase: 'Nie jest sprzedawane online',
      professionalSupply: 'Sprzedawane przez dostawców profesjonalnych',
      whyNoLink: 'Recenzujemy ten produkt, ale nie podajemy linku do zakupu.',
    },
    browseOnAmazon: 'Przeglądaj na Amazon',
    unavailableInCountry: 'Obecnie niedostępne ({country})',
    searchSimilarOnAmazon: 'Szukaj podobnych na Amazon',
    searchNow: 'Szukaj',
    ourMission: 'Nasza misja',
    overviewTitle: 'Szczegóły',
    askAQuestion: 'Masz pytanie?',
    askSentHint: 'Wpisz u góry',
    micBlocked: 'Mikrofon jest zablokowany. Zezwól na dostęp w ustawieniach przeglądarki.',
    pageReference: 'Na tej stronie',
    buyNow: 'Kup teraz',
    publishedOn: 'Opublikowano',
    publishingOn: 'Publikacja',
    quickMenu: 'Podsumowanie najlepszych wyborów',
    recentBlogs: 'Aktualności – PLACEHOLDER',
    regionPrompt: {
      switchQuestion: {},
      yesSwitch: 'Tak, zmień region',
      noThanks: 'Nie, dziękuję',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'Odkryj więcej',
    requirements: 'Wymagania',
    responsibilities: 'Zakres obowiązków',
    reviewSummary: 'Podsumowanie recenzji',
    xReviews: '{count} opinii',
    review: 'Recenzja',
    searching: 'Szukanie ',
    searchingProduct: 'Znajdowanie najlepszych alternatyw dla ',
    sidemenuListTitle: 'Nasze najlepsze wybory',
    slogan: `Odpowiadamy przed Tobą, nie przed reklamodawcami.`,
    submit: 'Wyślij',
    technicalSpecs: 'Specyfikacja techniczna',
    nutritionFacts: 'Wartości odżywcze',
    ingredients: 'Składniki',
    materialFabric: 'Materiał i tkanina',
    dimensionsSize: 'Wymiary i rozmiar',
    performanceMetrics: 'Wskaźniki wydajności',
    safetyFeatures: 'Funkcje bezpieczeństwa',
    compatibility: 'Kompatybilność',
    careInstructions: 'Instrukcje pielęgnacji',
    warrantySupport: 'Gwarancja i wsparcie',
    timeUiStrings: {
      noTimeAvailable: 'Brak dostępnego czasu',
      inTheFuture: 'W przyszłości',
      justNow: 'właśnie teraz',
      oneMinuteAgo: '1 minutę temu',
      xMinutesAgo: '{x} minut temu',
      over30Minutes: 'ponad 30 minut temu',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Aktualności Inception',
    trademarkPolicy: 'Polityka znaków towarowych',
    trademarkPolicyContent: [
      `'Inception' jest zarejestrowanym znakiem towarowym InceptionAI Inc.`,
      'Inne znaki towarowe pojawiające się na tej stronie są własnością ich odpowiednich właścicieli.',
      'W sprawie pytań dotyczących znaków towarowych prosimy o kontakt:',
    ],
    tryAgain: 'Spróbuj ponownie',
    uploadCV: 'Prześlij CV',
    viewDetails: 'Zobacz szczegóły',
    viewMore: 'Pokaż więcej',
    welcome: 'Witamy w Inception',
    linkInput: {
      smartExtract: 'Inteligentna ekstrakcja:',
      apply: 'Zastosuj',
      pasteHelper: 'Wklej pełny URL w celu automatycznego pobrania nazwy użytkownika',
      placeholderDefault: '---',
    },
    privacyPolicy: 'Polityka prywatności',
    affiliateDisclosure: 'Jako partner Amazon i partner afiliacyjny, Inception zarabia na kwalifikujących się zakupach. Nie wpływa to na nasze rankingi.',
    articlesIntro: 'Artykuły Inception oferują przewodniki zakupowe i analizy produktów przygotowane z pomocą AI.',
    fallbackNotice: 'Szukana strona nie została znaleziona, ale wyświetlamy powiązane treści.',
    aboutInceptionAi: `Inception znajduje najlepszy produkt dla Ciebie {region}, z AI, która odpowiada przed Tobą, a nie przed reklamodawcami.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'w Kanadzie',
      [Region.USA]: 'w USA',
      [Region.France]: 'we Francji',
      [Region.UK]: 'w Wielkiej Brytanii',
      [Region.Australia]: 'w Australii',
      [Region.All]: 'w Twoim regionie',
    },
  },
  nl: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Amazon-prijs · {time}',
    aboutTitle: 'Het essentiële:',
    aboutUs: 'Over ons',
    vision: 'Onze visie',
    aboutUsTitle: 'Wat is Inception?',
    ageVerification: {
      title: 'Leeftijdsverificatie vereist',
      subtitle: 'Waarschuwing voor inhoud voor volwassenen',
      warning: 'Deze website bevat inhoud voor volwassenen en is uitsluitend bedoeld voor personen van 18 jaar of ouder.',
      disclaimer:
        'Door deze site te betreden, bevestigt u dat u meerderjarig bent in uw rechtsgebied en dat u inhoud voor volwassenen wenst te bekijken.',
      confirmAge: 'Ik bevestig dat ik 18 jaar of ouder ben',
      enterButton: 'Site betreden',
      exitButton: 'Verlaten',
      mustBe18: 'U moet 18 jaar of ouder zijn om toegang te krijgen tot deze inhoud',
      adultContentWarning: 'Deze site bevat expliciete inhoud voor volwassenen, uitsluitend bedoeld voor een volwassen publiek.',
      euCompliance: 'Deze verificatie voldoet aan de EU Digital Services Act en nationale vereisten voor leeftijdsverificatie.',
    },
    alternativeLabel: `*beste alternatief dat wij hebben gevonden`,
    at: 'bij',
    averagePriceTag: 'Gemiddeld: ',
    best: 'Beste PLACEHOLDER',
    bestFeatures: 'Wat maakt een product het beste in zijn categorie?',
    articles: 'Artikelen',
    backToHome: 'Startpagina',
    breadcrumbHome: 'Home',
    buyActionDescription: 'Vergelijk op Inception waar u {product} kunt kopen: de betrouwbare winkels die we in deze markt volgen, de beste prijs die we daar hebben waargenomen en de optie die we aanbevelen. Prijzen worden regelmatig gecontroleerd en kunnen veranderen, en de winkel bevestigt de definitieve prijs bij het afrekenen.',
    goBack: 'Terug',
    by: 'door',
    cancel: 'Annuleren',
    methodology: 'Hoe we kiezen',
    partners: 'Partners',
    careers: 'Vacatures',
    changeRegion: 'Regio wijzigen',
    shoppingRegionLabel: 'Winkelregio: {region}',
    choosePrice: 'In de tussentijd kunt u een prijsklasse kiezen die bij u past.',
    comingSoon: 'Binnenkort beschikbaar',
    conclusion: 'Conclusie',
    cookieConsent: {
      title: 'Wij respecteren uw privacy',
      description: 'Wij gebruiken cookies om uw ervaring te verbeteren en betere productaanbevelingen te bieden. Uw gegevens blijven privé en anoniem.',
      essential: 'Essentiële cookies',
      analytics: 'Analytische cookies',
      advertising: 'Advertentiecookies',
      learnMore: 'Meer informatie',
      acceptAll: 'Accepteren en doorgaan',
      rejectAll: 'Weigeren',
      customizeSettings: 'Aanpassen',
      savePreferences: 'Voorkeuren opslaan',
      essentialDescription: 'Vereist voor het goed functioneren van de website. Kunnen niet worden uitgeschakeld.',
      analyticsDescription: 'Helpen ons te begrijpen hoe bezoekers met onze website omgaan door anonieme gegevens te verzamelen.',
      advertisingDescription: 'Gebruikt om relevante advertenties weer te geven via Google AdSense. Worden alleen geladen met uw toestemming.',
      californiaNotice: 'Inwoners van Californië: wij verkopen uw persoonlijke gegevens niet. Het weigeren van analytische cookies sluit u uit van alle niet-essentiële gegevensverzameling.',
    },
    contactUs: 'Neem contact op',
    contactFormDescription: 'Vul het onderstaande formulier in.',
    defaultMetadata: {
      title: `Inception | Het beste product voor jou, gevonden door eerlijke AI`,
      description:
        `Het beste product voor jou bestaat al. Onze AI vindt het, want wij zijn verantwoording schuldig aan jou, niet aan adverteerders. Inception geeft u door AI onderzochte productaanbevelingen voor uw regio, zonder gesponsorde plaatsingen.`,
    },
    descriptionArticle:
      'Onze missie is om de manier te transformeren waarop bedrijven hun online aanwezigheid verbeteren door hen in staat te stellen GRATIS kwalitatieve inhoud aan hun klanten te bieden.',
    describeDemoCategory:
      'Hier definiëren wij de kenmerken en kwaliteiten die een product tot een topper of leider in deze categorie maken. Dit is essentieel om een lijst van topproducten in een specifieke categorie te genereren, met uw gewenste product op de eerste plaats. De rest van de lijst bevat vergelijkbare producten, rekening houdend met zowel kwaliteit als prijsklasse, met een focus op Canadese bedrijven.',
    describeDemoComparison:
      'Hier definiëren wij de kenmerken en kwaliteiten die een product tot een topper of leider in deze categorie maken om een eenvoudige lijst van de beste productnamen te creëren op basis van uw zoekcriteria. Deze lijst sluit aanvullende details uit en geeft voorrang aan lokale bedrijven in uw regio voor ons selectieproces.',
    directlySearch: 'Direct zoeken',
    disconnect: 'Afmelden',
    emailPlaceholder: 'Voer uw e-mailadres in',
    emailTitle: 'E-mail',
    example: 'Voorbeeld',
    phoneTitle: 'Telefoonnummer',
    exploreMore: 'Meer ontdekken',
    existingPages: 'Bezoek bestaande pagina\'s die u kunnen helpen',
    articlesOtherRegions: 'Uit onze andere regio\'s',
    firstPostComingSoon: `Uw eerste publicatie verschijnt binnenkort, wij houden u op de hoogte!  
In de tussentijd kunt u de onderstaande inhoud bewerken als u dat wenst.`,
    joinUs: 'Word lid van ons team.',
    contactForm: {
      personalInformation: 'Persoonlijke gegevens',
      yourMessage: 'Uw bericht',
      firstName: 'Voornaam',
      firstNamePlaceholder: 'Voer uw voornaam in',
      emailAddress: 'E-mailadres',
      emailPlaceholder: 'Voer uw e-mailadres in',
      phoneNumber: 'Telefoonnummer',
      phonePlaceholder: 'Voer uw telefoonnummer in',
      company: 'Bedrijf/Organisatie',
      companyPlaceholder: 'Voer uw bedrijfsnaam in',
      message: 'Vertel ons over uw project of vraag',
      messagePlaceholder: 'Deel uw projectdetails, vereisten of vragen...',
      sendMessage: 'Bericht verzenden',
      sending: 'Verzenden...',
      successTitle: 'Bericht succesvol verzonden!',
      successMessage: 'Bedankt voor uw bericht! Wij nemen spoedig contact met u op.',
      processingMessage: 'Uw verzoek wordt verwerkt...',
      errorTitle: 'Fout bij het verzenden van het bericht',
      errorMessage: 'Er is een fout opgetreden bij het verzenden van uw bericht. Probeer het opnieuw.',
    },
    loading: 'Laden...',
    localeDisclaimer: 'Alleen X',
    login: 'Inloggen',
    lookingFor: 'Bent u op zoek naar?',
    profile: 'Profiel',
    otherLanguages: 'Andere talen',
    showLess: 'Minder weergeven',
    showMoreLanguages: 'Meer talen weergeven',
    messageTitle: 'Bericht',
    mission: `Het beste product voor jou bestaat al. Onze AI vindt het, want wij zijn verantwoording schuldig aan jou, niet aan adverteerders.`,
    namePlaceholder: 'Voer uw naam in',
    nameTitle: 'Naam',
    newProduct: 'NIEUW TOEGEVOEGD',
    newSearch: 'Nieuwe zoekopdracht: ',
    nextStep: 'Volgende stap',
    noArticles: 'Er zijn momenteel geen artikelen',
    noResultsFound: 'P.S. Ik kan alleen suggesties doen voor verhandelbare producten of categorieën.',
    noSearchResult: 'Blijf het product dat u zoekt beschrijven en druk vervolgens op Enter.',
    officialWebsite: 'officiële website',
    orderNow: 'Nu bestellen',
    regulatedNotice: {
      prescriptionOnly: 'Alleen op recept',
      professionalUse: 'Professioneel gebruik',
      fromVeterinarian: 'Verkrijgbaar bij uw dierenarts',
      fromPharmacist: 'Verkrijgbaar bij de apotheek',
      noOnlinePurchase: 'Niet online verkocht',
      professionalSupply: 'Verkocht via professionele leveranciers',
      whyNoLink: 'Wij beoordelen dit product maar linken er niet naar.',
    },
    browseOnAmazon: 'Bekijken op Amazon',
    unavailableInCountry: 'Momenteel niet beschikbaar in {country}',
    searchSimilarOnAmazon: 'Zoek vergelijkbare op Amazon',
    searchNow: 'Nu zoeken',
    ourMission: 'Onze missie',
    overviewTitle: 'Details',
    askAQuestion: 'Een vraag stellen?',
    askSentHint: 'Typ hem bovenaan',
    micBlocked: 'De microfoon is geblokkeerd. Sta hem toe in de instellingen van je browser.',
    pageReference: 'Op deze pagina',
    buyNow: 'Nu kopen',
    publishedOn: 'Gepubliceerd op',
    publishingOn: 'Publicatie op',
    quickMenu: 'Samenvatting topkeuzes',
    recentBlogs: 'Nieuws over PLACEHOLDER',
    regionPrompt: {
      switchQuestion: {
        [RegionCode.Belgium]: 'Wilt u overschakelen naar België?',
        [RegionCode.Netherlands]: 'Wilt u overschakelen naar Nederland?',
      },
      yesSwitch: 'Ja, regio wijzigen',
      noThanks: 'Nee, bedankt',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'Ontdek meer',
    requirements: 'Vereisten',
    responsibilities: 'Verantwoordelijkheden',
    reviewSummary: 'Samenvatting beoordelingen',
    xReviews: '{count} beoordelingen',
    review: 'Beoordeling',
    searching: 'Zoeken naar ',
    searchingProduct: 'De beste alternatieven zoeken voor ',
    sidemenuListTitle: 'Onze beste keuzes',
    slogan: `Wij zijn verantwoording schuldig aan jou, niet aan adverteerders.`,
    submit: 'Verzenden',
    technicalSpecs: 'Technische specificaties',
    nutritionFacts: 'Voedingswaarden',
    ingredients: 'Ingrediënten',
    materialFabric: 'Materiaal en stof',
    dimensionsSize: 'Afmetingen en maat',
    performanceMetrics: 'Prestatiemetingen',
    safetyFeatures: 'Veiligheidskenmerken',
    compatibility: 'Compatibiliteit',
    careInstructions: 'Onderhoudsvoorschriften',
    warrantySupport: 'Garantie en ondersteuning',
    timeUiStrings: {
      noTimeAvailable: 'Geen tijd beschikbaar',
      inTheFuture: 'In de toekomst',
      justNow: 'zojuist',
      oneMinuteAgo: '1 minuut geleden',
      xMinutesAgo: '{x} minuten geleden',
      over30Minutes: 'meer dan 30 minuten geleden',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Inception Nieuws',
    trademarkPolicy: 'Merkenbeleid',
    trademarkPolicyContent: [
      `'Inception' is een geregistreerd handelsmerk van InceptionAI Inc., naar behoren geregistreerd en gehandhaafd in Canada en in diverse andere rechtsgebieden. Het gebruik van het handelsmerk 'Inception' of een daarmee verwarringwekkend overeenstemmend handelsmerk zonder de uitdrukkelijke schriftelijke toestemming van InceptionAI Inc. is strikt verboden. Dit beleid strekt zich uit tot alle merksymbolen, logo's en andere onderscheidende merkkenmerken die verband houden met InceptionAI Inc.`,
      'Andere handelsmerken, dienstmerken, logo\'s en handelsnamen die op deze website of in onze producten of diensten verschijnen, zijn eigendom van hun respectieve eigenaren. Dit omvat handelsmerken die in gebruik zijn of geregistreerd zijn in Canada en/of andere landen. De vermelding of verschijning van deze handelsmerken op onze website of in onze producten of diensten impliceert geen banden met of goedkeuring door hun respectieve eigenaren, tenzij uitdrukkelijk vermeld.',
      'Wij respecteren intellectuele-eigendomsrechten en verwachten van onze gebruikers en partners dat zij hetzelfde doen. Het ongeautoriseerde gebruik van een handelsmerk dat op ons platform wordt weergegeven of in verband staat met onze producten of diensten is niet toegestaan. Mocht u vragen of opmerkingen hebben over het gebruik van handelsmerken of dit merkenbeleid, neem dan contact op met:',
    ],
    tryAgain: 'Opnieuw proberen',
    uploadCV: 'CV uploaden',
    viewDetails: 'Details bekijken',
    viewMore: 'Meer weergeven',
    welcome: 'Welkom bij Inception',
    linkInput: {
      smartExtract: 'Slimme extractie:',
      apply: 'Toepassen',
      pasteHelper: 'Plak de volledige URL voor automatische gebruikersnaamextractie',
      placeholderDefault: '---',
    },
    privacyPolicy: 'Privacybeleid',
    affiliateDisclosure: 'Als Amazon Associate en affiliate-partner verdient Inception aan in aanmerking komende aankopen. Dit heeft geen invloed op onze ranglijsten. Onze productzoekopdrachten en marktanalyse staan los van het verkoopgedeelte.',
    articlesIntro: 'Inception Artikelen biedt koopgidsen en productanalyses, gemaakt met AI, om u te helpen weloverwogen aankoopbeslissingen te nemen.',
    fallbackNotice:
      'De specifieke pagina die u zoekt is niet gevonden, maar wij tonen u in plaats daarvan gerelateerde inhoud.',
    aboutInceptionAi:
      `Inception vindt het beste product voor jou {region}, met AI die verantwoording aflegt aan jou, niet aan adverteerders.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'in Canada',
      [Region.USA]: 'in de VS',
      [Region.France]: 'in Frankrijk',
      [Region.UK]: 'in het Verenigd Koninkrijk',
      [Region.Australia]: 'in Australië',
      [Region.All]: 'in uw regio',
    },
  },
  // Welsh
  cy: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Pris Amazon · {time}',
    aboutTitle: 'Y Hanfodion:',
    aboutUs: 'Amdanom Ni',
    vision: 'Ein Gweledigaeth',
    aboutUsTitle: 'Beth yw Inception?',
    ageVerification: {
      title: 'Gwirio Oedran yn Ofynnol',
      subtitle: 'Rhybudd Cynnwys i Oedolion',
      warning: 'Mae\'r wefan hon yn cynnwys cynnwys i oedolion ac ar gyfer unigolion 18 oed neu hŷn yn unig.',
      disclaimer:
        'Drwy fynd i mewn i\'r wefan hon, rydych yn cadarnhau eich bod o oedran cyfreithiol yn eich awdurdodaeth a\'ch bod yn dymuno gweld cynnwys i oedolion.',
      confirmAge: 'Rwy\'n cadarnhau fy mod yn 18 oed neu\'n hŷn',
      enterButton: 'Mynd i Mewn i\'r Wefan',
      exitButton: 'Gadael',
      mustBe18: 'Rhaid i chi fod yn 18+ i gael mynediad at y cynnwys hwn',
      adultContentWarning: 'Mae\'r wefan hon yn cynnwys cynnwys penodol i oedolion a fwriadwyd ar gyfer cynulleidfaoedd aeddfed yn unig.',
      euCompliance: 'Mae\'r gwiriad hwn yn cydymffurfio â Deddf Gwasanaethau Digidol yr UE a gofynion gwirio oedran cenedlaethol.',
    },
    alternativeLabel: `*y dewis amgen gorau a ganfuwyd gennym`,
    at: 'yn',
    averagePriceTag: 'Cyfartaledd: ',
    best: 'PLACEHOLDER Gorau',
    bestFeatures: 'Beth sy\'n gwneud cynnyrch y gorau yn ei gategori?',
    articles: 'Erthyglau',
    backToHome: 'Adref',
    breadcrumbHome: 'Hafan',
    goBack: 'Yn ôl',
    by: 'gan',
    cancel: 'Canslo',
    methodology: 'Sut rydym yn dewis',
    partners: 'Partneriaid',
    careers: 'Gyrfaoedd',
    changeRegion: 'Newid Rhanbarth',
    choosePrice: 'Yn y cyfamser, gallwch ddewis ystod bris sy\'n addas i chi.',
    comingSoon: 'Yn Dod yn Fuan',
    conclusion: 'Casgliad',
    cookieConsent: {
      title: 'Rydym yn Gwerthfawrogi Eich Preifatrwydd',
      description: 'Rydym yn defnyddio cwcis i wella eich profiad ac i ddarparu argymhellion gwell ar gyfer cynhyrchion. Mae eich data yn aros yn breifat ac yn ddienw.',
      essential: 'Cwcis Hanfodol',
      analytics: 'Cwcis Dadansoddol',
      advertising: 'Cwcis Hysbysebu',
      learnMore: 'Dysgu mwy',
      acceptAll: 'Derbyn a Pharhau',
      rejectAll: 'Gwrthod',
      customizeSettings: 'Addasu',
      savePreferences: 'Cadw Dewisiadau',
      essentialDescription: 'Yn ofynnol er mwyn i\'r wefan weithio\'n gywir. Ni ellir eu hanalluogi.',
      analyticsDescription: 'Yn ein helpu i ddeall sut mae ymwelwyr yn rhyngweithio â\'n gwefan drwy gasglu data dienw.',
      advertisingDescription: 'Yn cael eu defnyddio i ddangos hysbysebion perthnasol drwy Google AdSense. Dim ond pan fyddwch yn cydsynio y byddant yn llwytho.',
      californiaNotice: 'Trigolion California: nid ydym yn gwerthu eich gwybodaeth bersonol. Mae gwrthod cwcis dadansoddol yn eich eithrio o bob casgliad data anhanfodol.',
    },
    contactUs: 'Cysylltwch â Ni',
    contactFormDescription: 'Llenwch y ffurflen isod os gwelwch yn dda.',
    defaultMetadata: {
      title: `Inception | Y cynnyrch gorau i chi, wedi'i ddarganfod gan AI gonest`,
      description:
        `Mae'r cynnyrch gorau i chi eisoes yn bodoli. Mae ein AI yn dod o hyd iddo, oherwydd i chi yr ydym yn atebol, nid i hysbysebwyr. Mae Inception yn rhoi argymhellion cynhyrchion gonest i chi ar gyfer eich rhanbarth.`,
    },
    descriptionArticle:
      'Ein cenhadaeth yw trawsnewid y ffordd y mae busnesau\'n gwella eu presenoldeb ar-lein drwy eu galluogi i ddarparu cynnwys o ansawdd AM DDIM i\'w cwsmeriaid.',
    describeDemoCategory:
      'Yma byddwn yn diffinio\'r nodweddion a\'r rhinweddau sy\'n gwneud cynnyrch yn gystadleuydd blaenllaw neu\'n arweinydd yn y categori hwn. Mae hyn yn hanfodol i allbynnu rhestr o\'r cynhyrchion gorau mewn categori penodol, gyda\'ch cynnyrch dewisol ar y brig. Mae gweddill y rhestr yn cynnwys cynhyrchion cymharol, gan ystyried ansawdd ac ystod brisiau, gyda ffocws ar gwmnïau Canadaidd.',
    describeDemoComparison:
      'Yma byddwn yn diffinio\'r nodweddion a\'r rhinweddau sy\'n gwneud cynnyrch yn gystadleuydd blaenllaw neu\'n arweinydd yn y categori hwn i greu rhestr syml o\'r enwau cynhyrchion gorau yn seiliedig ar eich meini prawf chwilio. Mae\'r rhestr hon yn eithrio unrhyw fanylion ychwanegol ac yn blaenoriaethu cwmnïau lleol yn eich rhanbarth ar gyfer ein proses ddethol.',
    directlySearch: 'Chwilio\'n uniongyrchol',
    disconnect: 'Datgysylltu',
    emailPlaceholder: 'Rhowch eich e-bost',
    emailTitle: 'E-bost',
    example: 'Enghraifft',
    phoneTitle: 'Rhif Ffôn',
    exploreMore: 'Archwilio Mwy',
    existingPages: 'Ewch i dudalennau presennol a allai helpu',
    articlesOtherRegions: 'O\'n rhanbarthau eraill',
    firstPostComingSoon: `Mae eich erthygl gyntaf ar y ffordd, byddwn yn rhoi\'r diweddaraf i chi!\nYn y cyfamser, gallwch olygu\'r cynnwys isod os dymunwch.`,
    joinUs: 'Ymunwch â ni.',
    contactForm: {
      personalInformation: 'Gwybodaeth Bersonol',
      yourMessage: 'Eich Neges',
      firstName: 'Enw Cyntaf',
      firstNamePlaceholder: 'Rhowch eich enw cyntaf',
      emailAddress: 'Cyfeiriad E-bost',
      emailPlaceholder: 'Rhowch eich cyfeiriad e-bost',
      phoneNumber: 'Rhif Ffôn',
      phonePlaceholder: 'Rhowch eich rhif ffôn',
      company: 'Cwmni/Sefydliad',
      companyPlaceholder: 'Rhowch enw eich cwmni',
      message: 'Dywedwch wrthym am eich prosiect neu ymholiad',
      messagePlaceholder: 'Rhannwch fanylion eich prosiect, gofynion, neu unrhyw gwestiynau sydd gennych...',
      sendMessage: 'Anfon Neges',
      sending: 'Yn anfon...',
      successTitle: 'Neges wedi\'i Hanfon yn Llwyddiannus!',
      successMessage: 'Diolch am eich neges! Byddwn yn cysylltu â chi\'n fuan.',
      processingMessage: 'Yn prosesu eich cais...',
      errorTitle: 'Gwall wrth Anfon y Neges',
      errorMessage: 'Cafwyd gwall wrth anfon eich neges. Ceisiwch eto os gwelwch yn dda.',
    },
    loading: 'Yn llwytho...',
    localeDisclaimer: 'X yn unig',
    login: 'Mewngofnodi',
    lookingFor: 'Ydych chi\'n chwilio am?',
    profile: 'Proffil',
    otherLanguages: 'Ieithoedd Eraill',
    showLess: 'Dangos Llai',
    showMoreLanguages: 'Dangos Mwy o Ieithoedd',
    messageTitle: 'Neges',
    mission: `Mae'r cynnyrch gorau i chi eisoes yn bodoli. Mae ein AI yn dod o hyd iddo, oherwydd i chi yr ydym yn atebol, nid i hysbysebwyr.`,
    namePlaceholder: 'Rhowch eich enw',
    nameTitle: 'Enw',
    newProduct: 'NEWYDD EI YCHWANEGU',
    newSearch: 'Chwiliad Newydd: ',
    nextStep: 'Cam Nesaf',
    noArticles: 'Nid oes erthyglau ar hyn o bryd',
    noResultsFound: 'Ô.N. Dim ond ar gynhyrchion neu gategorïau marchnadwy y gallaf gynnig awgrymiadau.',
    noSearchResult: 'Daliwch ati i ddisgrifio\'r cynnyrch rydych yn chwilio amdano, yna pwyswch Enter.',
    officialWebsite: 'gwefan swyddogol',
    orderNow: 'Archebwch Nawr',
    browseOnAmazon: 'Pori ar Amazon',
    searchNow: 'Chwilio Nawr',
    ourMission: 'Ein Cenhadaeth',
    overviewTitle: 'Manylion',
    askAQuestion: 'Gofyn cwestiwn?',
    askSentHint: 'Teipiwch hi uchod',
    pageReference: 'Ar y dudalen hon',
    buyNow: 'Prynu Nawr',
    publishedOn: 'Cyhoeddwyd ar',
    publishingOn: 'Yn cael ei gyhoeddi ar',
    quickMenu: 'Crynodeb o\'r Dewisiadau Gorau',
    recentBlogs: 'Newyddion PLACEHOLDER',
    regionPrompt: {
      switchQuestion: {},
      yesSwitch: 'Ie, newid rhanbarth',
      noThanks: 'Dim diolch',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'Darganfod Mwy',
    requirements: 'Gofynion',
    responsibilities: 'Cyfrifoldebau',
    reviewSummary: 'Crynodeb o Adolygiadau',
    xReviews: '{count} adolygiad',
    review: 'Adolygiad',
    searching: 'Yn chwilio am ',
    searchingProduct: 'Yn dod o hyd i\'r dewisiadau amgen gorau ar gyfer ',
    sidemenuListTitle: 'Ein Dewisiadau Gorau',
    slogan: `Rydym yn atebol i chi, nid i hysbysebwyr.`,
    submit: 'Cyflwyno',
    technicalSpecs: 'Manylebau Technegol',
    nutritionFacts: 'Ffeithiau Maethol',
    ingredients: 'Cynhwysion',
    materialFabric: 'Deunydd a Ffabrig',
    dimensionsSize: 'Dimensiynau a Maint',
    performanceMetrics: 'Metrigau Perfformiad',
    safetyFeatures: 'Nodweddion Diogelwch',
    compatibility: 'Cydnawsedd',
    careInstructions: 'Cyfarwyddiadau Gofal',
    warrantySupport: 'Gwarant a Chymorth',
    timeUiStrings: {
      noTimeAvailable: 'Dim amser ar gael',
      inTheFuture: 'Yn y dyfodol',
      justNow: 'newydd ddigwydd',
      oneMinuteAgo: '1 munud yn ôl',
      xMinutesAgo: '{x} munud yn ôl',
      over30Minutes: 'dros 30 munud yn ôl',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Newyddion Inception',
    trademarkPolicy: 'Polisi Nod Masnach',
    trademarkPolicyContent: [
      'Mae \'Inception\' yn nod masnach cofrestredig InceptionAI Inc., wedi\'i gofrestru a\'i orfodi\'n briodol yng Nghanada yn ogystal ag mewn awdurdodaethau amrywiol eraill. Mae defnyddio nod masnach \'Inception\' neu unrhyw nod masnach sy\'n debyg o ddrysu heb ganiatâd ysgrifenedig penodol InceptionAI Inc. wedi\'i wahardd yn llwyr. Mae\'r polisi hwn yn ymestyn i bob symbol nod masnach, logo, ac unrhyw nodweddion brand gwahaniaethol eraill sy\'n gysylltiedig ag InceptionAI Inc.',
      'Mae nodau masnach, marciau gwasanaeth, logos, ac enwau brand eraill sy\'n ymddangos ar y wefan hon neu o fewn ein cynhyrchion neu wasanaethau yn eiddo i\'w perchnogion priodol. Mae\'r rhain yn cynnwys nodau masnach sydd ar waith neu wedi\'u cofrestru yng Nghanada a/neu wledydd eraill. Nid yw crybwyll neu ymddangosiad y nodau masnach hyn ar ein gwefan neu yn ein cynhyrchion neu wasanaethau yn awgrymu unrhyw gysylltiad â\'u perchnogion priodol na chymeradwyaeth ganddynt oni nodir yn benodol.',
      'Rydym yn parchu hawliau eiddo deallusol ac yn disgwyl i\'n defnyddwyr a\'n partneriaid wneud yr un peth. Ni chaniateir defnyddio unrhyw nod masnach a ddangosir ar ein platfform neu mewn cysylltiad â\'n cynhyrchion neu wasanaethau heb awdurdod. Os oes gennych unrhyw gwestiynau neu bryderon ynghylch defnyddio nodau masnach neu\'r polisi nod masnach hwn, cysylltwch â:',
    ],
    tryAgain: 'Ceisiwch eto',
    uploadCV: 'Uwchlwytho CV',
    viewDetails: 'Gweld Manylion',
    viewMore: 'Dangos Mwy',
    welcome: 'Croeso i Inception',
    linkInput: {
      smartExtract: 'Echdynnu deallus:',
      apply: 'Cymhwyso',
      pasteHelper: 'Gludwch yr URL llawn ar gyfer echdynnu enw defnyddiwr yn awtomatig',
      placeholderDefault: '---',
    },
    privacyPolicy: 'Polisi Preifatrwydd',
    affiliateDisclosure: 'Fel Cydymaith Amazon a phartner cysylltiol, mae Inception yn ennill o bryniannau cymwys. Nid yw hyn yn dylanwadu ar ein safleoedd. Mae ein chwiliad cynhyrchion a\'n dadansoddiad marchnad ar wahân i\'r rhan werthu.',
    articlesIntro: 'Mae Erthyglau Inception yn cynnwys canllawiau prynu a dadansoddiadau manwl o gynhyrchion i\'ch helpu i wneud penderfyniadau prynu gwybodus.',
    fallbackNotice: 'Ni ddaethpwyd o hyd i\'r dudalen benodol rydych yn chwilio amdani, ond rydym yn dangos cynnwys cysylltiedig yn ei lle.',
    aboutInceptionAi:
      `Mae Inception yn dod o hyd i'r cynnyrch gorau i chi {region}, gydag AI sy'n atebol i chi, nid i hysbysebwyr.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'yng Nghanada',
      [Region.USA]: 'yn yr Unol Daleithiau',
      [Region.France]: 'yn Ffrainc',
      [Region.UK]: 'yn y Deyrnas Unedig',
      [Region.Australia]: 'yn Awstralia',
      [Region.All]: 'yn eich rhanbarth',
    },
  },
  hi: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Amazon पर कीमत · {time}',
    aboutTitle: 'मुख्य बातें:',
    aboutUs: 'हमारे बारे में',
    vision: 'हमारा विज़न',
    aboutUsTitle: 'Inception क्या है?',
    ageVerification: {
      title: 'आयु सत्यापन आवश्यक',
      subtitle: 'वयस्क सामग्री चेतावनी',
      warning: 'इस वेबसाइट में वयस्क सामग्री है और यह केवल 18 वर्ष या उससे अधिक आयु के व्यक्तियों के लिए है।',
      disclaimer:
        'इस साइट में प्रवेश करके, आप पुष्टि करते हैं कि आप अपने अधिकार क्षेत्र में कानूनी आयु के हैं और आप वयस्क सामग्री देखना चाहते हैं।',
      confirmAge: 'मैं पुष्टि करता/करती हूँ कि मेरी आयु 18 वर्ष या उससे अधिक है',
      enterButton: 'साइट में प्रवेश करें',
      exitButton: 'बाहर निकलें',
      mustBe18: 'इस सामग्री तक पहुँचने के लिए आपकी आयु 18+ होनी चाहिए',
      adultContentWarning: 'इस साइट में केवल वयस्क दर्शकों के लिए स्पष्ट वयस्क सामग्री है।',
      euCompliance: 'यह सत्यापन EU डिजिटल सेवा अधिनियम और राष्ट्रीय आयु सत्यापन आवश्यकताओं का अनुपालन करता है।',
    },
    alternativeLabel: `*हमें मिला सबसे अच्छा विकल्प`,
    at: 'पर',
    averagePriceTag: 'औसत: ',
    best: 'सर्वश्रेष्ठ PLACEHOLDER',
    bestFeatures: 'किसी उत्पाद को उसकी श्रेणी में सर्वश्रेष्ठ क्या बनाता है?',
    articles: 'लेख',
    backToHome: 'होम पर जाएँ',
    breadcrumbHome: 'होम',
    goBack: 'वापस जाएँ',
    by: 'द्वारा',
    cancel: 'रद्द करें',
    methodology: 'हम कैसे चुनते हैं',
    partners: 'साझेदार',
    careers: 'करियर',
    changeRegion: 'क्षेत्र बदलें',
    choosePrice: 'इस बीच, आप अपने अनुसार एक मूल्य सीमा चुन सकते हैं।',
    comingSoon: 'जल्द आ रहा है',
    conclusion: 'निष्कर्ष',
    cookieConsent: {
      title: 'हम आपकी गोपनीयता का सम्मान करते हैं',
      description: 'हम आपके अनुभव को बेहतर बनाने और बेहतर उत्पाद अनुशंसाएँ प्रदान करने के लिए कुकीज़ का उपयोग करते हैं। आपका डेटा निजी और गुमनाम रहता है।',
      essential: 'आवश्यक कुकीज़',
      analytics: 'एनालिटिक्स कुकीज़',
      advertising: 'विज्ञापन कुकीज़',
      learnMore: 'और जानें',
      acceptAll: 'स्वीकार करें और जारी रखें',
      rejectAll: 'अस्वीकार करें',
      customizeSettings: 'अनुकूलित करें',
      savePreferences: 'प्राथमिकताएँ सहेजें',
      essentialDescription: 'वेबसाइट के सही ढंग से काम करने के लिए आवश्यक। अक्षम नहीं किया जा सकता।',
      analyticsDescription: 'गुमनाम डेटा एकत्र करके यह समझने में हमारी मदद करती हैं कि आगंतुक हमारी वेबसाइट का उपयोग कैसे करते हैं।',
      advertisingDescription: 'Google AdSense के माध्यम से प्रासंगिक विज्ञापन दिखाने के लिए उपयोग की जाती हैं। केवल आपकी सहमति से लोड होती हैं।',
      californiaNotice: 'कैलिफ़ोर्निया निवासी: हम आपकी व्यक्तिगत जानकारी नहीं बेचते। एनालिटिक्स कुकीज़ अस्वीकार करने से आप सभी गैर-आवश्यक डेटा संग्रह से बाहर हो जाते हैं।',
    },
    contactUs: 'हमसे संपर्क करें',
    contactFormDescription: 'कृपया नीचे दिया गया फ़ॉर्म भरें।',
    defaultMetadata: {
      title: `Inception | आपके लिए सबसे अच्छा उत्पाद, ईमानदार AI द्वारा खोजा गया`,
      description:
        `आपके लिए सबसे अच्छा उत्पाद पहले से मौजूद है। हमारा AI उसे ढूँढता है, क्योंकि हम आपके प्रति जवाबदेह हैं, विज्ञापनदाताओं के प्रति नहीं। Inception आपको आपके क्षेत्र के लिए ईमानदार उत्पाद अनुशंसाएँ देता है।`,
    },
    descriptionArticle:
      'हमारा मिशन है कि व्यवसायों को अपनी ऑनलाइन उपस्थिति बढ़ाने के तरीके को बदलना, जिससे वे अपने ग्राहकों को मुफ़्त गुणवत्तापूर्ण सामग्री प्रदान कर सकें।',
    describeDemoCategory:
      'यहाँ हम उन विशेषताओं और गुणों को परिभाषित करेंगे जो किसी उत्पाद को इस श्रेणी में शीर्ष दावेदार बनाती हैं। यह एक विशिष्ट श्रेणी में शीर्ष उत्पादों की सूची तैयार करने के लिए आवश्यक है, जिसमें आपका वांछित उत्पाद पहले स्थान पर हो। शेष सूची में गुणवत्ता और मूल्य सीमा दोनों को ध्यान में रखते हुए तुलनीय उत्पाद शामिल हैं, जिसमें कनाडाई कंपनियों पर विशेष ध्यान दिया गया है।',
    describeDemoComparison:
      'यहाँ हम उन विशेषताओं और गुणों को परिभाषित करेंगे जो किसी उत्पाद को इस श्रेणी में शीर्ष दावेदार बनाती हैं ताकि आपके खोज मापदंडों के आधार पर सर्वश्रेष्ठ उत्पाद नामों की एक सीधी सूची तैयार की जा सके। यह सूची अतिरिक्त विवरण को बाहर रखती है और हमारी चयन प्रक्रिया के लिए आपके क्षेत्र की स्थानीय कंपनियों को प्राथमिकता देती है।',
    directlySearch: 'सीधे खोजें',
    disconnect: 'डिस्कनेक्ट करें',
    emailPlaceholder: 'अपना ईमेल दर्ज करें',
    emailTitle: 'ईमेल',
    example: 'उदाहरण',
    phoneTitle: 'फ़ोन नंबर',
    exploreMore: 'और अधिक जानें',
    existingPages: 'मौजूदा पृष्ठ देखें जो सहायक हो सकते हैं',
    articlesOtherRegions: 'हमारे अन्य क्षेत्रों से',
    firstPostComingSoon: `आपकी पहली पोस्ट जल्द ही आ रही है, हम आपको सूचित करते रहेंगे!  
इस बीच, आप चाहें तो नीचे दी गई सामग्री को संपादित कर सकते हैं।`,
    joinUs: 'हमसे जुड़ें।',
    contactForm: {
      personalInformation: 'व्यक्तिगत जानकारी',
      yourMessage: 'आपका संदेश',
      firstName: 'पहला नाम',
      firstNamePlaceholder: 'अपना पहला नाम दर्ज करें',
      emailAddress: 'ईमेल पता',
      emailPlaceholder: 'अपना ईमेल पता दर्ज करें',
      phoneNumber: 'फ़ोन नंबर',
      phonePlaceholder: 'अपना फ़ोन नंबर दर्ज करें',
      company: 'कंपनी/संगठन',
      companyPlaceholder: 'अपनी कंपनी का नाम दर्ज करें',
      message: 'हमें अपनी परियोजना या पूछताछ के बारे में बताएँ',
      messagePlaceholder: 'अपनी परियोजना का विवरण, आवश्यकताएँ, या कोई भी प्रश्न साझा करें...',
      sendMessage: 'संदेश भेजें',
      sending: 'भेजा जा रहा है...',
      successTitle: 'संदेश सफलतापूर्वक भेजा गया!',
      successMessage: 'आपके संदेश के लिए धन्यवाद! हम जल्द ही आपसे संपर्क करेंगे।',
      processingMessage: 'आपका अनुरोध संसाधित किया जा रहा है...',
      errorTitle: 'संदेश भेजने में त्रुटि',
      errorMessage: 'आपका संदेश भेजने में एक त्रुटि हुई। कृपया पुनः प्रयास करें।',
    },
    loading: 'लोड हो रहा है...',
    localeDisclaimer: 'केवल X',
    login: 'लॉगिन',
    lookingFor: 'क्या आप खोज रहे हैं?',
    profile: 'प्रोफ़ाइल',
    otherLanguages: 'अन्य भाषाएँ',
    showLess: 'कम दिखाएँ',
    showMoreLanguages: 'और भाषाएँ दिखाएँ',
    messageTitle: 'संदेश',
    mission: `आपके लिए सबसे अच्छा उत्पाद पहले से मौजूद है। हमारा AI उसे ढूँढता है, क्योंकि हम आपके प्रति जवाबदेह हैं, विज्ञापनदाताओं के प्रति नहीं।`,
    namePlaceholder: 'अपना नाम दर्ज करें',
    nameTitle: 'नाम',
    newProduct: 'नया जोड़ा गया',
    newSearch: 'नई खोज: ',
    nextStep: 'अगला कदम',
    noArticles: 'वर्तमान में कोई लेख नहीं है',
    noResultsFound: 'पी.एस. मैं केवल विपणन योग्य उत्पादों या श्रेणियों पर सुझाव दे सकता हूँ।',
    noSearchResult: 'आप जिस उत्पाद को खोज रहे हैं उसका वर्णन करते रहें, फिर Enter दबाएँ।',
    officialWebsite: 'आधिकारिक वेबसाइट',
    orderNow: 'अभी ऑर्डर करें',
    browseOnAmazon: 'Amazon पर देखें',
    searchNow: 'अभी खोजें',
    ourMission: 'हमारा मिशन',
    overviewTitle: 'विवरण',
    askAQuestion: 'कोई सवाल है?',
    askSentHint: 'ऊपर लिखें',
    pageReference: 'इस पृष्ठ पर',
    buyNow: 'अभी खरीदें',
    publishedOn: 'प्रकाशित',
    publishingOn: 'प्रकाशन तिथि',
    quickMenu: 'शीर्ष चयन सारांश',
    recentBlogs: 'PLACEHOLDER समाचार',
    regionPrompt: {
      switchQuestion: {},
      yesSwitch: 'हाँ, क्षेत्र बदलें',
      noThanks: 'नहीं, धन्यवाद',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'और अधिक खोजें',
    requirements: 'आवश्यकताएँ',
    responsibilities: 'ज़िम्मेदारियाँ',
    reviewSummary: 'समीक्षा सारांश',
    xReviews: '{count} समीक्षाएँ',
    review: 'समीक्षा',
    searching: 'खोज रहे हैं ',
    searchingProduct: 'सर्वश्रेष्ठ विकल्प खोजे जा रहे हैं ',
    sidemenuListTitle: 'हमारे सर्वश्रेष्ठ चयन',
    slogan: `हम आपके प्रति जवाबदेह हैं, विज्ञापनदाताओं के प्रति नहीं।`,
    submit: 'सबमिट करें',
    technicalSpecs: 'तकनीकी विनिर्देश',
    nutritionFacts: 'पोषण संबंधी जानकारी',
    ingredients: 'सामग्री',
    materialFabric: 'सामग्री और कपड़ा',
    dimensionsSize: 'आयाम और आकार',
    performanceMetrics: 'प्रदर्शन मापदंड',
    safetyFeatures: 'सुरक्षा विशेषताएँ',
    compatibility: 'संगतता',
    careInstructions: 'देखभाल निर्देश',
    warrantySupport: 'वारंटी और सहायता',
    timeUiStrings: {
      noTimeAvailable: 'कोई समय उपलब्ध नहीं',
      inTheFuture: 'भविष्य में',
      justNow: 'अभी-अभी',
      oneMinuteAgo: '1 मिनट पहले',
      xMinutesAgo: '{x} मिनट पहले',
      over30Minutes: '30 मिनट से अधिक पहले',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Inception लेख',
    trademarkPolicy: 'ट्रेडमार्क नीति',
    trademarkPolicyContent: [
      "'Inception' InceptionAI Inc. का एक पंजीकृत ट्रेडमार्क है, जो कनाडा तथा अन्य विभिन्न अधिकार क्षेत्रों में विधिवत पंजीकृत और लागू है। InceptionAI Inc. की स्पष्ट लिखित सहमति के बिना 'Inception' ट्रेडमार्क या किसी भी भ्रामक रूप से समान ट्रेडमार्क का उपयोग सख्त वर्जित है। यह नीति InceptionAI Inc. से जुड़े सभी ट्रेडमार्क चिह्नों, लोगो और अन्य विशिष्ट ब्रांड विशेषताओं पर लागू होती है।",
      'इस वेबसाइट पर या हमारे उत्पादों या सेवाओं में दिखाई देने वाले अन्य ट्रेडमार्क, सेवा चिह्न, लोगो और ब्रांड नाम उनके संबंधित स्वामियों की संपत्ति हैं। इनमें वे ट्रेडमार्क शामिल हैं जो कनाडा और/या अन्य देशों में उपयोग में हैं या पंजीकृत हैं। हमारी वेबसाइट पर या हमारे उत्पादों या सेवाओं में इन ट्रेडमार्क का उल्लेख या प्रदर्शन उनके संबंधित स्वामियों के साथ किसी संबद्धता या समर्थन का संकेत नहीं देता, जब तक कि स्पष्ट रूप से न कहा गया हो।',
      'हम बौद्धिक संपदा अधिकारों का सम्मान करते हैं और अपने उपयोगकर्ताओं और भागीदारों से भी ऐसा ही करने की अपेक्षा करते हैं। हमारे प्लेटफ़ॉर्म पर या हमारे उत्पादों या सेवाओं के संबंध में प्रदर्शित किसी भी ट्रेडमार्क का अनधिकृत उपयोग अनुमत नहीं है। यदि ट्रेडमार्क के उपयोग या इस ट्रेडमार्क नीति के बारे में आपके कोई प्रश्न या चिंताएँ हैं, तो कृपया संपर्क करें:',
    ],
    tryAgain: 'पुनः प्रयास करें',
    uploadCV: 'CV अपलोड करें',
    viewDetails: 'विवरण देखें',
    viewMore: 'और दिखाएँ',
    welcome: 'Inception में आपका स्वागत है',
    linkInput: {
      smartExtract: 'स्मार्ट एक्सट्रैक्ट:',
      apply: 'लागू करें',
      pasteHelper: 'स्वचालित उपयोगकर्ता नाम निकालने के लिए पूरा URL पेस्ट करें',
      placeholderDefault: '---',
    },
    privacyPolicy: 'गोपनीयता नीति',
    affiliateDisclosure: 'एक Amazon सहयोगी और सहबद्ध भागीदार के रूप में, Inception योग्य खरीदारी से कमाई करता है। यह हमारी रैंकिंग को प्रभावित नहीं करता। हमारी उत्पाद खोज और बाज़ार विश्लेषण बिक्री भाग से अलग हैं।',
    articlesIntro: 'Inception लेख खरीदारी गाइड और गहन उत्पाद विश्लेषण प्रस्तुत करते हैं ताकि आप सूचित खरीदारी निर्णय ले सकें।',
    fallbackNotice:
      'आप जिस विशिष्ट पृष्ठ की तलाश कर रहे हैं वह नहीं मिला, लेकिन हम आपको इसके बजाय संबंधित सामग्री दिखा रहे हैं।',
    aboutInceptionAi:
      `Inception {region} आपके लिए सबसे अच्छा उत्पाद ढूँढता है, ऐसे AI के साथ जो आपके प्रति जवाबदेह है, विज्ञापनदाताओं के प्रति नहीं।`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'कनाडा में',
      [Region.USA]: 'अमेरिका में',
      [Region.France]: 'फ़्रांस में',
      [Region.UK]: 'यूके में',
      [Region.Australia]: 'ऑस्ट्रेलिया में',
      [Region.India]: 'भारत में',
      [Region.All]: 'आपके क्षेत्र में',
    },
  },
  ta: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Amazon விலை · {time}',
    aboutTitle: 'அத்தியாவசியங்கள்:',
    aboutUs: 'எங்களைப் பற்றி',
    vision: 'எங்கள் தொலைநோக்கு',
    aboutUsTitle: 'Inception என்றால் என்ன?',
    ageVerification: {
      title: 'வயது சரிபார்ப்பு தேவை',
      subtitle: 'வயதுவந்தோர் உள்ளடக்க எச்சரிக்கை',
      warning: 'இந்த இணையதளத்தில் வயதுவந்தோர் உள்ளடக்கம் உள்ளது, 18 வயது அல்லது அதற்கு மேற்பட்டவர்களுக்கு மட்டுமே.',
      disclaimer: 'இந்த தளத்தில் நுழைவதன் மூலம், நீங்கள் உங்கள் நீதிமன்ற எல்லையில் சட்டப்பூர்வ வயதுடையவர் என்பதையும் வயதுவந்தோர் உள்ளடக்கத்தைப் பார்க்க விரும்புவதையும் உறுதிப்படுத்துகிறீர்கள்.',
      confirmAge: 'நான் 18 வயது அல்லது அதற்கு மேற்பட்டவன் என உறுதிப்படுத்துகிறேன்',
      enterButton: 'தளத்தில் நுழையுங்கள்',
      exitButton: 'வெளியேறு',
      mustBe18: 'இந்த உள்ளடக்கத்தை அணுக 18 வயதுக்கு மேற்பட்டவராக இருக்க வேண்டும்',
      adultContentWarning: 'இந்த தளத்தில் முதிர்ந்த பார்வையாளர்களுக்கு மட்டுமான வெளிப்படையான வயதுவந்தோர் உள்ளடக்கம் உள்ளது.',
      euCompliance: 'இந்த சரிபார்ப்பு EU டிஜிட்டல் சேவைகள் சட்டம் மற்றும் தேசிய வயது சரிபார்ப்பு தேவைகளுக்கு இணங்குகிறது.',
    },
    alternativeLabel: `*நாங்கள் கண்டறிந்த சிறந்த மாற்று`,
    at: 'இல்',
    averagePriceTag: 'சராசரி: ',
    best: 'சிறந்த PLACEHOLDER',
    bestFeatures: 'ஒரு பொருளை அதன் வகையில் சிறந்ததாக்குவது எது?',
    articles: 'கட்டுரைகள்',
    backToHome: 'முகப்புக்குச் செல்',
    breadcrumbHome: 'முகப்பு',
    goBack: 'பின்செல்',
    by: 'மூலம்',
    cancel: 'ரத்து செய்',
    methodology: 'நாங்கள் தேர்ந்தெடுக்கும் விதம்',
    partners: 'கூட்டாளர்கள்',
    careers: 'வேலைவாய்ப்புகள்',
    changeRegion: 'பிராந்தியத்தை மாற்று',
    choosePrice: 'இதற்கிடையில், உங்களுக்கு ஏற்ற விலை வரம்பைத் தேர்ந்தெடுக்கலாம்.',
    comingSoon: 'விரைவில் வரவுள்ளது',
    conclusion: 'முடிவுரை',
    cookieConsent: {
      title: 'உங்கள் தனியுரிமையை நாங்கள் மதிக்கிறோம்',
      description: 'உங்கள் அனுபவத்தை மேம்படுத்தவும் சிறந்த பொருள் பரிந்துரைகளை வழங்கவும் குக்கீகளைப் பயன்படுத்துகிறோம்.',
      essential: 'அத்தியாவசிய குக்கீகள்',
      analytics: 'பகுப்பாய்வு குக்கீகள்',
      advertising: 'விளம்பர குக்கீகள்',
      learnMore: 'மேலும் அறிக',
      acceptAll: 'ஏற்றுக்கொண்டு தொடருங்கள்',
      rejectAll: 'மறுக்கவும்',
      customizeSettings: 'தனிப்பயனாக்கு',
      savePreferences: 'விருப்பங்களைச் சேமி',
      essentialDescription: 'இணையதளம் சரியாக செயல்பட தேவையானது. முடக்க இயலாது.',
      analyticsDescription: 'அநாமதேய தரவுகளை சேகரித்து பார்வையாளர்கள் எங்கள் இணையதளத்துடன் எவ்வாறு தொடர்புகொள்கிறார்கள் என்பதைப் புரிந்துகொள்ள உதவுகின்றன.',
      advertisingDescription: 'Google AdSense மூலம் தொடர்புடைய விளம்பரங்களைக் காட்ட பயன்படுத்தப்படுகின்றன. உங்கள் ஒப்புதலுடன் மட்டுமே ஏற்றப்படும்.',
    },
    contactUs: 'எங்களைத் தொடர்புகொள்ளுங்கள்',
    contactFormDescription: 'கீழே உள்ள படிவத்தை நிரப்பவும்.',
    defaultMetadata: {
      title: `Inception | உங்களுக்கான சிறந்த தயாரிப்பு, நேர்மையான AI கண்டறிந்தது`,
      description: `உங்களுக்கான சிறந்த தயாரிப்பு ஏற்கனவே இருக்கிறது. எங்கள் AI அதைக் கண்டுபிடிக்கிறது, ஏனெனில் நாங்கள் உங்களுக்குப் பொறுப்பு, விளம்பரதாரர்களுக்கு அல்ல. Inception உங்கள் பிராந்தியத்திற்கு நேர்மையான பொருள் பரிந்துரைகளை வழங்குகிறது.`,
    },
    descriptionArticle: 'வாடிக்கையாளர்களுக்கு இலவச தரமான உள்ளடக்கத்தை வழங்க உதவுவதன் மூலம் வணிகங்கள் ஆன்லைன் இருப்பை மேம்படுத்தும் விதத்தை மாற்றுவதே எங்கள் நோக்கம்.',
    describeDemoCategory: 'இங்கே ஒரு பொருளை இந்த வகையில் முன்னணி நிலையிலிருப்பதற்கான அம்சங்களையும் தரங்களையும் வரையறுப்போம்.',
    describeDemoComparison: 'இங்கே உங்கள் தேடல் அளவுகோல்களின் அடிப்படையில் சிறந்த பொருள் பெயர்களின் நேரடியான பட்டியலை உருவாக்குவோம்.',
    directlySearch: 'நேரடியாகத் தேடுங்கள்',
    disconnect: 'துண்டிக்கவும்',
    emailPlaceholder: 'உங்கள் மின்னஞ்சலை உள்ளிடுங்கள்',
    emailTitle: 'மின்னஞ்சல்',
    example: 'எடுத்துக்காட்டு',
    phoneTitle: 'தொலைபேசி எண்',
    exploreMore: 'மேலும் ஆராயுங்கள்',
    existingPages: 'உதவக்கூடிய தற்போதைய பக்கங்களைப் பார்வையிடுங்கள்',
    articlesOtherRegions: 'எங்கள் பிற பிராந்தியங்களிலிருந்து',
    firstPostComingSoon: `உங்கள் முதல் இடுகை விரைவில் வரவுள்ளது, உங்களுக்குத் தெரிவிப்போம்!\nஇதற்கிடையில், கீழே உள்ள உள்ளடக்கத்தை நீங்கள் விரும்பினால் திருத்தலாம்.`,
    joinUs: 'எங்களுடன் சேருங்கள்.',
    contactForm: {
      personalInformation: 'தனிப்பட்ட தகவல்',
      yourMessage: 'உங்கள் செய்தி',
      firstName: 'முதல் பெயர்',
      firstNamePlaceholder: 'உங்கள் முதல் பெயரை உள்ளிடுங்கள்',
      emailAddress: 'மின்னஞ்சல் முகவரி',
      emailPlaceholder: 'உங்கள் மின்னஞ்சல் முகவரியை உள்ளிடுங்கள்',
      phoneNumber: 'தொலைபேசி எண்',
      phonePlaceholder: 'உங்கள் தொலைபேசி எண்ணை உள்ளிடுங்கள்',
      company: 'நிறுவனம்/அமைப்பு',
      companyPlaceholder: 'உங்கள் நிறுவனத்தின் பெயரை உள்ளிடுங்கள்',
      message: 'உங்கள் திட்டம் அல்லது விசாரணை பற்றி எங்களிடம் சொல்லுங்கள்',
      messagePlaceholder: 'உங்கள் திட்ட விவரங்கள், தேவைகள் அல்லது கேள்விகளைப் பகிருங்கள்...',
      sendMessage: 'செய்தி அனுப்பு',
      sending: 'அனுப்புகிறது...',
      successTitle: 'செய்தி வெற்றிகரமாக அனுப்பப்பட்டது!',
      successMessage: 'உங்கள் செய்திக்கு நன்றி! விரைவில் பதிலளிப்போம்.',
      processingMessage: 'உங்கள் கோரிக்கை செயலாக்கப்படுகிறது...',
      errorTitle: 'செய்தி அனுப்புவதில் பிழை',
      errorMessage: 'உங்கள் செய்தியை அனுப்புவதில் பிழை ஏற்பட்டது. மீண்டும் முயற்சிக்கவும்.',
    },
    loading: 'ஏற்றுகிறது...',
    localeDisclaimer: 'X மட்டும்',
    login: 'உள்நுழை',
    lookingFor: 'நீங்கள் தேடுகிறீர்களா?',
    profile: 'சுயவிவரம்',
    otherLanguages: 'பிற மொழிகள்',
    showLess: 'குறைவாகக் காட்டு',
    showMoreLanguages: 'மேலும் மொழிகளைக் காட்டு',
    messageTitle: 'செய்தி',
    mission: `உங்களுக்கான சிறந்த தயாரிப்பு ஏற்கனவே இருக்கிறது. எங்கள் AI அதைக் கண்டுபிடிக்கிறது, ஏனெனில் நாங்கள் உங்களுக்குப் பொறுப்பு, விளம்பரதாரர்களுக்கு அல்ல.`,
    namePlaceholder: 'உங்கள் பெயரை உள்ளிடுங்கள்',
    nameTitle: 'பெயர்',
    newProduct: 'புதிதாக சேர்க்கப்பட்டது',
    newSearch: 'புதிய தேடல்: ',
    nextStep: 'அடுத்த படி',
    noArticles: 'தற்போது கட்டுரைகள் எதுவும் இல்லை',
    noResultsFound: 'குறிப்பு: வணிக பொருட்கள் அல்லது வகைகள் குறித்த பரிந்துரைகளை மட்டுமே வழங்க முடியும்.',
    noSearchResult: 'நீங்கள் தேடும் பொருளை விவரித்து, Enter ஐ அழுத்தவும்.',
    officialWebsite: 'அதிகாரப்பூர்வ இணையதளம்',
    orderNow: 'இப்போது ஆர்டர் செய்யுங்கள்',
    browseOnAmazon: 'Amazon-ல் உலாவுங்கள்',
    searchNow: 'இப்போது தேடுங்கள்',
    ourMission: 'எங்கள் நோக்கம்',
    overviewTitle: 'விவரங்கள்',
    askAQuestion: 'கேள்வி கேட்க வேண்டுமா?',
    askSentHint: 'மேலே தட்டச்சு செய்யவும்',
    pageReference: 'இந்தப் பக்கத்தில்',
    buyNow: 'இப்போது வாங்குங்கள்',
    publishedOn: 'வெளியிடப்பட்டது',
    publishingOn: 'வெளியீட்டு தேதி',
    quickMenu: 'சிறந்த தேர்வுகள் சுருக்கம்',
    recentBlogs: 'PLACEHOLDER செய்திகள்',
    regionPrompt: {
      switchQuestion: {},
      yesSwitch: 'ஆம், பிராந்தியத்தை மாற்று',
      noThanks: 'வேண்டாம், நன்றி',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'மேலும் கண்டறியுங்கள்',
    requirements: 'தேவைகள்',
    responsibilities: 'பொறுப்புகள்',
    reviewSummary: 'மதிப்பாய்வு சுருக்கம்',
    xReviews: '{count} மதிப்பாய்வுகள்',
    review: 'மதிப்பாய்வு',
    searching: 'தேடுகிறது ',
    searchingProduct: 'சிறந்த மாற்றுகளைக் கண்டறிகிறது ',
    sidemenuListTitle: 'எங்கள் சிறந்த தேர்வுகள்',
    slogan: `நாங்கள் உங்களுக்குப் பொறுப்பு, விளம்பரதாரர்களுக்கு அல்ல.`,
    submit: 'சமர்ப்பிக்கவும்',
    technicalSpecs: 'தொழில்நுட்ப விவரக்குறிப்புகள்',
    nutritionFacts: 'ஊட்டச்சத்து தகவல்',
    ingredients: 'மூலப்பொருட்கள்',
    materialFabric: 'பொருள் & துணி',
    dimensionsSize: 'பரிமாணங்கள் & அளவு',
    performanceMetrics: 'செயல்திறன் அளவீடுகள்',
    safetyFeatures: 'பாதுகாப்பு அம்சங்கள்',
    compatibility: 'இணக்கத்தன்மை',
    careInstructions: 'பராமரிப்பு வழிமுறைகள்',
    warrantySupport: 'உத்தரவாதம் & ஆதரவு',
    timeUiStrings: {
      noTimeAvailable: 'நேரம் கிடைக்கவில்லை',
      inTheFuture: 'எதிர்காலத்தில்',
      justNow: 'இப்போதுதான்',
      oneMinuteAgo: '1 நிமிடம் முன்',
      xMinutesAgo: '{x} நிமிடங்கள் முன்',
      over30Minutes: '30 நிமிடங்களுக்கு மேல் முன்',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Inception செய்திகள்',
    trademarkPolicy: 'வர்த்தகமுத்திரை கொள்கை',
    trademarkPolicyContent: [
      `'Inception' என்பது InceptionAI Inc.-இன் பதிவு செய்யப்பட்ட வர்த்தகமுத்திரையாகும்.`,
      'இந்த இணையதளத்தில் தோன்றும் பிற வர்த்தகமுத்திரைகள் அவற்றின் உரிமையாளர்களின் சொத்தாகும்.',
      'வர்த்தகமுத்திரைகள் பற்றிய கேள்விகளுக்கு, தொடர்புகொள்ளுங்கள்:',
    ],
    tryAgain: 'மீண்டும் முயற்சிக்கவும்',
    uploadCV: 'CV பதிவேற்றவும்',
    viewDetails: 'விவரங்களைக் காண்',
    viewMore: 'மேலும் காட்டு',
    welcome: 'Inception-க்கு வரவேற்கிறோம்',
    linkInput: {
      smartExtract: 'புத்திசாலி பிரித்தெடுப்பு:',
      apply: 'பயன்படுத்து',
      pasteHelper: 'பயனர்பெயரை தானாகப் பிரித்தெடுக்க முழு URL-ஐ ஒட்டவும்',
      placeholderDefault: '---',
    },
    privacyPolicy: 'தனியுரிமைக் கொள்கை',
    affiliateDisclosure: 'Amazon அசோசியேட் மற்றும் அஃபிலியேட் கூட்டாளியாக, Inception தகுதியான வாங்குதல்களிலிருந்து ஈட்டுகிறது. இது எங்கள் தரவரிசைகளை பாதிக்காது.',
    articlesIntro: 'Inception கட்டுரைகள் வாங்கும் வழிகாட்டிகள் மற்றும் ஆழமான பொருள் பகுப்பாய்வுகளை வழங்குகின்றன.',
    fallbackNotice: 'நீங்கள் தேடும் குறிப்பிட்ட பக்கம் கிடைக்கவில்லை, ஆனால் தொடர்புடைய உள்ளடக்கத்தைக் காட்டுகிறோம்.',
    aboutInceptionAi: `Inception {region} உங்களுக்கான சிறந்த தயாரிப்பைக் கண்டுபிடிக்கிறது, உங்களுக்குப் பொறுப்பான AI உடன், விளம்பரதாரர்களுக்கு அல்ல.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'கனடாவில்',
      [Region.USA]: 'அமெரிக்காவில்',
      [Region.France]: 'பிரான்சில்',
      [Region.UK]: 'இங்கிலாந்தில்',
      [Region.Australia]: 'ஆஸ்திரேலியாவில்',
      [Region.All]: 'உங்கள் பிராந்தியத்தில்',
    },
  },
  // Scottish Gaelic
  gd: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Prìs Amazon · {time}',
    aboutTitle: 'Na Prìomh Rudan:',
    aboutUs: 'Mu ar Deidhinn',
    vision: 'Ar Lèirsinn',
    aboutUsTitle: 'Dè th\' ann an Inception?',
    ageVerification: {
      title: 'Dearbhadh Aoise Riatanach',
      subtitle: 'Rabhadh Susbaint Inbheach',
      warning: 'Tha susbaint inbheach air an làrach-lìn seo agus chan eil e ach airson daoine aois 18 bliadhna no nas sine.',
      disclaimer:
        'Le bhith a\' dol a-steach don làrach seo, tha thu a\' dearbhadh gu bheil thu aig aois laghail nad uachdranas agus gu bheil thu airson susbaint inbheach fhaicinn.',
      confirmAge: 'Tha mi a\' dearbhadh gu bheil mi 18 bliadhna a dh\'aois no nas sine',
      enterButton: 'Rach a-steach don Làrach',
      exitButton: 'Fàg',
      mustBe18: 'Feumaidh tu a bhith 18+ gus an t-susbaint seo fhaighinn',
      adultContentWarning: 'Tha susbaint inbheach soilleir air an làrach seo a tha dìreach airson luchd-amais abaich.',
      euCompliance: 'Tha an dearbhadh seo a\' gèilleadh ri Achd Seirbheisean Didseatach an EU agus riatanasan dearbhaidh aoise nàiseanta.',
    },
    alternativeLabel: `*an roghainn eile as fheàrr a lorg sinn`,
    at: 'aig',
    averagePriceTag: 'Cuibheasach: ',
    best: 'As Fheàrr PLACEHOLDER',
    bestFeatures: 'Dè a tha a\' dèanamh bathar mar am fear as fheàrr na roinn-seòrsa?',
    articles: 'Artaigilean',
    backToHome: 'Dhachaigh',
    breadcrumbHome: 'Dachaigh',
    goBack: 'Air ais',
    by: 'le',
    cancel: 'Sguir dheth',
    methodology: 'Mar a thaghas sinn',
    partners: 'Com-pàirtichean',
    careers: 'Dreuchdan',
    changeRegion: 'Atharraich Sgìre',
    choosePrice: 'San eadar-ama, faodaidh tu raon prìse a thaghadh a fhreagras ort.',
    comingSoon: 'A\' Tighinn a dh\'Aithghearr',
    conclusion: 'Co-dhùnadh',
    cookieConsent: {
      title: 'Tha Sinn a\' Cur Luach air Do Phrìobhaideachd',
      description: 'Bidh sinn a\' cleachdadh briosgaidean gus d\' eòlas a leasachadh agus molaidhean bathair nas fheàrr a thoirt seachad. Bidh do dhàta prìobhaideach agus gun ainm.',
      essential: 'Briosgaidean Riatanach',
      analytics: 'Briosgaidean Anailis',
      advertising: 'Briosgaidean Sanasachd',
      learnMore: 'Ionnsaich barrachd',
      acceptAll: 'Gabh ris ⁊ Lean air adhart',
      rejectAll: 'Diùlt',
      customizeSettings: 'Gnàthaich',
      savePreferences: 'Sàbhail Roghainnean',
      essentialDescription: 'Riatanach airson an làrach-lìn a bhith ag obair gu ceart. Chan urrainnear an cur dheth.',
      analyticsDescription: 'A\' cuideachadh gus tuigsinn mar a bhios luchd-tadhail a\' conaltradh leis an làrach-lìn againn le bhith a\' cruinneachadh dàta gun ainm.',
      advertisingDescription: 'Air an cleachdadh gus sanasan buntainneach a shealltainn tro Google AdSense. Cha tèid an luchdachadh ach le do chead.',
    },
    contactUs: 'Cuir Fios Thugainn',
    contactFormDescription: 'Lìon am foirm gu h-ìosal.',
    defaultMetadata: {
      title: `Inception | Am bathar as fheàrr dhut, air a lorg le AI onarach`,
      description:
        `Tha am bathar as fheàrr dhut ann mar-thà. Lorgaidh an AI againn e, oir tha sinn cunntachail dhut fhèin, chan ann do luchd-sanasachd. Bheir Inception dhut molaidhean bathair onarach airson do sgìre.`,
    },
    descriptionArticle:
      'Is e ar misean an dòigh anns a bheil gnothachasan a\' leasachadh am preusantachd air-loidhne atharrachadh le bhith a\' leigeil leotha susbaint chàileachd AN-ASGAIDH a sholarachadh do an luchd-ceannach.',
    describeDemoCategory:
      'An seo mìnichidh sinn na feartan agus na càileachdan a tha a\' dèanamh bathar mar phrìomh fharpaisiche no ceannard san roinn-seòrsa seo.',
    describeDemoComparison:
      'An seo mìnichidh sinn na feartan a tha a\' dèanamh bathar mar cheannard gus liosta sìmplidh a chruthachadh den na batharan as fheàrr.',
    directlySearch: 'Lorg gu dìreach',
    disconnect: 'Dì-cheangail',
    emailPlaceholder: 'Cuir a-steach do phost-d',
    emailTitle: 'Post-d',
    example: 'Eisimpleir',
    phoneTitle: 'Àireamh Fòn',
    exploreMore: 'Rannsaich Barrachd',
    existingPages: 'Tadhail air duilleagan a dh\'fhaodadh cuideachadh',
    articlesOtherRegions: 'Bho na roinnean eile againn',
    firstPostComingSoon: `Tha do chiad phost a\' tighinn a dh\'aithghearr, cumaidh sinn fios riut!\nSan eadar-ama, faodaidh tu an t-susbaint gu h-ìosal a dheasachadh ma thogras tu.`,
    joinUs: 'Thig còmhla rinn.',
    contactForm: {
      personalInformation: 'Fiosrachadh Pearsanta',
      yourMessage: 'Do Theachdaireachd',
      firstName: 'Ciad Ainm',
      firstNamePlaceholder: 'Cuir a-steach do chiad ainm',
      emailAddress: 'Seòladh Puist-d',
      emailPlaceholder: 'Cuir a-steach do sheòladh puist-d',
      phoneNumber: 'Àireamh Fòn',
      phonePlaceholder: 'Cuir a-steach d\' àireamh fòn',
      company: 'Companaidh/Buidheann',
      companyPlaceholder: 'Cuir a-steach ainm do chompanaidh',
      message: 'Innis dhuinn mu do phròiseact no d\' iarrtais',
      messagePlaceholder: 'Co-roinn mion-fhiosrachadh do phròiseact, riatanasan no ceistean sam bith...',
      sendMessage: 'Cuir Teachdaireachd',
      sending: 'A\' cur...',
      successTitle: 'Chaidh an Teachdaireachd a Chur Gu Soirbheachail!',
      successMessage: 'Tapadh leat airson do theachdaireachd! Cuiridh sinn fios thugad a dh\'aithghearr.',
      processingMessage: 'A\' giullachd d\' iarrtais...',
      errorTitle: 'Mearachd a\' Cur na Teachdaireachd',
      errorMessage: 'Thachair mearachd a\' cur do theachdaireachd. Feuch a-rithist.',
    },
    loading: 'A\' luchdachadh...',
    localeDisclaimer: 'Dìreach X',
    login: 'Clàraich a-steach',
    lookingFor: 'A bheil thu a\' lorg?',
    profile: 'Pròifil',
    otherLanguages: 'Cànanan Eile',
    showLess: 'Seall Nas Lugha',
    showMoreLanguages: 'Seall Barrachd Chànanan',
    messageTitle: 'Teachdaireachd',
    mission: `Tha am bathar as fheàrr dhut ann mar-thà. Lorgaidh an AI againn e, oir tha sinn cunntachail dhut fhèin, chan ann do luchd-sanasachd.`,
    namePlaceholder: 'Cuir a-steach d\' ainm',
    nameTitle: 'Ainm',
    newProduct: 'ÙR AIR A CHUR RIS',
    newSearch: 'Lorg Ùr: ',
    nextStep: 'An Ath Cheum',
    noArticles: 'Chan eil artaigilean ann an-dràsta',
    noResultsFound: 'ps. Chan urrainn dhomh ach molaidhean a thoirt air batharan no roinnean-seòrsa a ghabhas margaidheachd.',
    noSearchResult: 'Cùm a\' dol a\' mìneachadh am bathar a tha thu a\' lorg, an uair sin brùth Enter.',
    officialWebsite: 'làrach-lìn oifigeil',
    orderNow: 'Òrdaich a-nis',
    browseOnAmazon: 'Brabhsaich air Amazon',
    searchNow: 'Lorg a-nis',
    ourMission: 'Ar Misean',
    overviewTitle: 'Mion-fhiosrachadh',
    askAQuestion: 'A bheil ceist agad?',
    askSentHint: 'Sgrìobh shuas e',
    pageReference: 'Air an duilleig seo',
    buyNow: 'Ceannaich a-nis',
    publishedOn: 'Air fhoillseachadh air',
    publishingOn: 'Ri fhoillseachadh air',
    quickMenu: 'Geàrr-chunntas Prìomh Roghainnean',
    recentBlogs: 'Naidheachdan PLACEHOLDER',
    regionPrompt: {
      switchQuestion: {},
      yesSwitch: 'Tha, atharraich sgìre',
      noThanks: 'Chan eil, tapadh leat',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'Lorg Barrachd',
    requirements: 'Riatanasan',
    responsibilities: 'Dleastanasan',
    reviewSummary: 'Geàrr-chunntas Lèirmheasan',
    xReviews: '{count} lèirmheas',
    review: 'Lèirmheas',
    searching: 'A\' lorg airson ',
    searchingProduct: 'A\' lorg nan roghainnean eile as fheàrr airson ',
    sidemenuListTitle: 'Na Roghainnean As Fheàrr Againn',
    slogan: `Tha sinn cunntachail dhut fhèin, chan ann do luchd-sanasachd.`,
    submit: 'Cuir a-steach',
    technicalSpecs: 'Sònrachaidhean Teicnigeach',
    nutritionFacts: 'Fìrinnean Beathachaidh',
    ingredients: 'Tàthchuidean',
    materialFabric: 'Stuth & Aodach',
    dimensionsSize: 'Tomhasan & Meud',
    performanceMetrics: 'Tomhasan Coileanaidh',
    safetyFeatures: 'Feartan Sàbhailteachd',
    compatibility: 'Co-chòrdalachd',
    careInstructions: 'Stiùireadh Cùraim',
    warrantySupport: 'Barantas & Taic',
    timeUiStrings: {
      noTimeAvailable: 'Chan eil àm ri fhaighinn',
      inTheFuture: 'San àm ri teachd',
      justNow: 'an-dràsta fhèin',
      oneMinuteAgo: '1 mhionaid air ais',
      xMinutesAgo: '{x} mionaidean air ais',
      over30Minutes: 'còrr air 30 mionaidean air ais',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Naidheachdan Inception',
    trademarkPolicy: 'Poileasaidh Comharra-malairt',
    trademarkPolicyContent: [
      `Tha 'Inception' na chomharra-malairt clàraichte de InceptionAI Inc., air a chlàradh agus air a chur an gnìomh ann an Canada agus ann an diofar uachdranas eile.`,
      'Tha comharran-malairt, logothan agus ainmean malairt eile a tha a\' nochdadh air an làrach-lìn seo nan seilbh dha na h-ùghdarran aca fhèin.',
      'Tha sinn a\' toirt spèis do chòraichean seilbh inntleachdail. Airson ceistean mu chomharran-malairt, cuir fios gu:',
    ],
    tryAgain: 'Feuch a-rithist',
    uploadCV: 'Luchdaich Suas CV',
    viewDetails: 'Seall Mion-fhiosrachadh',
    viewMore: 'Seall Barrachd',
    welcome: 'Fàilte gu Inception',
    linkInput: {
      smartExtract: 'Togail tapaidh:',
      apply: 'Cuir an gnìomh',
      pasteHelper: 'Cuir a-steach URL slàn airson togail ainm-cleachdaidh gu fèin-obrachail',
      placeholderDefault: '---',
    },
    privacyPolicy: 'Poileasaidh Prìobhaideachd',
    affiliateDisclosure: 'Mar chom-pàirtiche Amazon agus com-pàirtiche ceangailte, bidh Inception a\' cosnadh bho cheannachdan a tha airidh. Chan eil seo a\' toirt buaidh air ar rangachaidhean.',
    articlesIntro: 'Tha artaigilean Inception a\' tairgsinn mion-sgrùdaidhean agus stiùireadh ceannach gus do chuideachadh le co-dhùnaidhean ceannach eòlach a dhèanamh.',
    fallbackNotice:
      'Cha deach an duilleag shònraichte a bha thu a\' lorg a lorg, ach tha sinn a\' sealltainn susbaint buntainneach na àite.',
    aboutInceptionAi:
      `Lorgaidh Inception am bathar as fheàrr dhut {region}, le AI a tha cunntachail dhut fhèin, chan ann do luchd-sanasachd.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'ann an Canada',
      [Region.USA]: 'anns na SA',
      [Region.France]: 'anns an Fhraing',
      [Region.UK]: 'anns an RA',
      [Region.Australia]: 'ann an Astràilia',
      [Region.All]: 'nad sgìre',
    },
  },
  // Māori
  mi: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Utu Amazon · {time}',
    aboutTitle: 'Ngā Mea Matua:',
    aboutUs: 'Mō Mātou',
    vision: 'Tō Mātou Matakite',
    aboutUsTitle: 'He aha a Inception?',
    ageVerification: {
      title: 'Me Whakaū te Pakeke',
      subtitle: 'Whakamātautau Ihirangi Pakeke',
      warning: 'He ihirangi pakeke kei tēnei paetukutuku, ā, mō ngā tāngata 18 tau neke atu anake.',
      disclaimer:
        'Mā te uru ki tēnei pae, ka whakaū koe he pakeke koe i raro i ngā ture o tō rohe, ā, e hiahia ana koe ki te mātakitaki ihirangi pakeke.',
      confirmAge: 'E whakaū ana au he 18 tau neke atu tōku pakeke',
      enterButton: 'Uru ki te Pae',
      exitButton: 'Puta',
      mustBe18: 'Me 18+ koe ki te uru ki tēnei ihirangi',
      adultContentWarning: 'He ihirangi pakeke tūturu kei tēnei pae mō ngā hunga pakeke anake.',
      euCompliance: 'E whai tikanga ana tēnei whakaū i te Ture Ratonga Matihiko o te EU me ngā herenga whakaū pakeke ā-motu.',
    },
    alternativeLabel: '*kōwhiringa kē pai rawa i kitea e mātou',
    at: 'i',
    averagePriceTag: 'Toharite: ',
    best: 'Pai Rawa PLACEHOLDER',
    bestFeatures: 'He aha ngā mea e pai ai he hua ki tōna wāhanga?',
    articles: 'Ngā Tuhinga',
    backToHome: 'Hoki ki te Kāinga',
    breadcrumbHome: 'Kāinga',
    goBack: 'Hoki',
    by: 'nā',
    cancel: 'Whakakore',
    methodology: 'Tā mātou kōwhiri',
    partners: 'Ngā hoa pakihi',
    careers: 'Ngā Mahi',
    changeRegion: 'Huri Rohe',
    choosePrice: 'I te wā e tatari ana, ka taea e koe te kōwhiri i tētahi awhe utu e pai ana ki a koe.',
    comingSoon: 'Kei te Haere Mai',
    conclusion: 'Whakakapinga',
    cookieConsent: {
      title: 'E Whai Mana Ana Tō Matatapu',
      description: 'Ka whakamahi mātou i ngā pihikete kia pai ake tō wheako, kia pai ake ngā tūtohutanga hua. Ka noho matatapu, ka noho matatapu tonu ō raraunga.',
      essential: 'Pihikete Waiwai',
      analytics: 'Pihikete Tātaritanga',
      advertising: 'Pihikete Pānui',
      learnMore: 'Ako atu anō',
      acceptAll: 'Whakaae & Haere Tonu',
      rejectAll: 'Whakakāhore',
      customizeSettings: 'Whakaritea',
      savePreferences: 'Tiakina Ngā Manakohanga',
      essentialDescription: 'E hiahiatia ana kia pai ai te mahi o te paetukutuku. Kāore e taea te whakakore.',
      analyticsDescription: 'Ka āwhina i a mātou ki te mārama me pēhea ngā manuhiri e tūhono ai ki tā mātou paetukutuku mā te kohi raraunga matatapu.',
      advertisingDescription: 'Ka whakamahia ki te whakaatu pānui e hāngai ana mā Google AdSense. Ka utaina anake ina whakaae koe.',
      californiaNotice: 'Ngā Kainoho o California: Kāore mātou e hoko i ō mōhiohio whaiaro. Ka puta koe i te kohinga raraunga katoa ehara i te mea waiwai mā te whakakāhore i ngā pihikete tātaritanga.',
    },
    contactUs: 'Whakapā Mai',
    contactFormDescription: 'Tēnā whakakīia te puka i raro nei.',
    defaultMetadata: {
      title: `Inception | Te hua pai rawa atu mōu, i kitea e te AI pono`,
      description:
        `Kei reira kē te hua pai rawa atu mōu. Ka kitea e tā mātou AI, nā te mea ko koe tā mātou e whakautu ai, ehara i te kaipānui. Ka hoatu e Inception ki a koe ngā tūtohutanga hua pono mō tō rohe.`,
    },
    descriptionArticle:
      'Ko tā mātou kaupapa he whakarerekē i te āhua o te whakapai i tā ngā pakihi noho ipurangi mā te tuku ihirangi kounga KORE UTU ki ā rātou kiritaki.',
    describeDemoCategory:
      'I konei ka whakamāramahia ngā āhuatanga me ngā kounga e mea ai he hua ki te taumata tiketike o tēnei wāhanga.',
    describeDemoComparison:
      'I konei ka whakamāramahia ngā āhuatanga me ngā kounga hei hanga rārangi māmā o ngā hua pai rawa.',
    directlySearch: 'Rapu tika',
    disconnect: 'Whakawehi',
    emailPlaceholder: 'Whakaurua tō īmēra',
    emailTitle: 'Īmēra',
    example: 'Tauira',
    phoneTitle: 'Nama Waea',
    exploreMore: 'Torohia Atu Anō',
    existingPages: 'Torohia ngā whārangi e wātea ana e āwhina pea',
    articlesOtherRegions: 'Mai i ō mātou rohe kē atu',
    firstPostComingSoon: `Kei te haere mai tō tuhinga tuatahi, ka whakamōhio atu mātou ki a koe!\nI te wā e tatari ana, ka taea e koe te whakatika i ngā ihirangi i raro nei.`,
    joinUs: 'Hono mai ki a mātou.',
    contactForm: {
      personalInformation: 'Mōhiohio Whaiaro',
      yourMessage: 'Tō Karere',
      firstName: 'Ingoa Tuatahi',
      firstNamePlaceholder: 'Whakaurua tō ingoa tuatahi',
      emailAddress: 'Wāhitau Īmēra',
      emailPlaceholder: 'Whakaurua tō wāhitau īmēra',
      phoneNumber: 'Nama Waea',
      phonePlaceholder: 'Whakaurua tō nama waea',
      company: 'Kamupene/Rōpū',
      companyPlaceholder: 'Whakaurua te ingoa o tō kamupene',
      message: 'Kōrero mai mō tō kaupapa, tō uiui rānei',
      messagePlaceholder: 'Tuaritia ngā taipitopito o tō kaupapa, ngā herenga, ngā pātai rānei...',
      sendMessage: 'Tukuna te Karere',
      sending: 'Kei te tuku...',
      successTitle: 'Kua Tukuna te Karere!',
      successMessage: 'Ngā mihi mō tō karere! Ka hoki mai mātou ki a koe ā tōna wā.',
      processingMessage: 'Kei te tukatuku i tō tono...',
      errorTitle: 'Hapa i te Tuku Karere',
      errorMessage: 'I puta he hapa i te tuku i tō karere. Tēnā whakamātauria anō.',
    },
    loading: 'Kei te uta...',
    localeDisclaimer: 'X Anake',
    login: 'Takiuru',
    lookingFor: 'E rapu ana koe i?',
    profile: 'Kōtaha',
    otherLanguages: 'Ētahi Atu Reo',
    showLess: 'Whakaatu Iti Iho',
    showMoreLanguages: 'Whakaatu Reo Ake',
    messageTitle: 'Karere',
    mission: `Kei reira kē te hua pai rawa atu mōu. Ka kitea e tā mātou AI, nā te mea ko koe tā mātou e whakautu ai, ehara i te kaipānui.`,
    namePlaceholder: 'Whakaurua tō ingoa',
    nameTitle: 'Ingoa',
    newProduct: 'HOU',
    newSearch: 'Rapu Hou: ',
    nextStep: 'Hipanga Panuku',
    noArticles: 'Kāore he tuhinga i tēnei wā',
    noResultsFound: 'PS: Ka taea anake e au te tuku whakaaro mō ngā hua, ngā wāhanga rānei e hokona ana.',
    noSearchResult: 'Haere tonu ki te whakaahua i te hua e rapu ana koe, kātahi pēhia Enter.',
    officialWebsite: 'paetukutuku mana',
    orderNow: 'Ōta Ināianei',
    browseOnAmazon: 'Tirotiro i Amazon',
    searchNow: 'Rapu Ināianei',
    ourMission: 'Tā Mātou Kaupapa',
    overviewTitle: 'Taipitopito',
    askAQuestion: 'He pātai tāu?',
    askSentHint: 'Patohia ki runga',
    pageReference: 'I tēnei whārangi',
    buyNow: 'Hokona Ināianei',
    publishedOn: 'I whakaputaina i te',
    publishingOn: 'Ka whakaputaina i te',
    quickMenu: 'Whakarāpopototanga Kōwhiringa Pai',
    recentBlogs: 'Rongo PLACEHOLDER',
    regionPrompt: {
      switchQuestion: {},
      yesSwitch: 'Āe, huri rohe',
      noThanks: 'Kāo, kia ora',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'Kimi Atu Anō',
    requirements: 'Ngā Herenga',
    responsibilities: 'Ngā Haepapa',
    reviewSummary: 'Whakarāpopototanga Arotake',
    xReviews: '{count} arotake',
    review: 'Arotake',
    searching: 'Kei te rapu i ',
    searchingProduct: 'Kei te kimi i ngā kōwhiringa pai rawa mō ',
    sidemenuListTitle: 'Ā Mātou Kōwhiringa Pai Rawa',
    slogan: `Ko koe tā mātou e whakautu ai, ehara i te kaipānui.`,
    submit: 'Tuku',
    technicalSpecs: 'Tautuhi Hangarau',
    nutritionFacts: 'Taipitopito Kai',
    ingredients: 'Ngā Rauemi',
    materialFabric: 'Rauemi & Kākahu',
    dimensionsSize: 'Inenga & Rahi',
    performanceMetrics: 'Inenga Whakatutuki',
    safetyFeatures: 'Āhuatanga Haumarutanga',
    compatibility: 'Hototahi',
    careInstructions: 'Tohutohu Tiaki',
    warrantySupport: 'Pūtea Ārai & Tautoko',
    timeUiStrings: {
      noTimeAvailable: 'Kāore he wā e wātea ana',
      inTheFuture: 'Ā tōna wā',
      justNow: 'ināianei tonu',
      oneMinuteAgo: '1 meneti ki muri',
      xMinutesAgo: '{x} meneti ki muri',
      over30Minutes: 'neke atu i te 30 meneti ki muri',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Rongo Inception',
    trademarkPolicy: 'Kaupapahere Tohu Tauhokohoko',
    trademarkPolicyContent: [
      `Ko 'Inception' he tohu tauhokohoko rēhitatia a InceptionAI Inc., kua rēhitatia ā, e whakatinanahia ana i Kānata me ētahi atu rohe maha. Ko te whakamahi i te tohu tauhokohoko 'Inception' me tētahi tohu tauhokohoko ōrite rānei me te kore whakaae ā-tuhi o InceptionAI Inc. e rāhuitia ana.`,
      'Ko ētahi atu tohu tauhokohoko, tohu ratonga, waitohu, me ngā ingoa tauhokohoko e puta ana i tēnei paetukutuku, i roto rānei i ā mātou hua, ratonga rānei, nō ō rātou rangatira ake.',
      'E whakaute ana mātou i ngā tika rawa hinengaro, ā, e tūmanako ana ka pērā anō ā mātou kaiwhakamahi me ngā hoa rangapū. Mēnā he pātai, he āwangawanga rānei ōu mō ngā tohu tauhokohoko, te kaupapahere tohu tauhokohoko rānei, tēnā whakapā mai:',
    ],
    tryAgain: 'Whakamātau anō',
    uploadCV: 'Tukuake CV',
    viewDetails: 'Tirohia ngā Taipitopito',
    viewMore: 'Whakaatu Ake',
    welcome: 'Nau mai ki Inception',
    linkInput: {
      smartExtract: 'Tangohanga atamai:',
      apply: 'Whakamahi',
      pasteHelper: 'Whakapiri i te URL katoa kia tangohia aunoatia te ingoa kaiwhakamahi',
      placeholderDefault: '---',
    },
    privacyPolicy: 'Kaupapahere Matatapu',
    affiliateDisclosure: 'Hei hoa rangapū Amazon me hoa hononga, ka whiwhi a Inception i ngā hokonga e uru ana. Kāore tēnei e pā ki ā mātou raupapa. Ko tā mātou rapu hua me te tātaritanga mākete he motuhake i te wāhanga hoko.',
    articlesIntro: 'Ko ngā Tuhinga Inception e whakarato ana i ngā aratohu hoko me ngā tātaritanga hua hōhonu kia āwhina i a koe ki te whakatau hoko mōhio.',
    fallbackNotice: 'Kāore i kitea te whārangi e rapuhia ana e koe, engari kei te whakaatu mātou i ngā ihirangi e hāngai ana.',
    aboutInceptionAi:
      `Ka kitea e Inception te hua pai rawa atu mōu {region}, me te AI e whakautu ana ki a koe, ehara i te kaipānui.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'i Kānata',
      [Region.USA]: 'i Amerika',
      [Region.France]: 'i Wīwī',
      [Region.UK]: 'i te United Kingdom',
      [Region.Australia]: 'i Ahitereiria',
      [Region.All]: 'i tō rohe',
    },
  },
  te: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Amazon ధర · {time}',
    aboutTitle: 'ముఖ్యాంశాలు:',
    aboutUs: 'మా గురించి',
    vision: 'మా విజన్',
    aboutUsTitle: 'Inception అంటే ఏమిటి?',
    ageVerification: {
      title: 'వయస్సు ధృవీకరణ అవసరం',
      subtitle: 'వయోజన కంటెంట్ హెచ్చరిక',
      warning: 'ఈ వెబ్‌సైట్‌లో వయోజన కంటెంట్ ఉంది మరియు 18 సంవత్సరాలు లేదా అంతకంటే ఎక్కువ వయస్సు ఉన్న వ్యక్తులకు మాత్రమే.',
      disclaimer: 'ఈ సైట్‌లోకి ప్రవేశించడం ద్వారా, మీరు మీ అధికార పరిధిలో చట్టపరమైన వయస్సు కలిగి ఉన్నారని మరియు వయోజన కంటెంట్‌ను చూడాలనుకుంటున్నారని నిర్ధారిస్తున్నారు.',
      confirmAge: 'నేను 18 సంవత్సరాలు లేదా అంతకంటే ఎక్కువ వయస్సు ఉన్నానని నిర్ధారిస్తున్నాను',
      enterButton: 'సైట్‌లోకి ప్రవేశించండి',
      exitButton: 'నిష్క్రమించండి',
      mustBe18: 'ఈ కంటెంట్‌ను యాక్సెస్ చేయడానికి మీరు 18+ ఉండాలి',
      adultContentWarning: 'ఈ సైట్‌లో పరిణత ప్రేక్షకుల కోసం మాత్రమే స్పష్టమైన వయోజన కంటెంట్ ఉంది.',
      euCompliance: 'ఈ ధృవీకరణ EU డిజిటల్ సేవల చట్టం మరియు జాతీయ వయస్సు ధృవీకరణ అవసరాలకు అనుగుణంగా ఉంటుంది.',
    },
    alternativeLabel: `*మేము కనుగొన్న ఉత్తమ ప్రత్యామ్నాయం`,
    at: 'వద్ద',
    averagePriceTag: 'సగటు: ',
    best: 'ఉత్తమ PLACEHOLDER',
    bestFeatures: 'ఒక ఉత్పత్తిని దాని వర్గంలో ఉత్తమంగా చేసేది ఏమిటి?',
    articles: 'ఆర్టికల్‌లు',
    backToHome: 'హోమ్‌కు వెళ్ళండి',
    breadcrumbHome: 'హోమ్',
    goBack: 'వెనుకకు',
    by: 'ద్వారా',
    cancel: 'రద్దు చేయండి',
    methodology: 'మేము ఎలా ఎంచుకుంటాము',
    partners: 'భాగస్వాములు',
    careers: 'కెరీర్లు',
    changeRegion: 'ప్రాంతం మార్చండి',
    choosePrice: 'ఈలోగా, మీకు సరిపోయే ధర శ్రేణిని ఎంచుకోవచ్చు.',
    comingSoon: 'త్వరలో వస్తోంది',
    conclusion: 'ముగింపు',
    cookieConsent: {
      title: 'మేము మీ గోప్యతను విలువైనదిగా భావిస్తాము',
      description: 'మీ అనుభవాన్ని మెరుగుపరచడానికి మరియు మెరుగైన ఉత్పత్తి సిఫారసులను అందించడానికి మేము కుకీలను ఉపయోగిస్తాము. మీ డేటా ప్రైవేట్ మరియు అజ్ఞాతంగా ఉంటుంది.',
      essential: 'అవసరమైన కుకీలు',
      analytics: 'విశ్లేషణ కుకీలు',
      advertising: 'ప్రకటన కుకీలు',
      learnMore: 'మరింత తెలుసుకోండి',
      acceptAll: 'ఆమోదించి కొనసాగించండి',
      rejectAll: 'తిరస్కరించండి',
      customizeSettings: 'అనుకూలీకరించండి',
      savePreferences: 'ప్రాధాన్యతలు సేవ్ చేయండి',
      essentialDescription: 'వెబ్‌సైట్ సరిగ్గా పనిచేయడానికి అవసరం. నిలిపివేయబడదు.',
      analyticsDescription: 'అజ్ఞాత డేటా సేకరించడం ద్వారా సందర్శకులు మా వెబ్‌సైట్‌తో ఎలా పరస్పర చర్య చేస్తారో అర్థం చేసుకోవడంలో సహాయపడతాయి.',
      advertisingDescription: 'Google AdSense ద్వారా సంబంధిత ప్రకటనలను ప్రదర్శించడానికి ఉపయోగించబడతాయి. మీరు సమ్మతించినప్పుడు మాత్రమే లోడ్ అవుతాయి.',
      californiaNotice: 'కాలిఫోర్నియా నివాసితులు: మేము మీ వ్యక్తిగత సమాచారాన్ని విక్రయించము. విశ్లేషణ కుకీలను తిరస్కరించడం అన్ని అనవసరమైన డేటా సేకరణ నుండి మిమ్మల్ని ఆప్ట్-అవుట్ చేస్తుంది.',
    },
    contactUs: 'మమ్మల్ని సంప్రదించండి',
    contactFormDescription: 'దయచేసి క్రింది ఫారమ్‌ను పూరించండి.',
    defaultMetadata: {
      title: `Inception | మీ కోసం ఉత్తమ ఉత్పత్తి, నిజాయితీగల AI కనుగొంది`,
      description: `మీ కోసం ఉత్తమ ఉత్పత్తి ఇప్పటికే ఉంది. మా AI దాన్ని కనుగొంటుంది, ఎందుకంటే మేము మీకు జవాబుదారీ, ప్రకటనదారులకు కాదు. Inception మీ ప్రాంతానికి నిజాయితీగల ఉత్పత్తి సిఫారసులను అందిస్తుంది.`,
    },
    descriptionArticle: 'వ్యాపారాలు వారి కస్టమర్లకు నాణ్యమైన కంటెంట్‌ను ఉచితంగా అందించడం ద్వారా వారి ఆన్‌లైన్ ఉనికిని మెరుగుపరచే విధానాన్ని మార్చడం మా లక్ష్యం.',
    describeDemoCategory: 'ఇక్కడ మేము ఈ వర్గంలో ఒక ఉత్పత్తిని అగ్ర ప్రతిష్టంభకునిగా లేదా నాయకునిగా చేసే ఫీచర్లు మరియు గుణాలను నిర్వచిస్తాము.',
    describeDemoComparison: 'ఇక్కడ మేము మీ శోధన ప్రమాణాల ఆధారంగా ఉత్తమ ఉత్పత్తి పేర్ల సరళ జాబితాను సృష్టిస్తాము.',
    directlySearch: 'నేరుగా శోధించండి',
    disconnect: 'డిస్‌కనెక్ట్',
    emailPlaceholder: 'మీ ఇమెయిల్ నమోదు చేయండి',
    emailTitle: 'ఇమెయిల్',
    example: 'ఉదాహరణ',
    phoneTitle: 'ఫోన్ నంబర్',
    exploreMore: 'మరింత అన్వేషించండి',
    existingPages: 'సహాయపడగల ఇప్పటికే ఉన్న పేజీలను సందర్శించండి',
    articlesOtherRegions: 'మా ఇతర ప్రాంతాల నుండి',
    firstPostComingSoon: `మీ మొదటి పోస్ట్ త్వరలో వస్తోంది, మేము మిమ్మల్ని తెలియజేస్తాము!  
ఈలోగా, మీకు కావాలంటే క్రింది కంటెంట్‌ను సవరించవచ్చు.`,
    joinUs: 'మాతో చేరండి.',
    contactForm: {
      personalInformation: 'వ్యక్తిగత సమాచారం',
      yourMessage: 'మీ సందేశం',
      firstName: 'మొదటి పేరు',
      firstNamePlaceholder: 'మీ మొదటి పేరు నమోదు చేయండి',
      emailAddress: 'ఇమెయిల్ చిరునామా',
      emailPlaceholder: 'మీ ఇమెయిల్ చిరునామా నమోదు చేయండి',
      phoneNumber: 'ఫోన్ నంబర్',
      phonePlaceholder: 'మీ ఫోన్ నంబర్ నమోదు చేయండి',
      company: 'కంపెనీ/సంస్థ',
      companyPlaceholder: 'మీ కంపెనీ పేరు నమోదు చేయండి',
      message: 'మీ ప్రాజెక్ట్ లేదా విచారణ గురించి చెప్పండి',
      messagePlaceholder: 'మీ ప్రాజెక్ట్ వివరాలు, అవసరాలు లేదా ఏవైనా ప్రశ్నలు పంచుకోండి...',
      sendMessage: 'సందేశం పంపండి',
      sending: 'పంపుతోంది...',
      successTitle: 'సందేశం విజయవంతంగా పంపబడింది!',
      successMessage: 'మీ సందేశానికి ధన్యవాదాలు! మేము త్వరలో మీకు తిరిగి సమాధానమిస్తాము.',
      processingMessage: 'మీ అభ్యర్థనను ప్రాసెస్ చేస్తోంది...',
      errorTitle: 'సందేశం పంపడంలో లోపం',
      errorMessage: 'మీ సందేశాన్ని పంపడంలో లోపం జరిగింది. దయచేసి మళ్ళీ ప్రయత్నించండి.',
    },
    loading: 'లోడ్ అవుతోంది...',
    localeDisclaimer: 'X మాత్రమే',
    login: 'లాగిన్',
    lookingFor: 'మీరు వెతుకుతున్నారా?',
    profile: 'ప్రొఫైల్',
    otherLanguages: 'ఇతర భాషలు',
    showLess: 'తక్కువ చూపించండి',
    showMoreLanguages: 'మరిన్ని భాషలు చూపించండి',
    messageTitle: 'సందేశం',
    mission: `మీ కోసం ఉత్తమ ఉత్పత్తి ఇప్పటికే ఉంది. మా AI దాన్ని కనుగొంటుంది, ఎందుకంటే మేము మీకు జవాబుదారీ, ప్రకటనదారులకు కాదు.`,
    namePlaceholder: 'మీ పేరు నమోదు చేయండి',
    nameTitle: 'పేరు',
    newProduct: 'కొత్తగా జోడించబడింది',
    newSearch: 'కొత్త శోధన: ',
    nextStep: 'తదుపరి దశ',
    noArticles: 'ప్రస్తుతం ఆర్టికల్‌లు లేవు',
    noResultsFound: 'p.s. నేను మార్కెట్ చేయగల ఉత్పత్తులు లేదా వర్గాలపై మాత్రమే సూచనలు అందించగలను.',
    noSearchResult: 'మీరు వెతుకుతున్న ఉత్పత్తిని వివరిస్తూ ఉండండి, ఆపై Enter నొక్కండి.',
    officialWebsite: 'అధికారిక వెబ్‌సైట్',
    orderNow: 'ఇప్పుడు ఆర్డర్ చేయండి',
    browseOnAmazon: 'Amazon లో బ్రౌజ్ చేయండి',
    searchNow: 'ఇప్పుడు శోధించండి',
    ourMission: 'మా లక్ష్యం',
    overviewTitle: 'వివరాలు',
    askAQuestion: 'ప్రశ్న ఉందా?',
    askSentHint: 'పైన టైప్ చేయండి',
    pageReference: 'ఈ పేజీలో',
    buyNow: 'ఇప్పుడు కొనుగోలు చేయండి',
    publishedOn: 'ప్రచురించబడింది',
    publishingOn: 'ప్రచురణ',
    quickMenu: 'అగ్ర ఎంపికల సారాంశం',
    recentBlogs: 'PLACEHOLDER వార్తలు',
    regionPrompt: {
      switchQuestion: {},
      yesSwitch: 'అవును, ప్రాంతం మార్చండి',
      noThanks: 'వద్దు, ధన్యవాదాలు',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'మరింత కనుగొనండి',
    requirements: 'అవసరాలు',
    responsibilities: 'బాధ్యతలు',
    reviewSummary: 'సమీక్ష సారాంశం',
    xReviews: '{count} సమీక్షలు',
    review: 'సమీక్ష',
    searching: 'శోధిస్తోంది ',
    searchingProduct: 'ఉత్తమ ప్రత్యామ్నాయాలను కనుగొంటోంది ',
    sidemenuListTitle: 'మా ఉత్తమ ఎంపికలు',
    slogan: `మేము మీకు జవాబుదారీ, ప్రకటనదారులకు కాదు.`,
    submit: 'సబ్మిట్ చేయండి',
    timeUiStrings: {
      noTimeAvailable: 'సమయం అందుబాటులో లేదు',
      inTheFuture: 'భవిష్యత్తులో',
      justNow: 'ఇప్పుడే',
      oneMinuteAgo: '1 నిమిషం క్రితం',
      xMinutesAgo: '{x} నిమిషాల క్రితం',
      over30Minutes: '30 నిమిషాల కంటే ఎక్కువ క్రితం',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Inception వార్తలు',
    trademarkPolicy: 'ట్రేడ్‌మార్క్ విధానం',
    trademarkPolicyContent: [
      "'Inception' అనేది InceptionAI Inc. యొక్క నమోదిత ట్రేడ్‌మార్క్, కెనడాలో అలాగే వివిధ ఇతర అధికార పరిధులలో చట్టబద్ధంగా నమోదు చేయబడింది మరియు అమలు చేయబడుతోంది.",
      'ఈ వెబ్‌సైట్‌లో లేదా మా ఉత్పత్తులు లేదా సేవలలో కనిపించే ఇతర ట్రేడ్‌మార్క్‌లు, సేవా గుర్తులు, లోగోలు మరియు బ్రాండ్ పేర్లు వాటి సంబంధిత యజమానుల ఆస్తి.',
      'మా ప్లాట్‌ఫారమ్‌లో ప్రదర్శించబడిన ఏదైనా ట్రేడ్‌మార్క్ అనధికారిక వాడకం అనుమతించబడదు. ట్రేడ్‌మార్క్‌ల వాడకం లేదా ఈ ట్రేడ్‌మార్క్ విధానం గురించి ఏవైనా ప్రశ్నలు లేదా ఆందోళనలు ఉంటే, దయచేసి సంప్రదించండి:',
    ],
    tryAgain: 'మళ్ళీ ప్రయత్నించండి',
    uploadCV: 'CV అప్‌లోడ్ చేయండి',
    viewDetails: 'వివరాలు చూడండి',
    viewMore: 'మరిన్ని చూపించండి',
    linkInput: {
      smartExtract: 'స్మార్ట్ ఎక్స్‌ట్రాక్ట్:',
      apply: 'వర్తింపజేయండి',
      pasteHelper: 'ఆటోమేటిక్ యూజర్‌నేమ్ ఎక్స్‌ట్రాక్షన్ కోసం పూర్తి URL పేస్ట్ చేయండి',
      placeholderDefault: '---',
    },
    technicalSpecs: 'సాంకేతిక స్పెసిఫికేషన్లు',
    nutritionFacts: 'పోషణ వాస్తవాలు',
    ingredients: 'పదార్థాలు',
    materialFabric: 'మెటీరియల్ & ఫ్యాబ్రిక్',
    dimensionsSize: 'కొలతలు & పరిమాణం',
    performanceMetrics: 'పనితీరు మెట్రిక్‌లు',
    safetyFeatures: 'భద్రతా ఫీచర్లు',
    compatibility: 'అనుకూలత',
    careInstructions: 'సంరక్షణ సూచనలు',
    warrantySupport: 'వారంటీ & సపోర్ట్',
    welcome: 'Inception కి స్వాగతం',
    privacyPolicy: 'గోప్యతా విధానం',
    affiliateDisclosure: 'Amazon Associate మరియు అనుబంధ భాగస్వామిగా, Inception అర్హత ఉన్న కొనుగోళ్ల నుండి ఆదాయం పొందుతుంది. ఇది మా ర్యాంకింగ్‌లను ప్రభావితం చేయదు. మా ఉత్పత్తి శోధన మరియు మార్కెట్ విశ్లేషణ విక్రయ భాగం నుండి వేరు.',
    articlesIntro: 'Inception ఆర్టికల్‌లు కొనుగోలు గైడ్‌లు మరియు సమగ్ర ఉత్పత్తి విశ్లేషణలను అందిస్తాయి.',
    fallbackNotice: 'మీరు వెతుకుతున్న నిర్దిష్ట పేజీ కనుగొనబడలేదు, కానీ సంబంధిత కంటెంట్‌ను మేము చూపిస్తున్నాము.',
    aboutInceptionAi: `Inception {region} మీ కోసం ఉత్తమ ఉత్పత్తిని కనుగొంటుంది, మీకు జవాబుదారీగా ఉండే AIతో, ప్రకటనదారులకు కాదు.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'కెనడాలో',
      [Region.USA]: 'అమెరికాలో',
      [Region.France]: 'ఫ్రాన్స్‌లో',
      [Region.UK]: 'యునైటెడ్ కింగ్‌డమ్‌లో',
      [Region.Australia]: 'ఆస్ట్రేలియాలో',
      [Region.All]: 'మీ ప్రాంతంలో',
    },
  },
  // Swedish
  sv: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Amazon-pris · {time}',
    aboutTitle: 'Det väsentliga:',
    aboutUs: 'Om oss',
    vision: 'Vår vision',
    aboutUsTitle: 'Vad är Inception?',
    ageVerification: {
      title: 'Åldersverifiering krävs',
      subtitle: 'Varning för vuxeninnehåll',
      warning: 'Denna webbplats innehåller vuxeninnehåll och är endast avsedd för personer som är 18 år eller äldre.',
      disclaimer:
        'Genom att gå in på denna webbplats bekräftar du att du har uppnått myndig ålder i din jurisdiktion och att du vill se vuxeninnehåll.',
      confirmAge: 'Jag bekräftar att jag är 18 år eller äldre',
      enterButton: 'Gå in på webbplatsen',
      exitButton: 'Lämna',
      mustBe18: 'Du måste vara 18+ för att komma åt detta innehåll',
      adultContentWarning: 'Denna webbplats innehåller explicit vuxeninnehåll som enbart är avsett för en mogen publik.',
      euCompliance: 'Denna verifiering uppfyller EU:s lag om digitala tjänster och nationella krav på åldersverifiering.',
    },
    alternativeLabel: '*bästa alternativet vi hittade',
    at: 'på',
    averagePriceTag: 'Genomsnitt: ',
    best: 'Bästa PLACEHOLDER',
    bestFeatures: 'Vad gör en produkt bäst i sin kategori?',
    articles: 'Artiklar',
    backToHome: 'Startsidan',
    breadcrumbHome: 'Hem',
    buyActionDescription: 'Jämför var du kan köpa {product} på Inception: de pålitliga återförsäljare vi följer på den här marknaden, det bästa pris vi har observerat hos var och en och det alternativ vi rekommenderar. Priserna kontrolleras regelbundet och kan ändras, och återförsäljaren bekräftar slutpriset i kassan.',
    goBack: 'Tillbaka',
    by: 'av',
    cancel: 'Avbryt',
    methodology: 'Så väljer vi',
    partners: 'Partner',
    careers: 'Karriär',
    changeRegion: 'Byt region',
    shoppingRegionLabel: 'Shoppingregion: {region}',
    choosePrice: 'Under tiden kan du välja ett prisintervall som passar dig.',
    comingSoon: 'Kommer snart',
    conclusion: 'Slutsats',
    cookieConsent: {
      title: 'Vi värnar om din integritet',
      description: 'Vi använder kakor för att förbättra din upplevelse och ge bättre produktrekommendationer. Dina data förblir privata och anonyma.',
      essential: 'Nödvändiga kakor',
      analytics: 'Analytiska kakor',
      advertising: 'Reklamkakor',
      learnMore: 'Läs mer',
      acceptAll: 'Acceptera och fortsätt',
      rejectAll: 'Avvisa',
      customizeSettings: 'Anpassa',
      savePreferences: 'Spara inställningar',
      essentialDescription: 'Krävs för att webbplatsen ska fungera korrekt. Kan inte inaktiveras.',
      analyticsDescription: 'Hjälper oss förstå hur besökare interagerar med vår webbplats genom att samla in anonym data.',
      advertisingDescription: 'Används för att visa relevanta annonser via Google AdSense. Laddas bara med ditt samtycke.',
      californiaNotice: 'Invånare i Kalifornien: Vi säljer inte dina personuppgifter. Att avvisa analytiska kakor undantar dig från all icke-nödvändig datainsamling.',
    },
    contactUs: 'Kontakta oss',
    contactFormDescription: 'Vänligen fyll i formuläret nedan.',
    defaultMetadata: {
      title: `Inception | Den bästa produkten för dig, hittad av ärlig AI`,
      description:
        `Den bästa produkten för dig finns redan. Vår AI hittar den, för vi svarar inför dig, inte inför annonsörerna. Inception ger dig AI-researchade produktrekommendationer för din region, utan sponsrade placeringar.`,
    },
    descriptionArticle:
      'Vårt uppdrag är att förändra hur företag stärker sin närvaro online genom att göra det möjligt för dem att erbjuda GRATIS kvalitetsinnehåll till sina kunder.',
    describeDemoCategory:
      'Här definierar vi de egenskaper och kvaliteter som gör en produkt till en ledare i denna kategori.',
    describeDemoComparison:
      'Här definierar vi de egenskaper som gör en produkt till en ledare för att skapa en enkel lista över de bästa produktnamnen baserat på dina sökkriterier.',
    directlySearch: 'Sök direkt',
    disconnect: 'Logga ut',
    emailPlaceholder: 'Ange din e-postadress',
    emailTitle: 'E-post',
    example: 'Exempel',
    phoneTitle: 'Telefonnummer',
    exploreMore: 'Utforska mer',
    existingPages: 'Besök befintliga sidor som kan hjälpa dig',
    articlesOtherRegions: 'Från våra andra regioner',
    firstPostComingSoon: `Ditt första inlägg kommer snart, vi håller dig uppdaterad!\nUnder tiden kan du redigera innehållet nedan om du vill.`,
    joinUs: 'Gå med oss.',
    contactForm: {
      personalInformation: 'Personuppgifter',
      yourMessage: 'Ditt meddelande',
      firstName: 'Förnamn',
      firstNamePlaceholder: 'Ange ditt förnamn',
      emailAddress: 'E-postadress',
      emailPlaceholder: 'Ange din e-postadress',
      phoneNumber: 'Telefonnummer',
      phonePlaceholder: 'Ange ditt telefonnummer',
      company: 'Företag/Organisation',
      companyPlaceholder: 'Ange ditt företagsnamn',
      message: 'Berätta om ditt projekt eller din förfrågan',
      messagePlaceholder: 'Dela med dig av projektdetaljer, krav eller frågor...',
      sendMessage: 'Skicka meddelande',
      sending: 'Skickar...',
      successTitle: 'Meddelande skickat!',
      successMessage: 'Tack för ditt meddelande! Vi hör av oss snart.',
      processingMessage: 'Bearbetar din förfrågan...',
      errorTitle: 'Fel vid sändning av meddelande',
      errorMessage: 'Ett fel uppstod vid sändning av ditt meddelande. Försök igen.',
    },
    loading: 'Laddar...',
    localeDisclaimer: 'Endast X',
    login: 'Logga in',
    lookingFor: 'Letar du efter?',
    profile: 'Profil',
    otherLanguages: 'Andra språk',
    showLess: 'Visa mindre',
    showMoreLanguages: 'Visa fler språk',
    messageTitle: 'Meddelande',
    mission: `Den bästa produkten för dig finns redan. Vår AI hittar den, för vi svarar inför dig, inte inför annonsörerna.`,
    namePlaceholder: 'Ange ditt namn',
    nameTitle: 'Namn',
    newProduct: 'NYLIGEN TILLAGD',
    newSearch: 'Ny sökning: ',
    nextStep: 'Nästa steg',
    noArticles: 'Det finns inga artiklar för närvarande',
    noResultsFound: 'PS: Jag kan bara ge förslag på säljbara produkter eller kategorier.',
    noSearchResult: 'Fortsätt beskriva produkten du letar efter och tryck sedan på Enter.',
    officialWebsite: 'officiell webbplats',
    orderNow: 'Beställ nu',
    regulatedNotice: {
      prescriptionOnly: 'Endast receptbelagt',
      professionalUse: 'Professionellt bruk',
      fromVeterinarian: 'Finns hos din veterinär',
      fromPharmacist: 'Finns på apotek',
      noOnlinePurchase: 'Säljs inte online',
      professionalSupply: 'Säljs via professionella leverantörer',
      whyNoLink: 'Vi recenserar produkten men länkar inte till den.',
    },
    browseOnAmazon: 'Bläddra på Amazon',
    unavailableInCountry: 'För närvarande otillgänglig i {country}',
    searchSimilarOnAmazon: 'Sök liknande på Amazon',
    searchNow: 'Sök nu',
    ourMission: 'Vårt uppdrag',
    overviewTitle: 'Detaljer',
    askAQuestion: 'Har du en fråga?',
    askSentHint: 'Skriv den högst upp',
    micBlocked: 'Mikrofonen är blockerad. Tillåt den i webbläsarens inställningar.',
    pageReference: 'På denna sida',
    buyNow: 'Köp nu',
    publishedOn: 'Publicerad',
    publishingOn: 'Publiceras',
    quickMenu: 'Sammanfattning av toppval',
    recentBlogs: 'Nyheter om PLACEHOLDER',
    regionPrompt: {
      switchQuestion: {},
      yesSwitch: 'Ja, byt region',
      noThanks: 'Nej tack',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'Utforska mer',
    requirements: 'Krav',
    responsibilities: 'Ansvarsområden',
    reviewSummary: 'Sammanfattning av omdömen',
    xReviews: '{count} omdömen',
    review: 'Omdöme',
    searching: 'Söker efter ',
    searchingProduct: 'Hittar de bästa alternativen för ',
    sidemenuListTitle: 'Våra bästa val',
    slogan: `Vi svarar inför dig, inte inför annonsörerna.`,
    submit: 'Skicka',
    technicalSpecs: 'Tekniska specifikationer',
    nutritionFacts: 'Näringsinformation',
    ingredients: 'Ingredienser',
    materialFabric: 'Material och tyg',
    dimensionsSize: 'Dimensioner och storlek',
    performanceMetrics: 'Prestandamått',
    safetyFeatures: 'Säkerhetsfunktioner',
    compatibility: 'Kompatibilitet',
    careInstructions: 'Skötselråd',
    warrantySupport: 'Garanti och support',
    timeUiStrings: {
      noTimeAvailable: 'Ingen tid tillgänglig',
      inTheFuture: 'I framtiden',
      justNow: 'just nu',
      oneMinuteAgo: '1 minut sedan',
      xMinutesAgo: '{x} minuter sedan',
      over30Minutes: 'för mer än 30 minuter sedan',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Inception Nyheter',
    trademarkPolicy: 'Varumärkespolicy',
    trademarkPolicyContent: [
      `'Inception' är ett registrerat varumärke tillhörande InceptionAI Inc., vederbörligen registrerat och upprätthållet i Kanada samt i flera andra jurisdiktioner.`,
      'Andra varumärken, tjänstemärken, logotyper och handelsnamn som förekommer på denna webbplats tillhör sina respektive ägare.',
      'Vi respekterar immateriella rättigheter. Vid frågor om varumärken eller denna varumärkespolicy, vänligen kontakta:',
    ],
    tryAgain: 'Försök igen',
    uploadCV: 'Ladda upp CV',
    viewDetails: 'Visa detaljer',
    viewMore: 'Visa mer',
    welcome: 'Välkommen till Inception',
    linkInput: {
      smartExtract: 'Smart extrahering:',
      apply: 'Tillämpa',
      pasteHelper: 'Klistra in fullständig URL för automatisk extrahering av användarnamn',
      placeholderDefault: '---',
    },
    privacyPolicy: 'Integritetspolicy',
    affiliateDisclosure: 'Som Amazon-partner och affiliatepartner tjänar Inception på kvalificerade köp. Detta påverkar inte våra rankningar. Vår produktsökning och marknadsanalys är separata från försäljningsdelen.',
    articlesIntro: 'Inception Artiklar erbjuder köpguider och produktanalyser, skapade med AI, för att hjälpa dig fatta välgrundade köpbeslut.',
    fallbackNotice: 'Den sida du letade efter hittades inte, men vi visar relaterat innehåll istället.',
    aboutInceptionAi:
      `Inception hittar den bästa produkten för dig {region}, med AI som svarar inför dig, inte inför annonsörerna.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'i Kanada',
      [Region.USA]: 'i USA',
      [Region.France]: 'i Frankrike',
      [Region.UK]: 'i Storbritannien',
      [Region.Australia]: 'i Australien',
      [Region.All]: 'i din region',
    },
  },
  // Irish
  ga: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Praghas Amazon · {time}',
    aboutTitle: 'Na Bunrudaí:',
    aboutUs: 'Fúinn',
    vision: 'Ár bhFís',
    aboutUsTitle: 'Cad is Inception ann?',
    ageVerification: {
      title: 'Fíorú Aoise ag Teastáil',
      subtitle: 'Rabhadh Ábhair do Dhaoine Fásta',
      warning: 'Tá ábhar do dhaoine fásta ar an suíomh gréasáin seo agus tá sé do dhaoine 18 mbliana d\'aois nó níos sine amháin.',
      disclaimer:
        'Trí dhul isteach sa suíomh seo, dearbhaíonn tú go bhfuil tú d\'aois dlíthiúil i do dhlínse agus gur mian leat ábhar do dhaoine fásta a fheiceáil.',
      confirmAge: 'Dearbhaím go bhfuil mé 18 mbliana d\'aois nó níos sine',
      enterButton: 'Téigh Isteach sa Suíomh',
      exitButton: 'Fág',
      mustBe18: 'Ní mór duit a bheith 18+ chun rochtain a fháil ar an ábhar seo',
      adultContentWarning: 'Tá ábhar follasach do dhaoine fásta ar an suíomh seo atá ceaptha do lucht féachana aibí amháin.',
      euCompliance: 'Comhlíonann an fíorú seo Acht Seirbhísí Digiteacha an AE agus ceanglais náisiúnta um fhíorú aoise.',
    },
    alternativeLabel: '*an rogha mhalartach is fearr a fuaireamar',
    at: 'ag',
    averagePriceTag: 'Meán: ',
    best: 'PLACEHOLDER is Fearr',
    bestFeatures: 'Cad a dhéanann táirge mar an ceann is fearr ina chatagóir?',
    articles: 'Ailt',
    backToHome: 'Abhaile',
    breadcrumbHome: 'Baile',
    goBack: 'Ar ais',
    by: 'le',
    cancel: 'Cealaigh',
    methodology: 'Conas a roghnaímid',
    partners: 'Comhpháirtithe',
    careers: 'Gairmeacha',
    changeRegion: 'Athraigh Réigiún',
    choosePrice: 'Idir an dá linn, is féidir leat raon praghsanna a roghnú a oireann duit.',
    comingSoon: 'Ag Teacht go Luath',
    conclusion: 'Conclúid',
    cookieConsent: {
      title: 'Tá Meas Againn ar do Phríobháideachas',
      description: 'Úsáidimid fianáin chun d\'eispéireas a fheabhsú agus moltaí táirgí níos fearr a sholáthar. Fanann do chuid sonraí príobháideach agus anaithnid.',
      essential: 'Fianáin Riachtanacha',
      analytics: 'Fianáin Anailíseacha',
      advertising: 'Fianáin Fógraíochta',
      learnMore: 'Foghlaim tuilleadh',
      acceptAll: 'Glac leis agus Lean ar Aghaidh',
      rejectAll: 'Diúltaigh',
      customizeSettings: 'Saincheap',
      savePreferences: 'Sábháil Roghanna',
      essentialDescription: 'Riachtanach chun go n-oibreoidh an suíomh i gceart. Ní féidir iad a dhíchumasú.',
      analyticsDescription: 'Cabhraíonn siad linn tuiscint a fháil ar conas a n-idirghníomhaíonn cuairteoirí lenár suíomh trí shonraí anaithnid a bhailiú.',
      advertisingDescription: 'Úsáidtear iad chun fógraí ábhartha a thaispeáint trí Google AdSense. Ní luchtaítear iad ach le do thoiliú.',
      californiaNotice: 'Cónaitheoirí California: Ní dhíolaimid do chuid faisnéise pearsanta. Má dhiúltaíonn tú fianáin anailíseacha, eisiatar thú ó gach bailiúchán sonraí neamhriachtanach.',
    },
    contactUs: 'Déan Teagmháil Linn',
    contactFormDescription: 'Líon isteach an fhoirm thíos le do thoil.',
    defaultMetadata: {
      title: `Inception | An táirge is fearr duitse, aimsithe ag intleacht shaorga ionraic`,
      description:
        `Tá an táirge is fearr duitse ann cheana féin. Aimsíonn ár n-intleacht shaorga é, mar is duitse atá muid freagrach, ní do na fógróirí. Tugann Inception moltaí táirgí ionraic duit do do réigiún.`,
    },
    descriptionArticle:
      'Is é ár misean an chaoi a gcuireann gnólachtaí a láithreacht ar líne chun feabhais a athrú trí ligean dóibh ábhar cáilíochta SAOR IN AISCE a sholáthar dá gcustaiméirí.',
    describeDemoCategory:
      'Anseo saineoimid na gnéithe agus na cáilíochtaí a dhéanann táirge mar cheannaire sa chatagóir seo.',
    describeDemoComparison:
      'Anseo saineoimid na gnéithe a dhéanann táirge mar cheannaire chun liosta simplí de na hainmneacha táirgí is fearr a chruthú bunaithe ar do chritéir chuardaigh.',
    directlySearch: 'Cuardaigh go díreach',
    disconnect: 'Dícheangl',
    emailPlaceholder: 'Cuir isteach do ríomhphost',
    emailTitle: 'Ríomhphost',
    example: 'Sampla',
    phoneTitle: 'Uimhir Ghutháin',
    exploreMore: 'Tuilleadh le hIniúchadh',
    existingPages: 'Téigh chuig leathanaigh atá ann cheana a d\'fhéadfadh cabhrú',
    articlesOtherRegions: 'Ónár réigiúin eile',
    firstPostComingSoon: `Tá do chéad phostáil ag teacht go luath, coinneoimid ar an eolas thú!\nIdir an dá linn, is féidir leat an t-ábhar thíos a chur in eagar más mian leat.`,
    joinUs: 'Tar linn.',
    contactForm: {
      personalInformation: 'Faisnéis Phearsanta',
      yourMessage: 'Do Theachtaireacht',
      firstName: 'Céadainm',
      firstNamePlaceholder: 'Cuir isteach do chéadainm',
      emailAddress: 'Seoladh Ríomhphoist',
      emailPlaceholder: 'Cuir isteach do sheoladh ríomhphoist',
      phoneNumber: 'Uimhir Ghutháin',
      phonePlaceholder: 'Cuir isteach d\'uimhir ghutháin',
      company: 'Cuideachta/Eagraíocht',
      companyPlaceholder: 'Cuir isteach ainm do chuideachta',
      message: 'Inis dúinn faoi do thionscadal nó d\'fhiosrúchán',
      messagePlaceholder: 'Roinn sonraí do thionscadail, riachtanais nó aon cheisteanna...',
      sendMessage: 'Seol Teachtaireacht',
      sending: 'Á sheoladh...',
      successTitle: 'Teachtaireacht Seolta go Rathúil!',
      successMessage: 'Go raibh maith agat as do theachtaireacht! Rachaimid i dteagmháil leat go luath.',
      processingMessage: 'Ag próiseáil d\'iarratais...',
      errorTitle: 'Earráid ag Seoladh na Teachtaireachta',
      errorMessage: 'Tharla earráid agus do theachtaireacht á seoladh. Bain triail eile as le do thoil.',
    },
    loading: 'Ag lódáil...',
    localeDisclaimer: 'X amháin',
    login: 'Logáil isteach',
    lookingFor: 'An bhfuil tú ag cuardach?',
    profile: 'Próifíl',
    otherLanguages: 'Teangacha Eile',
    showLess: 'Taispeáin Níos Lú',
    showMoreLanguages: 'Taispeáin Níos Mó Teangacha',
    messageTitle: 'Teachtaireacht',
    mission: `Tá an táirge is fearr duitse ann cheana féin. Aimsíonn ár n-intleacht shaorga é, mar is duitse atá muid freagrach, ní do na fógróirí.`,
    namePlaceholder: 'Cuir isteach d\'ainm',
    nameTitle: 'Ainm',
    newProduct: 'NUA-CHUR LEIS',
    newSearch: 'Cuardach Nua: ',
    nextStep: 'An Chéad Chéim Eile',
    noArticles: 'Níl aon ailt ann faoi láthair',
    noResultsFound: 'NB: Ní féidir liom moltaí a thabhairt ach ar tháirgí nó catagóirí inmhargaidh.',
    noSearchResult: 'Lean ag cur síos ar an táirge atá á chuardach agat, ansin brúigh Enter.',
    officialWebsite: 'suíomh gréasáin oifigiúil',
    orderNow: 'Ordaigh Anois',
    browseOnAmazon: 'Brabhsáil ar Amazon',
    searchNow: 'Cuardaigh Anois',
    ourMission: 'Ár Misean',
    overviewTitle: 'Sonraí',
    askAQuestion: 'An bhfuil ceist agat?',
    askSentHint: 'Clóscríobh thuas é',
    pageReference: 'Ar an leathanach seo',
    buyNow: 'Ceannaigh Anois',
    publishedOn: 'Foilsithe ar',
    publishingOn: 'Le foilsiú ar',
    quickMenu: 'Achoimre ar na Roghanna is Fearr',
    recentBlogs: 'Nuacht PLACEHOLDER',
    regionPrompt: {
      switchQuestion: {
        [RegionCode.Ireland]: 'Ar mhaith leat athrú go hÉirinn?',
        [RegionCode.UK]: 'Ar mhaith leat athrú go dtí an Ríocht Aontaithe?',
      },
      yesSwitch: 'Sea, athraigh réigiún',
      noThanks: 'Ní hea, go raibh maith agat',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'Aimsigh Tuilleadh',
    requirements: 'Riachtanais',
    responsibilities: 'Freagrachtaí',
    reviewSummary: 'Achoimre Léirmheasanna',
    xReviews: '{count} léirmheas',
    review: 'Léirmheas',
    searching: 'Ag cuardach ',
    searchingProduct: 'Ag aimsiú na roghanna malartacha is fearr do ',
    sidemenuListTitle: 'Ár Roghanna is Fearr',
    slogan: `Is duitse atá muid freagrach, ní do na fógróirí.`,
    submit: 'Cuir Isteach',
    technicalSpecs: 'Sonraíochtaí Teicniúla',
    nutritionFacts: 'Fíricí Cothaithe',
    ingredients: 'Comhábhair',
    materialFabric: 'Ábhar agus Fabraic',
    dimensionsSize: 'Toisí agus Méid',
    performanceMetrics: 'Méadraíochtaí Feidhmíochta',
    safetyFeatures: 'Gnéithe Sábháilteachta',
    compatibility: 'Comhoiriúnacht',
    careInstructions: 'Treoracha Cúraim',
    warrantySupport: 'Barántas agus Tacaíocht',
    timeUiStrings: {
      noTimeAvailable: 'Níl am ar fáil',
      inTheFuture: 'Sa todhchaí',
      justNow: 'díreach anois',
      oneMinuteAgo: '1 nóiméad ó shin',
      xMinutesAgo: '{x} nóiméad ó shin',
      over30Minutes: 'breis agus 30 nóiméad ó shin',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Nuacht Inception',
    trademarkPolicy: 'Polasaí Trádmharcanna',
    trademarkPolicyContent: [
      `Is trádmharc cláraithe de chuid InceptionAI Inc. é 'Inception', atá cláraithe agus forfheidhmithe go cuí i gCeanada agus i ndlínsí éagsúla eile.`,
      'Is le húinéirí faoi seach iad trádmharcanna, marcanna seirbhíse, lógónna agus ainmneacha trádála eile a thaispeántar ar an suíomh gréasáin seo nó inár dtáirgí nó seirbhísí.',
      'Tugaimid meas ar chearta maoine intleachtúla. Le haghaidh ceisteanna faoi thrádmharcanna nó an polasaí trádmharcanna seo, déan teagmháil le:',
    ],
    tryAgain: 'Bain triail eile as',
    uploadCV: 'Uaslódáil CV',
    viewDetails: 'Féach ar Shonraí',
    viewMore: 'Taispeáin Tuilleadh',
    welcome: 'Fáilte go Inception',
    linkInput: {
      smartExtract: 'Eastóscadh cliste:',
      apply: 'Cuir i bhfeidhm',
      pasteHelper: 'Greamaigh an URL iomlán le haghaidh eastóscadh ainm úsáideora go huathoibríoch',
      placeholderDefault: '---',
    },
    privacyPolicy: 'Polasaí Príobháideachais',
    affiliateDisclosure: 'Mar Chomhlach Amazon agus comhpháirtí cleamhnaithe, tuileann Inception ó cheannacháin cháilitheacha. Ní chuireann sé seo isteach ar ár ranguithe.',
    articlesIntro: 'Cuireann Ailt Inception treoracha ceannaithe agus anailís dhomhain ar tháirgí ar fáil chun cabhrú leat cinntí ceannaithe eolasacha a dhéanamh.',
    fallbackNotice: 'Ní bhfuarthas an leathanach ar leith a bhí á chuardach agat, ach táimid ag taispeáint ábhair ghaolmhair ina áit.',
    aboutInceptionAi:
      `Aimsíonn Inception an táirge is fearr duitse {region}, le hintleacht shaorga atá freagrach duitse, ní do na fógróirí.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'i gCeanada',
      [Region.USA]: 'sna Stáit Aontaithe',
      [Region.France]: 'sa Fhrainc',
      [Region.UK]: 'sa Ríocht Aontaithe',
      [Region.Australia]: 'san Astráil',
      [Region.All]: 'i do réigiún',
    },
  },
  // Catalan
  ca: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Preu a Amazon · {time}',
    aboutTitle: 'L\'essencial:',
    aboutUs: 'Sobre nosaltres',
    vision: 'La nostra visió',
    aboutUsTitle: 'Què és Inception?',
    ageVerification: {
      title: 'Cal verificació d\'edat',
      subtitle: 'Avís de contingut per a adults',
      warning: 'Aquest lloc web conté contingut per a adults i és només per a persones de 18 anys o més.',
      disclaimer:
        'En entrar en aquest lloc, confirmeu que sou major d\'edat a la vostra jurisdicció i que desitgeu veure contingut per a adults.',
      confirmAge: 'Confirmo que tinc 18 anys o més',
      enterButton: 'Entrar al lloc',
      exitButton: 'Sortir',
      mustBe18: 'Cal tenir 18 anys o més per accedir a aquest contingut',
      adultContentWarning: 'Aquest lloc conté contingut explícit per a adults destinat únicament a un públic madur.',
      euCompliance: 'Aquesta verificació compleix la Llei de Serveis Digitals de la UE i els requisits nacionals de verificació d\'edat.',
    },
    alternativeLabel: '*millor alternativa que hem trobat',
    at: 'a',
    averagePriceTag: 'Mitjana: ',
    best: 'Millor PLACEHOLDER',
    bestFeatures: 'Què fa que un producte sigui el millor de la seva categoria?',
    articles: 'Articles',
    backToHome: 'Inici',
    breadcrumbHome: 'Inici',
    goBack: 'Enrere',
    by: 'per',
    cancel: 'Cancel·lar',
    methodology: 'Com triem',
    partners: 'Col·laboracions',
    careers: 'Carreres',
    changeRegion: 'Canviar de regió',
    shoppingRegionLabel: 'Regió de compra: {region}',
    choosePrice: 'Mentrestant, podeu triar un rang de preus que us convingui.',
    comingSoon: 'Properament',
    conclusion: 'Conclusió',
    cookieConsent: {
      title: 'Valorem la vostra privadesa',
      description: 'Utilitzem galetes per millorar la vostra experiència i oferir millors recomanacions de productes. Les vostres dades romanen privades i anònimes.',
      essential: 'Galetes essencials',
      analytics: 'Galetes analítiques',
      advertising: 'Galetes publicitàries',
      learnMore: 'Més informació',
      acceptAll: 'Acceptar i continuar',
      rejectAll: 'Rebutjar',
      customizeSettings: 'Personalitzar',
      savePreferences: 'Desar preferències',
      essentialDescription: 'Necessàries perquè el lloc funcioni correctament. No es poden desactivar.',
      analyticsDescription: 'Ens ajuden a entendre com els visitants interactuen amb el nostre lloc recollint dades anònimes.',
      advertisingDescription: 'Utilitzades per mostrar anuncis rellevants mitjançant Google AdSense. Només es carreguen amb el vostre consentiment.',
    },
    contactUs: 'Contacteu-nos',
    contactFormDescription: 'Si us plau, empleneu el formulari a continuació.',
    defaultMetadata: {
      title: `Inception | El millor producte per a tu, trobat per una IA honesta`,
      description:
        `El millor producte per a tu ja existeix. La nostra IA el troba, perquè responem davant teu, no davant els anunciants. Inception us ofereix recomanacions de productes investigades amb IA per a la vostra regió, sense posicions patrocinades.`,
    },
    descriptionArticle:
      'La nostra missió és transformar la manera com les empreses milloren la seva presència en línia permetent-los oferir contingut de qualitat GRATUÏT als seus clients.',
    describeDemoCategory:
      'Aquí definirem les característiques i qualitats que fan d\'un producte un líder en aquesta categoria.',
    describeDemoComparison:
      'Aquí definirem les característiques que fan d\'un producte un líder per crear una llista senzilla dels millors noms de productes.',
    directlySearch: 'Cercar directament',
    disconnect: 'Desconnectar',
    emailPlaceholder: 'Introduïu el vostre correu electrònic',
    emailTitle: 'Correu electrònic',
    example: 'Exemple',
    phoneTitle: 'Número de telèfon',
    exploreMore: 'Explorar més',
    existingPages: 'Visiteu pàgines existents que us podrien ajudar',
    articlesOtherRegions: 'De les nostres altres regions',
    firstPostComingSoon: `La vostra primera publicació arribarà aviat, us mantindrem informats!\nMentrestant, podeu editar el contingut a continuació si ho desitgeu.`,
    joinUs: 'Uniu-vos a nosaltres.',
    contactForm: {
      personalInformation: 'Informació Personal',
      yourMessage: 'El Vostre Missatge',
      firstName: 'Nom',
      firstNamePlaceholder: 'Introduïu el vostre nom',
      emailAddress: 'Adreça de Correu Electrònic',
      emailPlaceholder: 'Introduïu la vostra adreça de correu electrònic',
      phoneNumber: 'Número de Telèfon',
      phonePlaceholder: 'Introduïu el vostre número de telèfon',
      company: 'Empresa/Organització',
      companyPlaceholder: 'Introduïu el nom de la vostra empresa',
      message: 'Expliqueu-nos el vostre projecte o consulta',
      messagePlaceholder: 'Compartiu els detalls del vostre projecte, requisits o qualsevol pregunta...',
      sendMessage: 'Enviar Missatge',
      sending: 'Enviant...',
      successTitle: 'Missatge Enviat Correctament!',
      successMessage: 'Gràcies pel vostre missatge! Ens posarem en contacte aviat.',
      processingMessage: 'Processant la vostra sol·licitud...',
      errorTitle: 'Error en Enviar el Missatge',
      errorMessage: 'S\'ha produït un error en enviar el vostre missatge. Si us plau, torneu-ho a provar.',
    },
    loading: 'Carregant...',
    localeDisclaimer: 'Només X',
    login: 'Iniciar sessió',
    lookingFor: 'Què busqueu?',
    profile: 'Perfil',
    otherLanguages: 'Altres Idiomes',
    showLess: 'Mostrar Menys',
    showMoreLanguages: 'Mostrar Més Idiomes',
    messageTitle: 'Missatge',
    mission: `El millor producte per a tu ja existeix. La nostra IA el troba, perquè responem davant teu, no davant els anunciants.`,
    namePlaceholder: 'Introduïu el vostre nom',
    nameTitle: 'Nom',
    newProduct: 'RECENTMENT AFEGIT',
    newSearch: 'Nova Cerca: ',
    nextStep: 'Pas Següent',
    noArticles: 'Actualment no hi ha articles',
    noResultsFound: 'PD: Només puc oferir suggeriments sobre productes o categories comercialitzables.',
    noSearchResult: 'Continueu descrivint el producte que busqueu i premeu Enter.',
    officialWebsite: 'lloc web oficial',
    orderNow: 'Demanar ara',
    browseOnAmazon: 'Navegar per Amazon',
    searchNow: 'Cercar ara',
    ourMission: 'La Nostra Missió',
    overviewTitle: 'Detalls',
    askAQuestion: 'Tens una pregunta?',
    askSentHint: 'Escriu-la a dalt',
    micBlocked: 'El micròfon està bloquejat. Permet-lo a la configuració del navegador.',
    pageReference: 'En aquesta pàgina',
    buyNow: 'Comprar ara',
    publishedOn: 'Publicat el',
    publishingOn: 'Publicació el',
    quickMenu: 'Resum de les millors seleccions',
    recentBlogs: 'Notícies de PLACEHOLDER',
    regionPrompt: {
      switchQuestion: {},
      yesSwitch: 'Sí, canviar de regió',
      noThanks: 'No, gràcies',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'Descobrir Més',
    requirements: 'Requisits',
    responsibilities: 'Responsabilitats',
    reviewSummary: 'Resum de ressenyes',
    xReviews: '{count} ressenyes',
    review: 'Ressenya',
    searching: 'Cercant ',
    searchingProduct: 'Trobant les millors alternatives per a ',
    sidemenuListTitle: 'Les Nostres Millors Opcions',
    slogan: `Responem davant teu, no davant els anunciants.`,
    submit: 'Enviar',
    technicalSpecs: 'Especificacions tècniques',
    nutritionFacts: 'Informació nutricional',
    ingredients: 'Ingredients',
    materialFabric: 'Material i teixit',
    dimensionsSize: 'Dimensions i mida',
    performanceMetrics: 'Mètriques de rendiment',
    safetyFeatures: 'Característiques de seguretat',
    compatibility: 'Compatibilitat',
    careInstructions: 'Instruccions de cura',
    warrantySupport: 'Garantia i suport',
    timeUiStrings: {
      noTimeAvailable: 'Cap hora disponible',
      inTheFuture: 'En el futur',
      justNow: 'ara mateix',
      oneMinuteAgo: 'fa 1 minut',
      xMinutesAgo: 'fa {x} minuts',
      over30Minutes: 'fa més de 30 minuts',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Notícies d\'Inception',
    trademarkPolicy: 'Política de marques',
    trademarkPolicyContent: [
      `'Inception' és una marca registrada d'InceptionAI Inc.`,
      'Altres marques que apareixen en aquest lloc web són propietat dels seus respectius titulars.',
      'Per a preguntes sobre marques, contacteu amb:',
    ],
    tryAgain: 'Tornar-ho a provar',
    uploadCV: 'Pujar CV',
    viewDetails: 'Veure detalls',
    viewMore: 'Mostrar més',
    welcome: 'Benvinguts a Inception',
    linkInput: {
      smartExtract: 'Extracció intel·ligent:',
      apply: 'Aplicar',
      pasteHelper: 'Enganxeu l\'URL complet per a l\'extracció automàtica del nom d\'usuari',
      placeholderDefault: '---',
    },
    privacyPolicy: 'Política de Privadesa',
    affiliateDisclosure: 'Com a associat d\'Amazon i soci afiliat, Inception obté ingressos per compres que compleixen els requisits. Això no influeix en les nostres classificacions.',
    articlesIntro: 'Els articles d\'Inception ofereixen guies de compra i anàlisis de productes elaborades amb IA per ajudar-vos a prendre decisions de compra informades.',
    fallbackNotice: 'La pàgina específica que busqueu no s\'ha trobat, però us mostrem contingut relacionat.',
    aboutInceptionAi:
      `Inception troba el millor producte per a tu {region}, amb una IA que respon davant teu, no davant els anunciants.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'al Canadà',
      [Region.USA]: 'als Estats Units',
      [Region.France]: 'a França',
      [Region.UK]: 'al Regne Unit',
      [Region.Australia]: 'a Austràlia',
      [Region.All]: 'a la vostra regió',
    },
  },
  // Galician
  gl: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Prezo en Amazon · {time}',
    aboutTitle: 'O esencial:',
    aboutUs: 'Sobre nós',
    vision: 'A nosa visión',
    aboutUsTitle: 'Que é Inception?',
    ageVerification: {
      title: 'Verificación de idade requirida',
      subtitle: 'Aviso de contido para adultos',
      warning: 'Este sitio web contén contido para adultos e é só para persoas de 18 anos ou máis.',
      disclaimer:
        'Ao entrar neste sitio, vostede confirma que ten a idade legal na súa xurisdición e que desexa ver contido para adultos.',
      confirmAge: 'Confirmo que teño 18 anos ou máis',
      enterButton: 'Entrar no sitio',
      exitButton: 'Saír',
      mustBe18: 'Debe ter 18 anos ou máis para acceder a este contido',
      adultContentWarning: 'Este sitio contén contido explícito para adultos destinado unicamente a público maduro.',
      euCompliance: 'Esta verificación cumpre coa Lei de Servizos Dixitais da UE e os requisitos nacionais de verificación de idade.',
    },
    alternativeLabel: '*mellor alternativa que atopamos',
    at: 'en',
    averagePriceTag: 'Media: ',
    best: 'Mellor PLACEHOLDER',
    bestFeatures: 'Que fai que un produto sexa o mellor na súa categoría?',
    articles: 'Artigos',
    backToHome: 'Inicio',
    breadcrumbHome: 'Inicio',
    goBack: 'Atrás',
    by: 'por',
    cancel: 'Cancelar',
    methodology: 'Como escollemos',
    partners: 'Socios',
    careers: 'Carreiras',
    changeRegion: 'Cambiar rexión',
    shoppingRegionLabel: 'Rexión de compra: {region}',
    choosePrice: 'Mentres tanto, pode escoller un rango de prezos que lle conveña.',
    comingSoon: 'Proximamente',
    conclusion: 'Conclusión',
    cookieConsent: {
      title: 'Valoramos a súa privacidade',
      description: 'Usamos cookies para mellorar a súa experiencia e ofrecer mellores recomendacións de produtos. Os seus datos permanecen privados e anónimos.',
      essential: 'Cookies esenciais',
      analytics: 'Cookies analíticas',
      advertising: 'Cookies publicitarias',
      learnMore: 'Máis información',
      acceptAll: 'Aceptar e continuar',
      rejectAll: 'Rexeitar',
      customizeSettings: 'Personalizar',
      savePreferences: 'Gardar preferencias',
      essentialDescription: 'Necesarias para que o sitio funcione correctamente. Non se poden desactivar.',
      analyticsDescription: 'Axúdannos a comprender como os visitantes interactúan co noso sitio recollendo datos anónimos.',
      advertisingDescription: 'Utilizadas para mostrar anuncios relevantes mediante Google AdSense. Só se cargan co seu consentimento.',
    },
    contactUs: 'Contacte connosco',
    contactFormDescription: 'Por favor, encha o formulario a continuación.',
    defaultMetadata: {
      title: `Inception | O mellor produto para ti, atopado por unha IA honesta`,
      description:
        `O mellor produto para ti xa existe. A nosa IA atópao, porque respondemos ante ti, non ante os anunciantes. Inception ofréceche recomendacións de produtos investigadas con IA para a túa rexión, sen posicións patrocinadas.`,
    },
    descriptionArticle:
      'A nosa misión é transformar a forma en que as empresas melloran a súa presenza en liña permitíndolles ofrecer contido de calidade GRATIS aos seus clientes.',
    describeDemoCategory:
      'Aquí definiremos as características e calidades que fan dun produto un líder nesta categoría.',
    describeDemoComparison:
      'Aquí definiremos as características que fan dun produto un líder para crear unha lista sinxela dos mellores nomes de produtos.',
    directlySearch: 'Buscar directamente',
    disconnect: 'Desconectar',
    emailPlaceholder: 'Introduza o seu correo electrónico',
    emailTitle: 'Correo electrónico',
    example: 'Exemplo',
    phoneTitle: 'Número de teléfono',
    exploreMore: 'Explorar máis',
    existingPages: 'Visite páxinas existentes que poidan axudar',
    articlesOtherRegions: 'Das nosas outras rexións',
    firstPostComingSoon: `A súa primeira publicación chegará pronto, manterémollo informado!\nMentres tanto, pode editar o contido a continuación se o desexa.`,
    joinUs: 'Únase a nós.',
    contactForm: {
      personalInformation: 'Información Persoal',
      yourMessage: 'A Súa Mensaxe',
      firstName: 'Nome',
      firstNamePlaceholder: 'Introduza o seu nome',
      emailAddress: 'Enderezo de Correo Electrónico',
      emailPlaceholder: 'Introduza o seu enderezo de correo electrónico',
      phoneNumber: 'Número de Teléfono',
      phonePlaceholder: 'Introduza o seu número de teléfono',
      company: 'Empresa/Organización',
      companyPlaceholder: 'Introduza o nome da súa empresa',
      message: 'Cóntenos sobre o seu proxecto ou consulta',
      messagePlaceholder: 'Comparta os detalles do seu proxecto, requisitos ou calquera pregunta...',
      sendMessage: 'Enviar Mensaxe',
      sending: 'Enviando...',
      successTitle: 'Mensaxe Enviada Correctamente!',
      successMessage: 'Grazas pola súa mensaxe! Contactaremos pronto.',
      processingMessage: 'Procesando a súa solicitude...',
      errorTitle: 'Erro ao Enviar a Mensaxe',
      errorMessage: 'Produciuse un erro ao enviar a súa mensaxe. Por favor, ténteo de novo.',
    },
    loading: 'Cargando...',
    localeDisclaimer: 'Só X',
    login: 'Iniciar sesión',
    lookingFor: 'Que busca?',
    profile: 'Perfil',
    otherLanguages: 'Outros Idiomas',
    showLess: 'Mostrar Menos',
    showMoreLanguages: 'Mostrar Máis Idiomas',
    messageTitle: 'Mensaxe',
    mission: `O mellor produto para ti xa existe. A nosa IA atópao, porque respondemos ante ti, non ante os anunciantes.`,
    namePlaceholder: 'Introduza o seu nome',
    nameTitle: 'Nome',
    newProduct: 'RECÉN ENGADIDO',
    newSearch: 'Nova Busca: ',
    nextStep: 'Seguinte Paso',
    noArticles: 'Actualmente non hai artigos',
    noResultsFound: 'PD: Só podo ofrecer suxestións sobre produtos ou categorías comercializables.',
    noSearchResult: 'Siga describindo o produto que busca e prema Enter.',
    officialWebsite: 'sitio web oficial',
    orderNow: 'Pedir agora',
    browseOnAmazon: 'Navegar en Amazon',
    searchNow: 'Buscar agora',
    ourMission: 'A Nosa Misión',
    overviewTitle: 'Detalles',
    askAQuestion: 'Tes unha pregunta?',
    askSentHint: 'Escríbea arriba',
    micBlocked: 'O micrófono está bloqueado. Permíteo na configuración do navegador.',
    pageReference: 'Nesta páxina',
    buyNow: 'Comprar agora',
    publishedOn: 'Publicado o',
    publishingOn: 'Publicación o',
    quickMenu: 'Resumo das mellores seleccións',
    recentBlogs: 'Noticias de PLACEHOLDER',
    regionPrompt: {
      switchQuestion: {},
      yesSwitch: 'Si, cambiar rexión',
      noThanks: 'Non, grazas',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'Descubrir Máis',
    requirements: 'Requisitos',
    responsibilities: 'Responsabilidades',
    reviewSummary: 'Resumo de recensións',
    xReviews: '{count} recensións',
    review: 'Recensión',
    searching: 'Buscando ',
    searchingProduct: 'Atopando as mellores alternativas para ',
    sidemenuListTitle: 'As Nosas Mellores Opcións',
    slogan: `Respondemos ante ti, non ante os anunciantes.`,
    submit: 'Enviar',
    technicalSpecs: 'Especificacións técnicas',
    nutritionFacts: 'Información nutricional',
    ingredients: 'Ingredientes',
    materialFabric: 'Material e tecido',
    dimensionsSize: 'Dimensións e tamaño',
    performanceMetrics: 'Métricas de rendemento',
    safetyFeatures: 'Características de seguridade',
    compatibility: 'Compatibilidade',
    careInstructions: 'Instrucións de coidado',
    warrantySupport: 'Garantía e soporte',
    timeUiStrings: {
      noTimeAvailable: 'Non hai tempo dispoñible',
      inTheFuture: 'No futuro',
      justNow: 'agora mesmo',
      oneMinuteAgo: 'hai 1 minuto',
      xMinutesAgo: 'hai {x} minutos',
      over30Minutes: 'hai máis de 30 minutos',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Noticias de Inception',
    trademarkPolicy: 'Política de marcas',
    trademarkPolicyContent: [
      `'Inception' é unha marca rexistrada de InceptionAI Inc.`,
      'Outras marcas que aparecen neste sitio web son propiedade dos seus respectivos titulares.',
      'Para preguntas sobre marcas, contacte con:',
    ],
    tryAgain: 'Tentar de novo',
    uploadCV: 'Subir CV',
    viewDetails: 'Ver detalles',
    viewMore: 'Mostrar máis',
    welcome: 'Benvido a Inception',
    linkInput: {
      smartExtract: 'Extracción intelixente:',
      apply: 'Aplicar',
      pasteHelper: 'Pegue o URL completo para a extracción automática do nome de usuario',
      placeholderDefault: '---',
    },
    privacyPolicy: 'Política de Privacidade',
    affiliateDisclosure: 'Como asociado de Amazon e socio afiliado, Inception obtén ingresos por compras cualificadas. Isto non inflúe nas nosas clasificacións.',
    articlesIntro: 'Os artigos de Inception ofrecen guías de compra e análises de produtos elaboradas con IA para axudarlle a tomar decisións de compra informadas.',
    fallbackNotice: 'A páxina específica que busca non se atopou, pero estámoslle amosando contido relacionado.',
    aboutInceptionAi:
      `Inception atopa o mellor produto para ti {region}, cunha IA que responde ante ti, non ante os anunciantes.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'en Canadá',
      [Region.USA]: 'nos Estados Unidos',
      [Region.France]: 'en Francia',
      [Region.UK]: 'no Reino Unido',
      [Region.Australia]: 'en Australia',
      [Region.All]: 'na súa rexión',
    },
  },
  // Basque
  eu: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Amazoneko prezioa · {time}',
    aboutTitle: 'Oinarrizkoak:',
    aboutUs: 'Guri buruz',
    vision: 'Gure ikuspegia',
    aboutUsTitle: 'Zer da Inception?',
    ageVerification: {
      title: 'Adin-egiaztapena beharrezkoa',
      subtitle: 'Helduentzako edukiaren ohartarazpena',
      warning: 'Webgune honek helduentzako edukia du eta 18 urtetik gorakoei bakarrik dago zuzenduta.',
      disclaimer:
        'Gune honetan sartuz, baieztatzen duzu zure jurisdikzioan legezko adina duzula eta helduentzako edukia ikusi nahi duzula.',
      confirmAge: 'Baieztatzen dut 18 urte edo gehiago ditudala',
      enterButton: 'Gunean sartu',
      exitButton: 'Irten',
      mustBe18: '18 urtetik gorakoa izan behar duzu eduki honetara sartzeko',
      adultContentWarning: 'Gune honek helduentzako eduki esplizitua du, publiko heldu batentzat soilik.',
      euCompliance: 'Egiaztapen honek EBko Zerbitzu Digitalen Legea eta adin-egiaztapeneko baldintza nazionalak betetzen ditu.',
    },
    alternativeLabel: '*aurkitu dugun alternatiba onena',
    at: '-(e)n',
    averagePriceTag: 'Batez bestekoa: ',
    best: 'Onena PLACEHOLDER',
    bestFeatures: 'Zerk egiten du produktu bat bere kategoriako onena?',
    articles: 'Artikuluak',
    backToHome: 'Hasierara',
    breadcrumbHome: 'Hasiera',
    goBack: 'Atzera',
    by: '-(e)k',
    cancel: 'Utzi',
    methodology: 'Nola aukeratzen dugun',
    partners: 'Bazkideak',
    careers: 'Lanbideak',
    changeRegion: 'Eskualdea aldatu',
    choosePrice: 'Bitartean, zuri egokitzen zaizun prezio-tartea aukeratu dezakezu.',
    comingSoon: 'Laster',
    conclusion: 'Ondorioa',
    cookieConsent: {
      title: 'Zure pribatutasuna errespetatzen dugu',
      description: 'Cookieak erabiltzen ditugu zure esperientzia hobetzeko eta produktu-gomendio hobeak eskaintzeko. Zure datuak pribatuak eta anonimoak izaten jarraitzen dute.',
      essential: 'Cookie beharrezkoak',
      analytics: 'Analitika cookieak',
      advertising: 'Publizitate cookieak',
      learnMore: 'Gehiago jakin',
      acceptAll: 'Onartu eta jarraitu',
      rejectAll: 'Ukatu',
      customizeSettings: 'Pertsonalizatu',
      savePreferences: 'Hobespenak gorde',
      essentialDescription: 'Webguneak behar bezala funtzionatzeko beharrezkoak. Ezin dira desaktibatu.',
      analyticsDescription: 'Bisitariek gure webgunearekin nola elkarreragiten duten ulertzen laguntzen digute datu anonimoak bilduz.',
      advertisingDescription: 'Google AdSense bidez iragarki garrantzitsuak erakusteko erabiltzen dira. Zure baimenarekin soilik kargatzen dira.',
    },
    contactUs: 'Gurekin harremanetan jarri',
    contactFormDescription: 'Mesedez, bete beheko formularioa.',
    defaultMetadata: {
      title: `Inception | Zuretzako produkturik onena, AA zintzo batek aurkitua`,
      description:
        `Zuretzako produkturik onena badago jada. Gure AAk aurkitzen du, zuri erantzuten dizugulako, ez iragarleei. Inception-k zure eskualderako produktu-gomendio zintzoak eskaintzen dizkizu.`,
    },
    descriptionArticle:
      'Gure misioa enpresek beren lineako presentzia hobetzen duten modua eraldatzea da, bezeroei DOAN kalitatezko edukia eskaintzeko aukera emanez.',
    describeDemoCategory:
      'Hemen kategoria honetako produktu bat lider egiten duten ezaugarriak eta kalitateak zehaztuko ditugu.',
    describeDemoComparison:
      'Hemen produktu bat lider egiten duten ezaugarriak zehaztuko ditugu, zure bilaketa-irizpideetan oinarritutako produktu-izen onenen zerrenda sinple bat sortzeko.',
    directlySearch: 'Zuzenean bilatu',
    disconnect: 'Deskonektatu',
    emailPlaceholder: 'Sartu zure posta elektronikoa',
    emailTitle: 'Posta elektronikoa',
    example: 'Adibidea',
    phoneTitle: 'Telefono-zenbakia',
    exploreMore: 'Gehiago esploratu',
    existingPages: 'Bisitatu lagun dezaketen dauden orrialdeak',
    articlesOtherRegions: 'Gure beste eskualdeetatik',
    firstPostComingSoon: `Zure lehen argitalpena laster iritsiko da, jakinaraziko dizugu!\nBitartean, beheko edukia editatu dezakezu nahi baduzu.`,
    joinUs: 'Bat egin gurekin.',
    contactForm: {
      personalInformation: 'Informazio Pertsonala',
      yourMessage: 'Zure Mezua',
      firstName: 'Izena',
      firstNamePlaceholder: 'Sartu zure izena',
      emailAddress: 'Posta Elektroniko Helbidea',
      emailPlaceholder: 'Sartu zure posta elektroniko helbidea',
      phoneNumber: 'Telefono-zenbakia',
      phonePlaceholder: 'Sartu zure telefono-zenbakia',
      company: 'Enpresa/Erakundea',
      companyPlaceholder: 'Sartu zure enpresaren izena',
      message: 'Kontatu zure proiektuari edo kontsultari buruz',
      messagePlaceholder: 'Partekatu zure proiektuaren xehetasunak, eskakizunak edo galderak...',
      sendMessage: 'Mezua Bidali',
      sending: 'Bidaltzen...',
      successTitle: 'Mezua Ondo Bidali da!',
      successMessage: 'Eskerrik asko zure mezuagatik! Laster jarriko gara zurekin harremanetan.',
      processingMessage: 'Zure eskaera prozesatzen...',
      errorTitle: 'Errorea Mezua Bidaltzean',
      errorMessage: 'Errore bat gertatu da zure mezua bidaltzean. Mesedez, saiatu berriro.',
    },
    loading: 'Kargatzen...',
    localeDisclaimer: 'X bakarrik',
    login: 'Saioa hasi',
    lookingFor: 'Zer bilatzen duzu?',
    profile: 'Profila',
    otherLanguages: 'Beste Hizkuntzak',
    showLess: 'Gutxiago erakutsi',
    showMoreLanguages: 'Hizkuntza gehiago erakutsi',
    messageTitle: 'Mezua',
    mission: `Zuretzako produkturik onena badago jada. Gure AAk aurkitzen du, zuri erantzuten dizugulako, ez iragarleei.`,
    namePlaceholder: 'Sartu zure izena',
    nameTitle: 'Izena',
    newProduct: 'BERRIA',
    newSearch: 'Bilaketa Berria: ',
    nextStep: 'Hurrengo Urratsa',
    noArticles: 'Une honetan ez dago artikulurik',
    noResultsFound: 'Oh. Merkaturatzeko produktu edo kategoriei buruzko iradokizunak soilik eman ditzaket.',
    noSearchResult: 'Bilatzen ari zaren produktua deskribatzen jarraitu eta sakatu Enter.',
    officialWebsite: 'webgune ofiziala',
    orderNow: 'Orain eskatu',
    browseOnAmazon: 'Amazon-en arakatu',
    searchNow: 'Orain bilatu',
    ourMission: 'Gure Misioa',
    overviewTitle: 'Xehetasunak',
    askAQuestion: 'Galderarik duzu?',
    askSentHint: 'Idatzi goian',
    pageReference: 'Orrialde honetan',
    buyNow: 'Orain erosi',
    publishedOn: 'Argitaratua',
    publishingOn: 'Argitaratze-data',
    quickMenu: 'Hautapen onenen laburpena',
    recentBlogs: 'PLACEHOLDER Albisteak',
    regionPrompt: {
      switchQuestion: {},
      yesSwitch: 'Bai, eskualdea aldatu',
      noThanks: 'Ez, eskerrik asko',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'Gehiago Aurkitu',
    requirements: 'Eskakizunak',
    responsibilities: 'Erantzukizunak',
    reviewSummary: 'Ebaluazioen laburpena',
    xReviews: '{count} ebaluazio',
    review: 'Ebaluazioa',
    searching: 'Bilatzen ',
    searchingProduct: 'Alternatiba onenak bilatzen ',
    sidemenuListTitle: 'Gure Hautapen Onenak',
    slogan: `Zuri erantzuten dizugu, ez iragarleei.`,
    submit: 'Bidali',
    technicalSpecs: 'Zehaztapen teknikoak',
    nutritionFacts: 'Elikadura-informazioa',
    ingredients: 'Osagaiak',
    materialFabric: 'Materiala eta ehuna',
    dimensionsSize: 'Neurriak eta tamaina',
    performanceMetrics: 'Errendimendu-neurriak',
    safetyFeatures: 'Segurtasun-ezaugarriak',
    compatibility: 'Bateragarritasuna',
    careInstructions: 'Zaintza-argibideak',
    warrantySupport: 'Bermea eta laguntza',
    timeUiStrings: {
      noTimeAvailable: 'Denborarik ez dago eskuragarri',
      inTheFuture: 'Etorkizunean',
      justNow: 'oraintxe',
      oneMinuteAgo: 'duela minutu 1',
      xMinutesAgo: 'duela {x} minutu',
      over30Minutes: 'duela 30 minutu baino gehiago',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Inception Albisteak',
    trademarkPolicy: 'Marka-politika',
    trademarkPolicyContent: [
      `'Inception' InceptionAI Inc.-en marka erregistratua da, behar bezala erregistratua eta betearazia Kanadan eta beste hainbat jurisdikziotan.`,
      'Webgune honetan agertzen diren beste marka batzuk dagokien jabeen jabetza dira.',
      'Marken inguruko galderetarako, jarri harremanetan honekin:',
    ],
    tryAgain: 'Berriro saiatu',
    uploadCV: 'CV-a igo',
    viewDetails: 'Xehetasunak ikusi',
    viewMore: 'Gehiago erakutsi',
    welcome: 'Ongi etorri Inception-ra',
    linkInput: {
      smartExtract: 'Erauzketa adimentsua:',
      apply: 'Aplikatu',
      pasteHelper: 'Itsatsi URL osoa erabiltzaile-izena automatikoki erauzteko',
      placeholderDefault: '---',
    },
    privacyPolicy: 'Pribatutasun Politika',
    affiliateDisclosure: 'Amazon-en kide eta afiliatu gisa, Inception-k erosketa kualifikatuetatik irabazten du. Honek ez du gure sailkapenean eragiten.',
    articlesIntro: 'Inception-ren artikuluek erosketa-gidak eta produktu-analisi sakonak eskaintzen dituzte erosketa-erabaki informatuak hartzen laguntzeko.',
    fallbackNotice: 'Bilatzen ari zaren orrialde zehatza ez da aurkitu, baina eduki erlazionatua erakusten dizugu.',
    aboutInceptionAi:
      `Inception-k zuretzako produkturik onena aurkitzen du {region}, zuri erantzuten dizun AA batekin, ez iragarleei.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'Kanadan',
      [Region.USA]: 'AEBetan',
      [Region.France]: 'Frantzian',
      [Region.UK]: 'Erresuma Batuan',
      [Region.Australia]: 'Australian',
      [Region.All]: 'zure eskualdean',
    },
  },
  // Romansh
  rm: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Pretsch Amazon · {time}',
    aboutTitle: 'L\'essenzial:',
    aboutUs: 'Davart nus',
    vision: 'Nossa visiun',
    aboutUsTitle: 'Tge è Inception?',
    ageVerification: {
      title: 'Verificaziun da vegliadetgna necessaria',
      subtitle: 'Avertiment da cuntegn per adults',
      warning: 'Quest website cuntegna cuntegn per adults ed è mo per persunas da 18 onns u pli veglias.',
      disclaimer:
        'Cun entrar en quest lieu, Vus confirmais che Vus essas da vegliadetgna legala en Vossa jurisdicziun e che Vus vulais vesair cuntegn per adults.',
      confirmAge: 'Jau confirm che jau hai 18 onns u pli',
      enterButton: 'Entrar en il lieu',
      exitButton: 'Bandunar',
      mustBe18: 'Vus stuais avair 18+ per acceder a quest cuntegn',
      adultContentWarning: 'Quest lieu cuntegna cuntegn explicit per adults mo per in public adult.',
      euCompliance: 'Questa verificaziun correspunda a la Lescha davart ils servetschs digitals da l\'UE e las pretensiuns naziunalas da verificaziun da vegliadetgna.',
    },
    alternativeLabel: '*la meglra alternativa che nus avain chattà',
    at: 'tar',
    averagePriceTag: 'Media: ',
    best: 'Meglier PLACEHOLDER',
    bestFeatures: 'Tge fa in product il meglier en sia categoria?',
    articles: 'Artitgels',
    backToHome: 'A chasa',
    breadcrumbHome: 'Pagina iniziala',
    goBack: 'Enavos',
    by: 'da',
    cancel: 'Interrumper',
    methodology: 'Co nus tschernain',
    partners: 'Partenaris',
    careers: 'Carriera',
    changeRegion: 'Midar la regiun',
    choosePrice: 'En il fratemp pudais Vus tscherner ina classa da pretsch che Vus cunvegna.',
    comingSoon: 'Vegn prest',
    conclusion: 'Conclusiun',
    cookieConsent: {
      title: 'Nus respectain Vossa sfera privata',
      description: 'Nus utilisain cookies per meglierar Vossa experientscha e per purschir meglras recumandaziuns da products. Voss datas restan privats ed anonims.',
      essential: 'Cookies necessaris',
      analytics: 'Cookies analytics',
      advertising: 'Cookies da reclama',
      learnMore: 'Ulteriuras infurmaziuns',
      acceptAll: 'Acceptar e cuntinuar',
      rejectAll: 'Refusar',
      customizeSettings: 'Persunalisar',
      savePreferences: 'Memorisar las preferenzas',
      essentialDescription: 'Necessaris per il funcziunament correct dal website. Na pon betg vegnir deactivads.',
      analyticsDescription: 'Ans gidan a chapir co ils visitaders interagian cun noss website cun rimnar datas anonimas.',
      advertisingDescription: 'Vegnan utilisads per mussar reclamas relevantas via Google AdSense. Vegnan mo chargiads cun Voss consentiment.',
    },
    contactUs: 'Contactai nus',
    contactFormDescription: 'Emplenì per plaschair il formular sutvart.',
    defaultMetadata: {
      title: `Inception | Il meglier product per tai, chattà d'ina IA onesta`,
      description:
        `Il meglier product per tai exista gia. Nossa IA al chatta, perquai che nus essan responsabels envers tai, betg envers ils reclamists. Inception As porscha recumandaziuns da products onestas per Vossa regiun.`,
    },
    descriptionArticle:
      'Nossa missiun è da transformar il mod co fatschendas megliereschan lur preschientscha online, lubind ad ellas d\'offrir cuntegn da qualitad GRATUIT als clients.',
    describeDemoCategory:
      'Qua definirain nus las characteristicas e qualitads che fan in product in manader en questa categoria.',
    describeDemoComparison:
      'Qua definirain nus las characteristicas che fan in product in manader per crear ina glista simpla dals megliers nums da products.',
    directlySearch: 'Tschertgar directamain',
    disconnect: 'Deconnectar',
    emailPlaceholder: 'Endatai Vossa adressa dad e-mail',
    emailTitle: 'E-mail',
    example: 'Exempel',
    phoneTitle: 'Numer da telefon',
    exploreMore: 'Explorar dapli',
    existingPages: 'Visitai paginas existentas che pudessan gidar',
    articlesOtherRegions: 'Da nossas autras regiuns',
    firstPostComingSoon: `Voss emprim post vegn prest, nus As tegnan al current!\nEn il fratemp pudais Vus modifitgar il cuntegn sutvart sch\'i As plascha.`,
    joinUs: 'Associai Vus a nus.',
    contactForm: {
      personalInformation: 'Infurmaziuns persunalas',
      yourMessage: 'Voss messadi',
      firstName: 'Prenum',
      firstNamePlaceholder: 'Endatai Voss prenum',
      emailAddress: 'Adressa dad e-mail',
      emailPlaceholder: 'Endatai Vossa adressa dad e-mail',
      phoneNumber: 'Numer da telefon',
      phonePlaceholder: 'Endatai Voss numer da telefon',
      company: 'Fatschenda/Organisaziun',
      companyPlaceholder: 'Endatai il num da Vossa fatschenda',
      message: 'Quintai a nus da Voss project u da Vossa dumonda',
      messagePlaceholder: 'Cundividì ils detagls da Voss project, las pretensiuns u dumondas...',
      sendMessage: 'Trametter messadi',
      sending: 'Trametter...',
      successTitle: 'Messadi tramess cun success!',
      successMessage: 'Engraztgel per Voss messadi! Nus As contactain prest.',
      processingMessage: 'Elavuraziun da Vossa dumonda...',
      errorTitle: 'Errur cun trametter il messadi',
      errorMessage: 'I è capità in\'errur cun trametter Voss messadi. Empruvai anc ina giada.',
    },
    loading: 'Chargiar...',
    localeDisclaimer: 'Mo X',
    login: 'S\'annunziar',
    lookingFor: 'Tschertgais Vus?',
    profile: 'Profil',
    otherLanguages: 'Autras linguas',
    showLess: 'Mussar pli pauc',
    showMoreLanguages: 'Mussar dapli linguas',
    messageTitle: 'Messadi',
    mission: `Il meglier product per tai exista gia. Nossa IA al chatta, perquai che nus essan responsabels envers tai, betg envers ils reclamists.`,
    namePlaceholder: 'Endatai Voss num',
    nameTitle: 'Num',
    newProduct: 'NOVAMAIN AGIUNTÀ',
    newSearch: 'Nova tschertga: ',
    nextStep: 'Proxim pass',
    noArticles: 'I na dat actualmain nagins artitgels',
    noResultsFound: 'PS: Jau poi mo dar propostas davart products u categorias commercialisablas.',
    noSearchResult: 'Cuntinuai a descriver il product che Vus tschertgais e smatgai Enter.',
    officialWebsite: 'website uffizial',
    orderNow: 'Ordinar uss',
    browseOnAmazon: 'Navigar sin Amazon',
    searchNow: 'Tschertgar uss',
    ourMission: 'Nossa missiun',
    overviewTitle: 'Detagls',
    askAQuestion: 'Has ti ina dumonda?',
    askSentHint: 'Scriva sura',
    pageReference: 'Sin questa pagina',
    buyNow: 'Cumprar uss',
    publishedOn: 'Publitgà ils',
    publishingOn: 'Publicaziun ils',
    quickMenu: 'Resumaziun dals megliers tscherns',
    recentBlogs: 'Novitads PLACEHOLDER',
    regionPrompt: {
      switchQuestion: {},
      yesSwitch: 'Gea, midar la regiun',
      noThanks: 'Na, grazia',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'Scuvrir dapli',
    requirements: 'Pretensiuns',
    responsibilities: 'Responsabladads',
    reviewSummary: 'Resumaziun da las recensiuns',
    xReviews: '{count} recensiuns',
    review: 'Recensiun',
    searching: 'Tschertgar ',
    searchingProduct: 'Chattar las meglras alternativas per ',
    sidemenuListTitle: 'Noss megliers tscherns',
    slogan: `Nus essan responsabels envers tai, betg envers ils reclamists.`,
    submit: 'Trametter',
    technicalSpecs: 'Specificaziuns tecnicas',
    nutritionFacts: 'Valurs nutritivals',
    ingredients: 'Ingredients',
    materialFabric: 'Material e taissel',
    dimensionsSize: 'Dimensiuns e grondezza',
    performanceMetrics: 'Indicaturs da prestaziun',
    safetyFeatures: 'Caracteristicas da segirezza',
    compatibility: 'Cumpatiblitad',
    careInstructions: 'Instrucziuns per il tractament',
    warrantySupport: 'Garanzia e sustegn',
    timeUiStrings: {
      noTimeAvailable: 'Nagin temp disponibel',
      inTheFuture: 'En l\'avegnir',
      justNow: 'ussa gist',
      oneMinuteAgo: 'avant 1 minuta',
      xMinutesAgo: 'avant {x} minutas',
      over30Minutes: 'avant dapli che 30 minutas',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Novitads Inception',
    trademarkPolicy: 'Politica da marcas',
    trademarkPolicyContent: [
      `'Inception' è ina marca registrada da InceptionAI Inc.`,
      'Autras marcas che cumparan sin quest website èn proprietad dals respectivs proprietaris.',
      'Per dumondas davart marcas, contactai:',
    ],
    tryAgain: 'Empruvar anc ina giada',
    uploadCV: 'Chargiar si CV',
    viewDetails: 'Vesair detagls',
    viewMore: 'Mussar dapli',
    welcome: 'Bainvegni tar Inception',
    linkInput: {
      smartExtract: 'Extracziun intelligenta:',
      apply: 'Applitgar',
      pasteHelper: 'Enculai l\'URL cumpletta per l\'extracziun automatica dal num d\'utilisader',
      placeholderDefault: '---',
    },
    privacyPolicy: 'Politica da protecziun da datas',
    affiliateDisclosure: 'Sco associà dad Amazon e partenari affilià, Inception gudogna da cumpras qualifitgadas. Quai n\'influenzescha betg nossas classificaziuns.',
    articlesIntro: 'Ils artitgels dad Inception ofran guidas da cumpra e analisas profundas da products per gidar Vus tar decisiuns da cumpra infurmadas.',
    fallbackNotice: 'La pagina specifica che Vus tschertgais n\'è betg vegnida chattada, ma nus mussain cuntegn collià.',
    aboutInceptionAi:
      `Inception chatta il meglier product per tai {region}, cun ina IA ch'è responsabla envers tai, betg envers ils reclamists.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'en il Canada',
      [Region.USA]: 'en ils Stadis Unids',
      [Region.France]: 'en Frantscha',
      [Region.UK]: 'en il Reginavel Unì',
      [Region.Australia]: 'en l\'Australia',
      [Region.All]: 'en Vossa regiun',
    },
  },
  // Bengali
  bn: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Amazon মূল্য · {time}',
    aboutTitle: 'অপরিহার্য বিষয়গুলি:',
    aboutUs: 'আমাদের সম্পর্কে',
    vision: 'আমাদের ভিশন',
    aboutUsTitle: 'Inception কী?',
    ageVerification: {
      title: 'বয়স যাচাই প্রয়োজন',
      subtitle: 'প্রাপ্তবয়স্ক বিষয়বস্তুর সতর্কতা',
      warning: 'এই ওয়েবসাইটে প্রাপ্তবয়স্ক বিষয়বস্তু রয়েছে এবং এটি শুধুমাত্র ১৮ বছর বা তার বেশি বয়সী ব্যক্তিদের জন্য।',
      disclaimer:
        'এই সাইটে প্রবেশ করে, আপনি নিশ্চিত করছেন যে আপনি আপনার এখতিয়ারে আইনি বয়সের এবং প্রাপ্তবয়স্ক বিষয়বস্তু দেখতে চান।',
      confirmAge: 'আমি নিশ্চিত করছি যে আমার বয়স ১৮ বছর বা তার বেশি',
      enterButton: 'সাইটে প্রবেশ করুন',
      exitButton: 'প্রস্থান করুন',
      mustBe18: 'এই বিষয়বস্তু অ্যাক্সেস করতে আপনাকে অবশ্যই ১৮+ হতে হবে',
      adultContentWarning: 'এই সাইটে শুধুমাত্র পরিণত দর্শকদের জন্য স্পষ্ট প্রাপ্তবয়স্ক বিষয়বস্তু রয়েছে।',
      euCompliance: 'এই যাচাই EU ডিজিটাল সেবা আইন এবং জাতীয় বয়স যাচাই প্রয়োজনীয়তার সাথে সঙ্গতিপূর্ণ।',
    },
    alternativeLabel: '*আমরা যে সেরা বিকল্পটি খুঁজে পেয়েছি',
    at: '-এ',
    averagePriceTag: 'গড়: ',
    best: 'সেরা PLACEHOLDER',
    bestFeatures: 'কী একটি পণ্যকে তার বিভাগে সেরা করে তোলে?',
    articles: 'প্রবন্ধ',
    backToHome: 'হোমে যান',
    breadcrumbHome: 'হোম',
    goBack: 'ফিরে যান',
    by: 'দ্বারা',
    cancel: 'বাতিল করুন',
    methodology: 'আমরা কীভাবে বাছাই করি',
    partners: 'পার্টনার',
    careers: 'ক্যারিয়ার',
    changeRegion: 'অঞ্চল পরিবর্তন করুন',
    choosePrice: 'এই সময়ে, আপনি আপনার জন্য উপযুক্ত একটি মূল্য সীমা বেছে নিতে পারেন।',
    comingSoon: 'শীঘ্রই আসছে',
    conclusion: 'উপসংহার',
    cookieConsent: {
      title: 'আমরা আপনার গোপনীয়তাকে মূল্য দিই',
      description: 'আমরা আপনার অভিজ্ঞতা উন্নত করতে এবং আরও ভালো পণ্য সুপারিশ প্রদান করতে কুকি ব্যবহার করি। আপনার ডেটা ব্যক্তিগত এবং বেনামী থাকে।',
      essential: 'অপরিহার্য কুকি',
      analytics: 'বিশ্লেষণ কুকি',
      advertising: 'বিজ্ঞাপন কুকি',
      learnMore: 'আরও জানুন',
      acceptAll: 'স্বীকার করুন ও চালিয়ে যান',
      rejectAll: 'প্রত্যাখ্যান করুন',
      customizeSettings: 'কাস্টমাইজ করুন',
      savePreferences: 'পছন্দগুলি সংরক্ষণ করুন',
      essentialDescription: 'ওয়েবসাইট সঠিকভাবে কাজ করার জন্য প্রয়োজনীয়। নিষ্ক্রিয় করা যায় না।',
      analyticsDescription: 'বেনামী ডেটা সংগ্রহ করে দর্শকরা কীভাবে আমাদের ওয়েবসাইটের সাথে ইন্টারঅ্যাক্ট করে তা বুঝতে সাহায্য করে।',
      advertisingDescription: 'Google AdSense-এর মাধ্যমে প্রাসঙ্গিক বিজ্ঞাপন দেখাতে ব্যবহৃত হয়। শুধুমাত্র আপনার সম্মতিতে লোড হয়।',
    },
    contactUs: 'আমাদের সাথে যোগাযোগ করুন',
    contactFormDescription: 'অনুগ্রহ করে নিচের ফর্মটি পূরণ করুন।',
    defaultMetadata: {
      title: `Inception | আপনার জন্য সেরা পণ্য, সৎ AI দ্বারা খুঁজে পাওয়া`,
      description: `আপনার জন্য সেরা পণ্যটি ইতিমধ্যেই আছে। আমাদের AI সেটি খুঁজে বের করে, কারণ আমরা আপনার কাছে দায়বদ্ধ, বিজ্ঞাপনদাতাদের কাছে নয়। Inception আপনার অঞ্চলের জন্য সৎ পণ্য সুপারিশ প্রদান করে।`,
    },
    descriptionArticle: 'আমাদের লক্ষ্য হল ব্যবসাগুলো তাদের অনলাইন উপস্থিতি উন্নত করার পদ্ধতি রূপান্তর করা, তাদের গ্রাহকদের জন্য বিনামূল্যে মানসম্পন্ন কন্টেন্ট সরবরাহ করতে সক্ষম করে।',
    describeDemoCategory: 'এখানে আমরা এই বিভাগে একটি পণ্যকে নেতা করে তোলে এমন বৈশিষ্ট্য ও গুণাবলী নির্ধারণ করব।',
    describeDemoComparison: 'এখানে আমরা আপনার অনুসন্ধান মানদণ্ডের ভিত্তিতে সেরা পণ্যের নামের একটি সরল তালিকা তৈরি করব।',
    directlySearch: 'সরাসরি অনুসন্ধান করুন',
    disconnect: 'সংযোগ বিচ্ছিন্ন করুন',
    emailPlaceholder: 'আপনার ইমেল লিখুন',
    emailTitle: 'ইমেল',
    example: 'উদাহরণ',
    phoneTitle: 'ফোন নম্বর',
    exploreMore: 'আরও অন্বেষণ করুন',
    existingPages: 'সাহায্য করতে পারে এমন বিদ্যমান পৃষ্ঠাগুলি দেখুন',
    articlesOtherRegions: 'আমাদের অন্যান্য অঞ্চল থেকে',
    firstPostComingSoon: `আপনার প্রথম পোস্ট শীঘ্রই আসছে, আমরা আপনাকে জানাব!\nএই সময়ে, আপনি চাইলে নিচের বিষয়বস্তু সম্পাদনা করতে পারেন।`,
    joinUs: 'আমাদের সাথে যোগ দিন।',
    contactForm: {
      personalInformation: 'ব্যক্তিগত তথ্য',
      yourMessage: 'আপনার বার্তা',
      firstName: 'প্রথম নাম',
      firstNamePlaceholder: 'আপনার প্রথম নাম লিখুন',
      emailAddress: 'ইমেল ঠিকানা',
      emailPlaceholder: 'আপনার ইমেল ঠিকানা লিখুন',
      phoneNumber: 'ফোন নম্বর',
      phonePlaceholder: 'আপনার ফোন নম্বর লিখুন',
      company: 'কোম্পানি/প্রতিষ্ঠান',
      companyPlaceholder: 'আপনার কোম্পানির নাম লিখুন',
      message: 'আপনার প্রকল্প বা জিজ্ঞাসা সম্পর্কে বলুন',
      messagePlaceholder: 'আপনার প্রকল্পের বিবরণ, প্রয়োজনীয়তা বা প্রশ্ন শেয়ার করুন...',
      sendMessage: 'বার্তা পাঠান',
      sending: 'পাঠানো হচ্ছে...',
      successTitle: 'বার্তা সফলভাবে পাঠানো হয়েছে!',
      successMessage: 'আপনার বার্তার জন্য ধন্যবাদ! আমরা শীঘ্রই যোগাযোগ করব।',
      processingMessage: 'আপনার অনুরোধ প্রক্রিয়া করা হচ্ছে...',
      errorTitle: 'বার্তা পাঠাতে ত্রুটি',
      errorMessage: 'আপনার বার্তা পাঠাতে একটি ত্রুটি হয়েছে। অনুগ্রহ করে আবার চেষ্টা করুন।',
    },
    loading: 'লোড হচ্ছে...',
    localeDisclaimer: 'শুধুমাত্র X',
    login: 'লগইন',
    lookingFor: 'আপনি কি খুঁজছেন?',
    profile: 'প্রোফাইল',
    otherLanguages: 'অন্যান্য ভাষা',
    showLess: 'কম দেখান',
    showMoreLanguages: 'আরও ভাষা দেখান',
    messageTitle: 'বার্তা',
    mission: `আপনার জন্য সেরা পণ্যটি ইতিমধ্যেই আছে। আমাদের AI সেটি খুঁজে বের করে, কারণ আমরা আপনার কাছে দায়বদ্ধ, বিজ্ঞাপনদাতাদের কাছে নয়।`,
    namePlaceholder: 'আপনার নাম লিখুন',
    nameTitle: 'নাম',
    newProduct: 'নতুন যোগ করা হয়েছে',
    newSearch: 'নতুন অনুসন্ধান: ',
    nextStep: 'পরবর্তী ধাপ',
    noArticles: 'বর্তমানে কোনো প্রবন্ধ নেই',
    noResultsFound: 'বি.দ্র.: আমি শুধু বিপণনযোগ্য পণ্য বা বিভাগ সম্পর্কে পরামর্শ দিতে পারি।',
    noSearchResult: 'আপনি যে পণ্যটি খুঁজছেন তা বর্ণনা করতে থাকুন, তারপর Enter চাপুন।',
    officialWebsite: 'অফিসিয়াল ওয়েবসাইট',
    orderNow: 'এখনই অর্ডার করুন',
    browseOnAmazon: 'Amazon-এ ব্রাউজ করুন',
    searchNow: 'এখনই অনুসন্ধান করুন',
    ourMission: 'আমাদের লক্ষ্য',
    overviewTitle: 'বিবরণ',
    askAQuestion: 'কোনো প্রশ্ন আছে?',
    askSentHint: 'ওপরে লিখুন',
    pageReference: 'এই পৃষ্ঠায়',
    buyNow: 'এখনই কিনুন',
    publishedOn: 'প্রকাশিত',
    publishingOn: 'প্রকাশনার তারিখ',
    quickMenu: 'শীর্ষ পছন্দের সারসংক্ষেপ',
    recentBlogs: 'PLACEHOLDER সংবাদ',
    regionPrompt: {
      switchQuestion: {},
      yesSwitch: 'হ্যাঁ, অঞ্চল পরিবর্তন করুন',
      noThanks: 'না, ধন্যবাদ',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'আরও আবিষ্কার করুন',
    requirements: 'প্রয়োজনীয়তা',
    responsibilities: 'দায়িত্বসমূহ',
    reviewSummary: 'পর্যালোচনা সারসংক্ষেপ',
    xReviews: '{count} পর্যালোচনা',
    review: 'পর্যালোচনা',
    searching: 'অনুসন্ধান করা হচ্ছে ',
    searchingProduct: 'সেরা বিকল্পগুলি খুঁজছে ',
    sidemenuListTitle: 'আমাদের সেরা পছন্দগুলি',
    slogan: `আমরা আপনার কাছে দায়বদ্ধ, বিজ্ঞাপনদাতাদের কাছে নয়।`,
    submit: 'জমা দিন',
    technicalSpecs: 'প্রযুক্তিগত বিবরণ',
    nutritionFacts: 'পুষ্টি তথ্য',
    ingredients: 'উপকরণ',
    materialFabric: 'উপাদান ও কাপড়',
    dimensionsSize: 'মাত্রা ও আকার',
    performanceMetrics: 'কর্মক্ষমতা পরিমাপ',
    safetyFeatures: 'নিরাপত্তা বৈশিষ্ট্য',
    compatibility: 'সামঞ্জস্যতা',
    careInstructions: 'যত্নের নির্দেশাবলী',
    warrantySupport: 'ওয়ারেন্টি ও সহায়তা',
    timeUiStrings: {
      noTimeAvailable: 'কোনো সময় পাওয়া যায়নি',
      inTheFuture: 'ভবিষ্যতে',
      justNow: 'এইমাত্র',
      oneMinuteAgo: '১ মিনিট আগে',
      xMinutesAgo: '{x} মিনিট আগে',
      over30Minutes: '৩০ মিনিটের বেশি আগে',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Inception সংবাদ',
    trademarkPolicy: 'ট্রেডমার্ক নীতি',
    trademarkPolicyContent: [
      `'Inception' হল InceptionAI Inc.-এর একটি নিবন্ধিত ট্রেডমার্ক।`,
      'এই ওয়েবসাইটে প্রদর্শিত অন্যান্য ট্রেডমার্কগুলি তাদের সংশ্লিষ্ট মালিকদের সম্পত্তি।',
      'ট্রেডমার্ক সম্পর্কে প্রশ্নের জন্য, যোগাযোগ করুন:',
    ],
    tryAgain: 'আবার চেষ্টা করুন',
    uploadCV: 'CV আপলোড করুন',
    viewDetails: 'বিবরণ দেখুন',
    viewMore: 'আরও দেখান',
    welcome: 'Inception-তে স্বাগতম',
    linkInput: {
      smartExtract: 'স্মার্ট এক্সট্র্যাক্ট:',
      apply: 'প্রয়োগ করুন',
      pasteHelper: 'স্বয়ংক্রিয়ভাবে ব্যবহারকারীর নাম বের করতে সম্পূর্ণ URL পেস্ট করুন',
      placeholderDefault: '---',
    },
    privacyPolicy: 'গোপনীয়তা নীতি',
    affiliateDisclosure: 'Amazon অ্যাসোসিয়েট এবং অ্যাফিলিয়েট পার্টনার হিসেবে, Inception যোগ্য কেনাকাটা থেকে আয় করে। এটি আমাদের র‍্যাঙ্কিং প্রভাবিত করে না।',
    articlesIntro: 'Inception প্রবন্ধগুলি কেনার গাইড এবং গভীর পণ্য বিশ্লেষণ প্রদান করে আপনাকে সচেতন ক্রয় সিদ্ধান্ত নিতে সাহায্য করতে।',
    fallbackNotice: 'আপনি যে নির্দিষ্ট পৃষ্ঠাটি খুঁজছেন তা পাওয়া যায়নি, তবে আমরা সংশ্লিষ্ট বিষয়বস্তু দেখাচ্ছি।',
    aboutInceptionAi: `Inception {region} আপনার জন্য সেরা পণ্যটি খুঁজে বের করে, এমন AI দিয়ে যা আপনার কাছে দায়বদ্ধ, বিজ্ঞাপনদাতাদের কাছে নয়।`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'কানাডায়',
      [Region.USA]: 'আমেরিকায়',
      [Region.France]: 'ফ্রান্সে',
      [Region.UK]: 'যুক্তরাজ্যে',
      [Region.Australia]: 'অস্ট্রেলিয়ায়',
      [Region.All]: 'আপনার অঞ্চলে',
    },
  },
  // Marathi
  mr: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Amazon किंमत · {time}',
    aboutTitle: 'मुख्य मुद्दे:',
    aboutUs: 'आमच्याबद्दल',
    vision: 'आमचे व्हिजन',
    aboutUsTitle: 'Inception म्हणजे काय?',
    ageVerification: {
      title: 'वयाची पडताळणी आवश्यक',
      subtitle: 'प्रौढ सामग्री इशारा',
      warning: 'या वेबसाइटवर प्रौढ सामग्री आहे आणि ती केवळ १८ वर्षे किंवा त्याहून अधिक वयाच्या व्यक्तींसाठी आहे.',
      disclaimer:
        'या साइटमध्ये प्रवेश करून, आपण पुष्टी करता की आपण आपल्या अधिकारक्षेत्रात कायदेशीर वयाचे आहात आणि प्रौढ सामग्री पाहू इच्छिता.',
      confirmAge: 'मी पुष्टी करतो/करते की मी १८ वर्षे किंवा त्याहून अधिक वयाचा/ची आहे',
      enterButton: 'साइटमध्ये प्रवेश करा',
      exitButton: 'बाहेर पडा',
      mustBe18: 'ही सामग्री पाहण्यासाठी आपण १८+ असणे आवश्यक आहे',
      adultContentWarning: 'या साइटवर केवळ प्रौढ प्रेक्षकांसाठी स्पष्ट प्रौढ सामग्री आहे.',
      euCompliance: 'ही पडताळणी EU डिजिटल सेवा कायदा आणि राष्ट्रीय वय पडताळणी आवश्यकतांचे पालन करते.',
    },
    alternativeLabel: '*आम्हाला सापडलेला सर्वोत्तम पर्याय',
    at: 'येथे',
    averagePriceTag: 'सरासरी: ',
    best: 'सर्वोत्तम PLACEHOLDER',
    bestFeatures: 'एखाद्या उत्पादनाला त्याच्या श्रेणीत सर्वोत्तम कशामुळे बनवते?',
    articles: 'लेख',
    backToHome: 'मुख्यपृष्ठावर जा',
    breadcrumbHome: 'मुख्यपृष्ठ',
    goBack: 'मागे जा',
    by: 'द्वारे',
    cancel: 'रद्द करा',
    methodology: 'आम्ही कसे निवडतो',
    partners: 'भागीदार',
    careers: 'करिअर',
    changeRegion: 'प्रदेश बदला',
    choosePrice: 'दरम्यान, आपण आपल्यासाठी योग्य अशी किंमत श्रेणी निवडू शकता.',
    comingSoon: 'लवकरच येत आहे',
    conclusion: 'निष्कर्ष',
    cookieConsent: {
      title: 'आम्ही आपल्या गोपनीयतेचा आदर करतो',
      description: 'आम्ही आपला अनुभव सुधारण्यासाठी आणि चांगल्या उत्पादन शिफारसी देण्यासाठी कुकीज वापरतो. आपला डेटा खाजगी आणि अनामिक राहतो.',
      essential: 'आवश्यक कुकीज',
      analytics: 'विश्लेषण कुकीज',
      advertising: 'जाहिरात कुकीज',
      learnMore: 'अधिक जाणून घ्या',
      acceptAll: 'स्वीकारा आणि सुरू ठेवा',
      rejectAll: 'नाकारा',
      customizeSettings: 'सानुकूल करा',
      savePreferences: 'प्राधान्ये जतन करा',
      essentialDescription: 'वेबसाइट योग्यरित्या कार्य करण्यासाठी आवश्यक. बंद करता येत नाही.',
      analyticsDescription: 'अनामिक डेटा गोळा करून अभ्यागत आमच्या वेबसाइटशी कसे संवाद साधतात हे समजून घेण्यास मदत करतात.',
      advertisingDescription: 'Google AdSense द्वारे संबंधित जाहिराती दाखवण्यासाठी वापरल्या जातात. फक्त आपल्या संमतीने लोड होतात.',
    },
    contactUs: 'आमच्याशी संपर्क साधा',
    contactFormDescription: 'कृपया खालील फॉर्म भरा.',
    defaultMetadata: {
      title: `Inception | तुमच्यासाठी सर्वोत्तम उत्पादन, प्रामाणिक AI ने शोधलेले`,
      description: `तुमच्यासाठी सर्वोत्तम उत्पादन आधीच अस्तित्वात आहे. आमचा AI ते शोधतो, कारण आम्ही तुम्हाला जबाबदार आहोत, जाहिरातदारांना नाही. Inception आपल्या प्रदेशासाठी प्रामाणिक उत्पादन शिफारसी देते.`,
    },
    descriptionArticle: 'व्यवसायांना त्यांच्या ग्राहकांसाठी मोफत दर्जेदार सामग्री प्रदान करण्यास सक्षम करून त्यांची ऑनलाइन उपस्थिती सुधारण्याचा मार्ग बदलणे हे आमचे ध्येय आहे.',
    describeDemoCategory: 'येथे आम्ही या श्रेणीत उत्पादनाला नेता बनवणारी वैशिष्ट्ये आणि गुण परिभाषित करू.',
    describeDemoComparison: 'येथे आम्ही आपल्या शोध निकषांवर आधारित सर्वोत्तम उत्पादन नावांची सरळ यादी तयार करू.',
    directlySearch: 'थेट शोधा',
    disconnect: 'डिस्कनेक्ट करा',
    emailPlaceholder: 'आपला ईमेल प्रविष्ट करा',
    emailTitle: 'ईमेल',
    example: 'उदाहरण',
    phoneTitle: 'फोन नंबर',
    exploreMore: 'अधिक शोधा',
    existingPages: 'मदत करू शकतील अशी विद्यमान पृष्ठे पहा',
    articlesOtherRegions: 'आमच्या इतर प्रदेशांमधून',
    firstPostComingSoon: `आपली पहिली पोस्ट लवकरच येत आहे, आम्ही आपल्याला कळवू!\nदरम्यान, आपल्याला हवे असल्यास खालील सामग्री संपादित करू शकता.`,
    joinUs: 'आमच्यात सामील व्हा.',
    contactForm: {
      personalInformation: 'वैयक्तिक माहिती',
      yourMessage: 'आपला संदेश',
      firstName: 'पहिले नाव',
      firstNamePlaceholder: 'आपले पहिले नाव प्रविष्ट करा',
      emailAddress: 'ईमेल पत्ता',
      emailPlaceholder: 'आपला ईमेल पत्ता प्रविष्ट करा',
      phoneNumber: 'फोन नंबर',
      phonePlaceholder: 'आपला फोन नंबर प्रविष्ट करा',
      company: 'कंपनी/संस्था',
      companyPlaceholder: 'आपल्या कंपनीचे नाव प्रविष्ट करा',
      message: 'आपल्या प्रकल्पाबद्दल किंवा चौकशीबद्दल सांगा',
      messagePlaceholder: 'आपल्या प्रकल्पाचे तपशील, आवश्यकता किंवा प्रश्न सामायिक करा...',
      sendMessage: 'संदेश पाठवा',
      sending: 'पाठवत आहे...',
      successTitle: 'संदेश यशस्वीरित्या पाठवला!',
      successMessage: 'आपल्या संदेशाबद्दल धन्यवाद! आम्ही लवकरच संपर्क करू.',
      processingMessage: 'आपली विनंती प्रक्रिया होत आहे...',
      errorTitle: 'संदेश पाठवण्यात त्रुटी',
      errorMessage: 'आपला संदेश पाठवताना त्रुटी आली. कृपया पुन्हा प्रयत्न करा.',
    },
    loading: 'लोड होत आहे...',
    localeDisclaimer: 'केवळ X',
    login: 'लॉगिन',
    lookingFor: 'आपण शोधत आहात का?',
    profile: 'प्रोफाइल',
    otherLanguages: 'इतर भाषा',
    showLess: 'कमी दाखवा',
    showMoreLanguages: 'अधिक भाषा दाखवा',
    messageTitle: 'संदेश',
    mission: `तुमच्यासाठी सर्वोत्तम उत्पादन आधीच अस्तित्वात आहे. आमचा AI ते शोधतो, कारण आम्ही तुम्हाला जबाबदार आहोत, जाहिरातदारांना नाही.`,
    namePlaceholder: 'आपले नाव प्रविष्ट करा',
    nameTitle: 'नाव',
    newProduct: 'नवीन जोडले',
    newSearch: 'नवीन शोध: ',
    nextStep: 'पुढील पायरी',
    noArticles: 'सध्या कोणतेही लेख नाहीत',
    noResultsFound: 'टीप: मी फक्त विपणन करता येण्याजोग्या उत्पादने किंवा श्रेणींवर सूचना देऊ शकतो.',
    noSearchResult: 'आपण शोधत असलेल्या उत्पादनाचे वर्णन करत राहा, नंतर Enter दाबा.',
    officialWebsite: 'अधिकृत वेबसाइट',
    orderNow: 'आता ऑर्डर करा',
    browseOnAmazon: 'Amazon वर ब्राउझ करा',
    searchNow: 'आता शोधा',
    ourMission: 'आमचे ध्येय',
    overviewTitle: 'तपशील',
    askAQuestion: 'प्रश्न आहे का?',
    askSentHint: 'वर टाइप करा',
    pageReference: 'या पृष्ठावर',
    buyNow: 'आता खरेदी करा',
    publishedOn: 'प्रकाशित',
    publishingOn: 'प्रकाशन तारीख',
    quickMenu: 'शीर्ष निवडींचा सारांश',
    recentBlogs: 'PLACEHOLDER बातम्या',
    regionPrompt: {
      switchQuestion: {},
      yesSwitch: 'होय, प्रदेश बदला',
      noThanks: 'नाही, धन्यवाद',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: 'अधिक शोधा',
    requirements: 'आवश्यकता',
    responsibilities: 'जबाबदाऱ्या',
    reviewSummary: 'पुनरावलोकन सारांश',
    xReviews: '{count} पुनरावलोकने',
    review: 'पुनरावलोकन',
    searching: 'शोधत आहे ',
    searchingProduct: 'सर्वोत्तम पर्याय शोधत आहे ',
    sidemenuListTitle: 'आमच्या सर्वोत्तम निवडी',
    slogan: `आम्ही तुम्हाला जबाबदार आहोत, जाहिरातदारांना नाही.`,
    submit: 'सबमिट करा',
    technicalSpecs: 'तांत्रिक तपशील',
    nutritionFacts: 'पोषण माहिती',
    ingredients: 'घटक',
    materialFabric: 'साहित्य आणि कापड',
    dimensionsSize: 'परिमाणे आणि आकार',
    performanceMetrics: 'कामगिरी मोजमाप',
    safetyFeatures: 'सुरक्षा वैशिष्ट्ये',
    compatibility: 'सुसंगतता',
    careInstructions: 'काळजी सूचना',
    warrantySupport: 'हमी आणि सहाय्य',
    timeUiStrings: {
      noTimeAvailable: 'वेळ उपलब्ध नाही',
      inTheFuture: 'भविष्यात',
      justNow: 'आत्ताच',
      oneMinuteAgo: '१ मिनिटापूर्वी',
      xMinutesAgo: '{x} मिनिटांपूर्वी',
      over30Minutes: '३० मिनिटांहून अधिक पूर्वी',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Inception बातम्या',
    trademarkPolicy: 'ट्रेडमार्क धोरण',
    trademarkPolicyContent: [
      `'Inception' हे InceptionAI Inc. चे नोंदणीकृत ट्रेडमार्क आहे.`,
      'या वेबसाइटवर दिसणारे इतर ट्रेडमार्क त्यांच्या संबंधित मालकांची मालमत्ता आहेत.',
      'ट्रेडमार्कबद्दल प्रश्नांसाठी, संपर्क साधा:',
    ],
    tryAgain: 'पुन्हा प्रयत्न करा',
    uploadCV: 'CV अपलोड करा',
    viewDetails: 'तपशील पहा',
    viewMore: 'अधिक दाखवा',
    welcome: 'Inception मध्ये स्वागत',
    linkInput: {
      smartExtract: 'स्मार्ट एक्सट्रॅक्ट:',
      apply: 'लागू करा',
      pasteHelper: 'वापरकर्ता नाव आपोआप काढण्यासाठी पूर्ण URL पेस्ट करा',
      placeholderDefault: '---',
    },
    privacyPolicy: 'गोपनीयता धोरण',
    affiliateDisclosure: 'Amazon सहयोगी आणि संलग्न भागीदार म्हणून, Inception पात्र खरेदींमधून कमाई करते. हे आमच्या रँकिंगवर प्रभाव टाकत नाही.',
    articlesIntro: 'Inception लेख खरेदी मार्गदर्शक आणि सखोल उत्पादन विश्लेषण प्रदान करतात.',
    fallbackNotice: 'आपण शोधत असलेले विशिष्ट पृष्ठ सापडले नाही, परंतु आम्ही संबंधित सामग्री दाखवत आहोत.',
    aboutInceptionAi: `Inception {region} तुमच्यासाठी सर्वोत्तम उत्पादन शोधते, असा AI सह जो तुम्हाला जबाबदार आहे, जाहिरातदारांना नाही.`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'कॅनडामध्ये',
      [Region.USA]: 'अमेरिकेत',
      [Region.France]: 'फ्रान्समध्ये',
      [Region.UK]: 'युनायटेड किंगडममध्ये',
      [Region.Australia]: 'ऑस्ट्रेलियात',
      [Region.All]: 'आपल्या प्रदेशात',
    },
  },
  ja: {
    // The stamp's label (offers v2 6.1): the day word and the clock come from Intl.
    amazonPriceAsOf: 'Amazonの価格 · {time}',
    aboutTitle: 'ポイントまとめ：',
    aboutUs: '私たちについて',
    vision: '私たちのビジョン',
    aboutUsTitle: 'Inception とは？',
    ageVerification: {
      title: '年齢確認が必要です',
      subtitle: '成人向けコンテンツの警告',
      warning: 'このサイトには成人向けコンテンツが含まれており、18歳以上の方のみご利用いただけます。',
      disclaimer:
        'このサイトにアクセスすることで、お住まいの地域で成人年齢に達していること、および成人向けコンテンツの閲覧を希望していることを確認するものとします。',
      confirmAge: '18歳以上であることを確認します',
      enterButton: 'サイトに入る',
      exitButton: '戻る',
      mustBe18: 'このコンテンツを閲覧するには18歳以上である必要があります',
      adultContentWarning: 'このサイトには成人向けの露骨なコンテンツが含まれています。',
      euCompliance: 'この年齢確認はEUデジタルサービス法および各国の年齢確認要件に準拠しています。',
    },
    alternativeLabel: `※おすすめの代替品`,
    at: '（',
    averagePriceTag: '平均価格：',
    best: 'おすすめ PLACEHOLDER',
    bestFeatures: 'このカテゴリで最高の製品を選ぶポイントとは？',
    articles: '記事一覧',
    backToHome: 'ホームに戻る',
    breadcrumbHome: 'ホーム',
    buyActionDescription: 'Inceptionで{product}の購入先を比較できます。この市場で追跡している信頼できる販売店、各店で確認できた最安価格、そして当社のおすすめを掲載しています。価格は定期的に確認していますが変動する場合があり、最終的な価格は販売店の購入手続きで確定します。',
    goBack: '戻る',
    by: '著者：',
    cancel: 'キャンセル',
    methodology: '商品の選び方',
    partners: 'パートナー',
    careers: '採用情報',
    changeRegion: '地域を変更',
    shoppingRegionLabel: 'ショッピング地域：{region}',
    choosePrice: 'ご希望の価格帯をお選びください。',
    comingSoon: '近日公開',
    conclusion: 'まとめ',
    cookieConsent: {
      title: 'プライバシーを大切にしています',
      description: 'より良い製品おすすめと快適な体験を提供するため、Cookieを使用しています。お客様のデータは匿名のまま安全に保護されます。',
      essential: '必須Cookie',
      analytics: '分析Cookie',
      advertising: '広告Cookie',
      learnMore: '詳しく見る',
      acceptAll: 'すべて許可して続行',
      rejectAll: '拒否する',
      customizeSettings: 'カスタマイズ',
      savePreferences: '設定を保存',
      essentialDescription: 'サイトの正常な動作に必要です。無効にすることはできません。',
      analyticsDescription: '匿名データを収集し、訪問者がサイトをどのように利用しているかを分析します。',
      advertisingDescription: 'Google AdSenseを通じて関連性の高い広告を表示するために使用されます。同意した場合のみ読み込まれます。',
      californiaNotice: 'カリフォルニア州居住者の方へ：当社はお客様の個人情報を販売しません。分析Cookieを拒否すると、必須でないデータ収集がすべて無効になります。',
    },
    contactUs: 'お問い合わせ',
    contactFormDescription: '以下のフォームにご記入ください。',
    defaultMetadata: {
      title: `Inception | あなたに最適な商品を、誠実なAIが見つける`,
      description:
        `あなたに最適な商品は、すでにどこかにあります。私たちのAIがそれを見つけます。応えるのは広告主ではなく、あなたにです。Inceptionは、AIが調べたあなたの地域向けの商品のおすすめを提供します。スポンサー枠はありません。`,
    },
    descriptionArticle:
      '私たちのミッションは、企業がお客様に高品質なコンテンツを無料で提供できるようにし、オンラインでの存在感を向上させることです。',
    describeDemoCategory:
      'ここでは、このカテゴリで最高の製品となる特徴と品質を定義します。特定カテゴリのおすすめ製品ランキングを作成するために不可欠です。',
    describeDemoComparison:
      'ここでは、検索条件に基づいた製品名の比較リストを作成するため、製品の特徴と品質を定義します。お住まいの地域の企業を優先的に選定します。',
    directlySearch: '直接検索',
    disconnect: 'ログアウト',
    emailPlaceholder: 'メールアドレスを入力',
    emailTitle: 'メールアドレス',
    example: '例',
    phoneTitle: '電話番号',
    exploreMore: 'もっと見る',
    existingPages: '関連するページもご覧ください',
    articlesOtherRegions: 'ほかの地域から',
    firstPostComingSoon: `最初の記事はまもなく公開されます。公開されたらお知らせします！\nそれまでの間、以下のコンテンツを編集できます。`,
    joinUs: '仲間を募集しています。',
    contactForm: {
      personalInformation: 'お客様情報',
      yourMessage: 'メッセージ内容',
      firstName: 'お名前',
      firstNamePlaceholder: 'お名前を入力してください',
      emailAddress: 'メールアドレス',
      emailPlaceholder: 'メールアドレスを入力してください',
      phoneNumber: '電話番号',
      phonePlaceholder: '電話番号を入力してください',
      company: '会社名・組織名',
      companyPlaceholder: '会社名を入力してください',
      message: 'プロジェクトやお問い合わせ内容をお聞かせください',
      messagePlaceholder: 'プロジェクトの詳細、ご要望、ご質問などをお書きください…',
      sendMessage: '送信する',
      sending: '送信中…',
      successTitle: 'メッセージを送信しました',
      successMessage: 'お問い合わせありがとうございます。担当者より折り返しご連絡いたします。',
      processingMessage: 'リクエストを処理しています…',
      errorTitle: '送信エラー',
      errorMessage: 'メッセージの送信中にエラーが発生しました。もう一度お試しください。',
    },
    loading: '読み込み中…',
    localeDisclaimer: '対象：X のみ',
    login: 'ログイン',
    lookingFor: 'お探しのものは？',
    profile: 'プロフィール',
    otherLanguages: 'その他の言語',
    showLess: '閉じる',
    showMoreLanguages: 'その他の言語を表示',
    messageTitle: 'メッセージ',
    mission: `あなたに最適な商品は、すでにどこかにあります。私たちのAIがそれを見つけます。応えるのは広告主ではなく、あなたにです。`,
    namePlaceholder: 'お名前を入力',
    nameTitle: 'お名前',
    newProduct: '新着',
    newSearch: '新しい検索：',
    nextStep: '次のステップ',
    noArticles: '現在、記事はありません',
    noResultsFound: '※販売されている製品・カテゴリのみご提案できます。',
    noSearchResult: 'お探しの製品の特徴を入力して、Enterキーを押してください。',
    officialWebsite: '公式サイト',
    orderNow: '今すぐ注文',
    regulatedNotice: {
      prescriptionOnly: '処方箋が必要です',
      professionalUse: '専門家向け',
      fromVeterinarian: 'かかりつけの獣医師にご相談ください',
      fromPharmacist: '薬局でお求めいただけます',
      noOnlinePurchase: 'オンラインでは販売していません',
      professionalSupply: '専門業者を通じて販売されています',
      whyNoLink: '本製品はレビュー対象ですが、購入リンクは掲載していません。',
    },
    browseOnAmazon: 'Amazonで見る',
    unavailableInCountry: '{country}では現在購入できません',
    searchSimilarOnAmazon: 'Amazonで類似商品を検索',
    searchNow: '検索する',
    ourMission: '私たちのミッション',
    overviewTitle: '詳細',
    askAQuestion: '質問はありますか？',
    askSentHint: '上の欄に入力',
    micBlocked: 'マイクがブロックされています。ブラウザの設定で許可してください。',
    pageReference: 'このページの内容',
    buyNow: '今すぐ購入',
    publishedOn: '公開日：',
    publishingOn: '公開予定日：',
    quickMenu: 'おすすめピックアップ',
    recentBlogs: 'PLACEHOLDER ニュース',
    regionPrompt: {
      switchQuestion: {},
      yesSwitch: 'はい、変更する',
      noThanks: 'いいえ、結構です',
      switchedNotice: {},
      goBackTo: {},
    },
    relatedTitle: '関連コンテンツ',
    requirements: '応募条件',
    responsibilities: '業務内容',
    reviewSummary: 'レビューまとめ',
    xReviews: '{count}件のレビュー',
    review: 'レビュー',
    searching: '検索中：',
    searchingProduct: 'おすすめの代替品を検索中：',
    sidemenuListTitle: '厳選おすすめ',
    slogan: `私たちが応えるのは広告主ではなく、あなたにです。`,
    submit: '送信',
    technicalSpecs: 'スペック・仕様',
    nutritionFacts: '栄養成分表示',
    ingredients: '原材料',
    materialFabric: '素材・生地',
    dimensionsSize: 'サイズ・寸法',
    performanceMetrics: 'パフォーマンス指標',
    safetyFeatures: '安全機能',
    compatibility: '互換性',
    careInstructions: 'お手入れ方法',
    warrantySupport: '保証・サポート',
    timeUiStrings: {
      noTimeAvailable: '時刻情報なし',
      inTheFuture: '今後公開予定',
      justNow: 'たった今',
      oneMinuteAgo: '1分前',
      xMinutesAgo: '{x}分前',
      over30Minutes: '30分以上前',
    },
    title: 'I N C E P T I O N',
    titleArticle: 'Inception ニュース',
    trademarkPolicy: '商標ポリシー',
    trademarkPolicyContent: [
      `「Inception」は InceptionAI Inc. の登録商標であり、カナダおよびその他の国で適切に登録・保護されています。InceptionAI Inc. の書面による事前承諾なしに「Inception」商標またはこれに類似する商標を使用することは固く禁じられています。`,
      '本サイトまたは当社の製品・サービスに表示されるその他の商標、サービスマーク、ロゴ、ブランド名は、それぞれの所有者に帰属します。明示的に記載されている場合を除き、これらの商標の表示は当該所有者との提携や推薦を意味するものではありません。',
      '当社は知的財産権を尊重しており、ユーザーおよびパートナーの皆様にも同様の姿勢をお願いしています。商標に関するご質問・ご懸念がございましたら、下記までお問い合わせください：',
    ],
    tryAgain: 'もう一度試す',
    uploadCV: '履歴書をアップロード',
    viewDetails: '詳細を見る',
    viewMore: 'もっと表示',
    welcome: 'Inception へようこそ',
    linkInput: {
      smartExtract: 'スマート抽出：',
      apply: '適用',
      pasteHelper: '完全なURLを貼り付けると、ユーザー名が自動で抽出されます',
      placeholderDefault: '---',
    },
    privacyPolicy: 'プライバシーポリシー',
    affiliateDisclosure: 'InceptionはAmazonアソシエイトおよびアフィリエイトパートナーとして、対象となる購入から収益を得ています。これはランキングに一切影響しません。製品の調査・分析と販売は完全に分離されています。',
    articlesIntro: 'Inceptionの記事では、AIを使って作成した購入ガイドや製品分析をお届けします。',
    fallbackNotice: 'お探しのページは見つかりませんでしたが、関連するコンテンツを表示しています。',
    aboutInceptionAi:
      `Inceptionは、{region}あなたに最適な商品を見つけます。応えるのは広告主ではなく、あなたにであるAIで。`,
    aboutInceptionAiRegion: {
      [Region.Canada]: 'カナダで',
      [Region.USA]: 'アメリカで',
      [Region.France]: 'フランスで',
      [Region.UK]: 'イギリスで',
      [Region.Australia]: 'オーストラリアで',
      [Region.Japan]: '日本で',
      [Region.All]: 'お住まいの地域で',
    },
  },
};

// The offer chips ("Our pick", "Best price") and the prescription / professional
// notice exist in every language. Languages authored above keep their own;
// the rest come from translations/<lang>/offer-labels.ts.
for (const code of Object.keys(uiContentCore) as CoreLanguageType[]) {
  const labels = offerLabelsTranslations[code];
  if (!labels) continue;
  const record = uiContentCore[code];
  record.bestPlaceBadge ??= labels.bestPlaceBadge;
  record.bestPriceBadge ??= labels.bestPriceBadge;
  if (labels.regulatedNotice) record.regulatedNotice ??= labels.regulatedNotice;
}

export const uiContent = proxyLanguageRecord<UiContent>(uiContentCore);

export const getUiContent = (locale: LocaleDetails) => {
  const content = uiContent[locale.languageCode];
  if (!content) {
    console.warn(`No UI content found for locale: ${locale.path}`);
    return uiContent.en; // Fallback to English if no content found
  }
  return content;
};

/**
 * Section headings for the Trademark policy's three paragraphs, consumed by the
 * unified legal layout (sticky rail + section titles). Authored for the
 * languages we can translate with confidence; every other locale falls back to
 * English — the policy body itself (trademarkPolicyContent) stays fully
 * localized. Extend this map to localize the headings for further locales.
 */
export const trademarkPolicySectionTitles: Partial<
  Record<LanguageType, readonly [string, string, string]>
> = {
  en: ['Our marks', 'Third-party marks', 'Questions'],
  fr: ['Nos marques', 'Marques de tiers', 'Questions'],
  es: ['Nuestras marcas', 'Marcas de terceros', 'Preguntas'],
  de: ['Unsere Marken', 'Marken Dritter', 'Fragen'],
  it: ['I nostri marchi', 'Marchi di terzi', 'Domande'],
  'pt-BR': ['Nossas marcas', 'Marcas de terceiros', 'Dúvidas'],
  'pt-PT': ['As nossas marcas', 'Marcas de terceiros', 'Questões'],
  nl: ['Onze merken', 'Merken van derden', 'Vragen'],
  pl: ['Nasze znaki towarowe', 'Znaki towarowe stron trzecich', 'Pytania'],
  sv: ['Våra varumärken', 'Tredjeparts varumärken', 'Frågor'],
  ca: ['Les nostres marques', 'Marques de tercers', 'Preguntes'],
  gl: ['As nosas marcas', 'Marcas de terceiros', 'Preguntas'],
  ja: ['当社の商標', '第三者の商標', 'お問い合わせ'],
};

export const getTrademarkPolicySectionTitles = (
  language: LanguageType,
): readonly [string, string, string] =>
  trademarkPolicySectionTitles[language] ?? trademarkPolicySectionTitles.en!;
